import { installPortalControls, focusControl } from "./controls.js";
import { createIcons, AlertTriangle, ArrowDownToLine, Bell, Building2, Calculator, Check, ChevronDown, ChevronRight, Copy, Crop, ExternalLink, FileImage, FileSpreadsheet, FileText, FileVideo, Folder, FolderPlus, History, Home, KeyRound, Link as LinkIcon, Lock, LockOpen, LogOut, Map as MapIcon, Menu, Plus, RefreshCw, Save, Search, Shield, SlidersHorizontal, Trash2, Upload, Users, X } from "lucide";
import { numberFieldRule, numberFieldError } from "../shared/calculator-fields.js";
import { addMonths, calendarMonthsBetween } from "../shared/calculation-dates.js";
import { expenseTreatmentLabels } from "../shared/calculation-expenses.js";
import { buildCalculationReport, reportSectionsHtml, money as calculationMoney } from "../shared/calculation-report.js";
import type { CalculatorInput, CalculatorResult } from "../server/types.js";
import { aiRopPage, hydrateAiRop, refreshAiRopCriticalCount } from "./ai-rop.js";

type Role = "manager" | "director" | "admin";
type User = { id: number; login: string; name: string; role: Role; state: string; complex_ids?: number[]; amo_user_id?: number | null };
type ObjectCoverImage = { id: number; url: string; name: string; cropX: number; cropY: number; isPrimary: boolean };
type ApiObject = Record<string, any> & { id: number; name: string; district: string; address: string; developer: string; status: string; min_price: number | string; area_min: number; area_max: number; units: number; lat: number; lng: number; image: string; cover_images?: ObjectCoverImage[] };
type ObjectFilters = { q: string; district: string; status: string };
type ObjectDossierKey = "object_buildings" | "available_units" | "object_contacts" | "booking_terms" | "document_requirements" | "purchase_programs" | "object_sources";
type ObjectTab = "buildings" | "units" | "purchase" | "documents" | "contacts" | "files" | "history";
type DossierFieldConfig = [string, string, string, string?];
type CalculatorTab = "profitability" | "mortgage-compare";
type ProfitabilityResult = CalculatorResult;
type FileReadStatus = { status: "read" | "unread"; comment: string; updated_at: string };
type FileReadSummary = { user_id: number; name: string; role: string; status: "read" | "unread"; updated_at: string | null };
type FileItem = Record<string, any> & { id: number; name: string; type: string; mime_type?: string | null; size_label: string; folder_id: number | null; folder_path: string; required: number; complex_id: number | null; stored_name?: string | null; read_status?: FileReadStatus | null; read_summary?: FileReadSummary[] };
type FolderNode = { id: number; name: string; nodes?: FolderNode[] };
type MapProvider = "geoapify" | "yandex" | "yandex-tiles" | "osm";
type AppConfig = {
  mapProvider: MapProvider;
  geoapifyApiKey: string;
  geoapifyMapStyle: string;
  yandexMapsApiKey: string;
  yandexMapsLang: string;
  yandexTilesEnabled: boolean;
  yandexTilesMapType: string;
  aiRopEnabled: boolean;
};

declare global {
  interface Window {
    L?: any;
    ymaps3?: any;
  }
}

const app = document.querySelector<HTMLDivElement>("#app")!;
const portalSessionExpiredEvent = "portal:session-expired";
let currentUser: User | null = null;
let route = currentRoutePath();
let objects: ApiObject[] = [];
let activeObject: ApiObject | null = null;
let appConfig: AppConfig | null = null;
let leafletPromise: Promise<any> | null = null;
const objectDraftKey = "novy-bereg-object-draft";
const clientWorkModeKey = "novy-bereg-client-work-mode";
const integerDossierFields = new Set(["rooms", "floor", "price", "amount"]);
let yandexMapsPromise: Promise<any> | null = null;
const objectDetailRequests = new Set<number>();
let currentFiles: FileItem[] = [];
let currentFolders: Array<{ id: number; name: string; parentId: number | null; trail: Array<{ id: number | null; name: string }> }> = [];
let unreadFilesCount = 0;
let managerObjectOutsideHandler: ((event: PointerEvent) => void) | null = null;
let fileMoveOutsideHandler: ((event: PointerEvent) => void) | null = null;
let fileMoveKeyHandler: ((event: KeyboardEvent) => void) | null = null;
let fileMoveReturnFocus: HTMLElement | null = null;
let filePreviewReturnFocus: HTMLElement | null = null;

const currency = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 });
const number = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });
const date = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });

window.addEventListener("hashchange", () => {
  route = currentRoutePath();
  document.body.classList.remove("is-mobile-nav-open");
  render();
});
window.addEventListener(portalSessionExpiredEvent, expirePortalSession);

installPortalControls();
boot();

async function boot() {
  try {
    const session = await api<{ user: User }>("/api/session");
    currentUser = session.user;
    await Promise.all([loadObjects(), loadAppConfig()]);
    await refreshUnreadFilesCount();
    if (appConfig?.aiRopEnabled) await refreshAiRopCriticalCount();
  } catch {
    currentUser = null;
  }
  render();
}

async function loadObjects() {
  const data = await api<{ items: ApiObject[] }>("/api/objects");
  objects = data.items;
}

async function loadObjectDetails(id: number) {
  if (!id || objectDetailRequests.has(id)) return;
  objectDetailRequests.add(id);
  try {
    const data = await api<{ object: ApiObject; files: FileItem[] }>(`/api/objects/${id}`);
    const item = { ...data.object, object_files: data.files };
    objects = objects.map((object) => object.id === id ? { ...object, ...item } : object);
    if (!objects.some((object) => object.id === id)) objects = [item, ...objects];
    if (activeObject?.id === id || route.startsWith(`/objects/${id}`)) activeObject = item;
    render();
  } finally {
    objectDetailRequests.delete(id);
  }
}

function hasObjectDetails(item: ApiObject | null | undefined) {
  return Boolean(item && Array.isArray(item.object_buildings) && Array.isArray(item.available_units));
}

function render() {
  document.querySelector<HTMLDialogElement>(".calc-report-dialog")?.close();
  if (!currentUser) {
    document.body.classList.remove("is-client-work-mode");
    renderLogin();
    return;
  }
  document.body.classList.toggle("is-client-work-mode", isClientWorkMode());
  if (isClientWorkMode() && isClientHiddenRoute(route)) {
    location.hash = "/objects";
    return;
  }
  const title = titleForRoute(route);
  app.innerHTML = `
    <div class="portal-shell">
      <header class="command-bar">
        <button class="nav-toggle" type="button" data-action="toggle-nav" aria-controls="module-nav" aria-expanded="false" aria-label="Открыть меню"><i data-lucide="menu"></i></button>
        <a class="brand-mark" href="#/objects" aria-label="Новый берег: к объектам">
          <span>Новый берег</span>
          <small>Рабочее место</small>
        </a>
        <nav class="module-nav module-nav-desktop" aria-label="Разделы портала">${navItems({ includeNews: false }).map(navItem).join("")}</nav>
        <div class="operator-tools">
          ${clientWorkModeToggle()}
          <a class="top-news-button icon-button${route === "/news" ? " active" : ""}" href="#/news" aria-label="Новости"${tooltipAttr("Новости")}><i data-lucide="bell"></i></a>
          <div class="profile">
            <span>${currentUser.name}</span>
            <button class="icon-button" data-action="logout" aria-label="Выйти из портала"${tooltipAttr("Выйти из портала")}><i data-lucide="log-out"></i></button>
          </div>
        </div>
      </header>
      <nav class="module-nav module-nav-mobile" id="module-nav" aria-label="Разделы портала">${navItems().map(navItem).join("")}</nav>
      <button class="mobile-nav-backdrop" type="button" data-action="close-nav" aria-label="Закрыть меню"></button>
      <main id="main" class="stage">
        <section class="page-hero${route === "/objects" || route === "/ai-rop" ? " page-hero-compact" : ""}">
          <div>
            <h1>${title}</h1>
            ${routeDescription(route) ? `<p>${routeDescription(route)}</p>` : ""}
          </div>

        </section>
        <div class="page-body">${page()}</div>
      </main>
    </div>
  `;
  bindGlobal();
  createIcons({ icons: iconSet });
}

function renderLogin() {
  app.innerHTML = `
    <main class="login-screen">
      <section class="login-panel">
        <div class="login-panel-content">
          <div class="login-brand">Новый берег<span>Портал менеджеров</span></div>
          <div class="login-form-heading"><h1>Вход в портал</h1></div>
          <form id="login-form" class="login-form">
            <label>Логин<input name="login" autocomplete="username" spellcheck="false" autofocus /></label>
            <label>Пароль<input name="password" type="password" autocomplete="current-password" spellcheck="false" /></label>
            <p class="form-error" id="login-error" role="alert"></p>
            <button class="primary" type="submit"><i data-lucide="key-round"></i> Войти</button>
          </form>
        </div>
      </section>
      <aside class="login-showcase" aria-label="Внутреннее корпоративное пространство">
        <div class="login-showcase-content">
          <span>Только для сотрудников</span>
          <strong>Рабочее пространство команды «Новый берег»</strong>
          <p>Объекты, расчёты и документы для ежедневной работы менеджеров.</p>
        </div>
      </aside>
    </main>
  `;
  document.querySelector<HTMLFormElement>("#login-form")!.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    try {
      const data = await api<{ user: User }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ login: form.get("login"), password: form.get("password") })
      });
      currentUser = data.user;
      await Promise.all([loadObjects(), loadAppConfig()]);
      await refreshUnreadFilesCount();
      if (appConfig?.aiRopEnabled) await refreshAiRopCriticalCount();
      location.hash = "/objects";
      render();
    } catch (error) {
      document.querySelector("#login-error")!.textContent = message(error);
    }
  });
  createIcons({ icons: { KeyRound } });
}

function page(): string {
  if (route === "/objects/new") return objectEditorPage(null);
  if (route.startsWith("/objects/") && route.endsWith("/edit")) {
    const id = Number(route.split("/")[2]);
    const item = objects.find((object) => object.id === id) || (activeObject?.id === id ? activeObject : null);
    if (!item) return emptyState("Объект не найден", "Обновите список объектов.");
    if (item && !hasObjectDetails(item)) {
      void loadObjectDetails(id);
      return objectLoadingState("Загружаем данные объекта");
    }
    return objectEditorPage(item);
  }
  if (route.startsWith("/objects/")) return objectPage(Number(route.split("/")[2]));
  if (route === "/objects") return objectsPage();
  if (route === "/map") return mapPage();
  if (route === "/calculator") return calculatorPage();
  if (route === "/files") return filesPage();
  if (route === "/news") return newsPage();
  if (route === "/ai-rop") return isClientWorkMode() ? clientHiddenSectionPage() : aiRopWorkspacePage();
  if (route === "/manage/users") return isClientWorkMode() ? clientHiddenSectionPage() : usersPage();
  if (route === "/manage/audit") return isClientWorkMode() ? clientHiddenSectionPage() : auditPage();
  return objectsPage();
}

function clientWorkModeToggle() {
  const enabled = isClientWorkMode();
  const label = enabled ? "Работа с клиентом включена. Внутренние данные скрыты" : "Работа с клиентом";
  return `
    <button class="client-work-toggle${enabled ? " is-active" : ""}" type="button" data-action="toggle-client-work-mode" aria-pressed="${enabled}" aria-label="${label}"${tooltipAttr(label)}>
      <i data-lucide="shield" aria-hidden="true"></i>
    </button>
  `;
}

function clientHiddenSectionPage() {
  return `
    <section class="empty-state client-hidden-state">
      <h2>Раздел скрыт в режиме работы с клиентом</h2>
      <p>Выключите режим в верхней панели, чтобы вернуться к административным данным.</p>
    </section>
  `;
}

function objectsPage(): string {
  const districts = unique(objects.map((item) => item.district));
  const statuses = unique(objects.map((item) => item.status));
  const filters = objectFiltersFromUrl();
  const items = filteredObjects(filters);
  return `
    ${canManage() ? `<section class="object-management-bar"><a class="primary" href="#/objects/new"><i data-lucide="building-2"></i> Добавить объект</a></section>` : ""}
    ${objectsOverview(items)}
    ${objectFilterToolbar(districts, statuses, filters)}
    <section class="object-grid" id="object-grid">${objectGrid(items)}</section>
  `;
}

function objectCard(item: ApiObject): string {
  const badgeClass = statusBadgeClass(item.status);
  return `
    <article class="object-card" role="button" tabindex="0" data-open-object="${item.id}" aria-label="Открыть объект ${h(item.name)}">
      <div class="object-image" style="${objectImageStyle(item.image)}"><span class="status-badge ${badgeClass}">${h(item.status)}</span></div>
      <div class="object-card-body">
        <div class="object-heading"><p class="eyebrow">${h(item.developer)}</p><h2>${h(item.name)}</h2><p>${h(item.district)} · ${h(item.address)}</p></div>
        <dl>
          <div><dt>Цена от</dt><dd>${h(formatObjectPrice(item.min_price))}</dd></div>
          <div><dt>Площадь</dt><dd>${item.area_min > 0 && item.area_max > 0 ? `${number.format(item.area_min)}–${number.format(item.area_max)} м²` : "Уточняется"}</dd></div>
          <div><dt>Свободно</dt><dd>${item.units > 0 ? item.units : "Уточняется"}</dd></div>
        </dl>
        <div class="card-actions">
          <button class="ghost" data-calc-object="${item.id}"><i data-lucide="calculator"></i> В расчет</button>
        </div>
      </div>
    </article>
  `;
}

function objectPage(id: number): string {
  const item = objects.find((object) => object.id === id) || (activeObject?.id === id ? activeObject : null);
  if (!item) return `<section class="empty-state">Объект не найден</section>`;
  if (!hasObjectDetails(item)) {
    void loadObjectDetails(id);
    return objectLoadingState("Загружаем карточку объекта");
  }
  activeObject = item;
  const badgeClass = statusBadgeClass(item.status);
  const selection = filteredObjects(objectFiltersFromUrl());
  const navigationItems = selection.some((object) => object.id === item.id) ? selection : objects;
  const navigationIndex = navigationItems.findIndex((object) => object.id === item.id);
  const previousObject = navigationIndex > 0 ? navigationItems[navigationIndex - 1] : null;
  const nextObject = navigationIndex >= 0 && navigationIndex < navigationItems.length - 1 ? navigationItems[navigationIndex + 1] : null;
  const activeTab = objectTabFromUrl();
  return `
    <section class="detail-layout">
      <article class="detail-main">
        <div class="detail-hero" style="${objectImageStyle(item.image)}">
          <span class="status-badge ${badgeClass}">${h(item.status)}</span>
        </div>
        <div class="section-header">
          <div><p class="eyebrow">${h(item.developer)}</p><h2>${h(item.name)}</h2><p>${h(item.address)}</p></div>
          <div class="detail-actions">
            <a class="button secondary" href="${objectsListHref()}">← К списку объектов</a>
            ${canManage() ? `<a class="button secondary" href="#/objects/${item.id}/edit"><i data-lucide="sliders-horizontal"></i> Редактировать</a>` : ""}
            <button class="primary" data-calc-object="${item.id}"${tooltipAttr("Открыть калькулятор для этого объекта")}><i data-lucide="calculator"></i> Рассчитать</button>
          </div>
        </div>
        ${objectDetailSections(item)}
        <section class="object-detail-section object-dossier-view">
          <div class="section-header section-header-compact">
            <div>
              <p class="eyebrow">Досье объекта</p>
              <h3>Информация из настройки объекта</h3>
            </div>
          </div>
          ${objectTabs(activeTab)}
          ${objectTabPanel(item, activeTab)}
        </section>
      </article>
      <aside class="detail-side">
        ${objectStepActions(previousObject, nextObject)}
        <a class="button secondary detail-map-link" href="#/map"${tooltipAttr("Открыть карту объектов")}>Показать на карте</a>
        ${canManage() ? `<button class="object-delete-button" type="button" data-delete-object="${item.id}"${tooltipAttr("Полностью удалить объект после подтверждения")}><i data-lucide="trash-2"></i> Удалить объект</button>` : ""}
      </aside>
    </section>
  `;
}

function objectLoadingState(title: string) {
  return `
    <section class="empty-state" aria-busy="true">
      <h2>${h(title)}</h2>
      <p>Данные откроются автоматически.</p>
    </section>
  `;
}

function objectEditorPage(item: ApiObject | null) {
  if (!canManage()) return denied();
  const editing = Boolean(item);
  const draft = editing ? {} : readObjectDraft();
  const value = (key: string, fallback = "") => h(item?.[key] ?? draft[key] ?? fallback);
  return `
    <section class="object-editor">
      <div class="section-header">
        <div>
          <p class="eyebrow">${editing ? "Редактирование" : "Новый объект"}</p>
          <h2>${editing ? h(item?.name) : "Добавить объект"}</h2>
          <p>Заполните параметры, описание, координаты и условия. Документы и обложку можно загрузить после сохранения.</p>
        </div>
        <a class="button secondary object-editor-cancel" href="${editing && item ? objectHref(item.id) : "#/objects"}">Отмена</a>
      </div>
      <form id="object-editor-form" class="object-editor-form" data-object-id="${item?.id ?? ""}">
        ${editing ? "" : `<div class="draft-toolbar" aria-live="polite"><span data-draft-status>${Object.keys(draft).length ? "Черновик восстановлен" : "Черновик не сохранён"}</span><div><button class="ghost" type="button" data-save-object-draft>Сохранить черновик</button><button class="button secondary" type="button" data-clear-object-draft>Удалить черновик</button></div></div>`}
        <fieldset class="field-group" data-editor-section="basic">
          ${editorSectionStart("basic", "Основное")}
          ${textInput("name", "Название", value("name"))}
          ${textInput("developer", "Застройщик", value("developer", "Новый берег Девелопмент"))}
          ${textInput("district", "Район", value("district"))}
          ${textInput("address", "Адрес", value("address"))}
          ${selectInput("status", "Статус", value("status", "В продаже"), objectStatusOptions())}
          ${dateInput("handover_date", "Срок сдачи", value("handover_date"))}
          ${editorSectionEnd()}
        </fieldset>
        <fieldset class="field-group" data-editor-section="params">
          ${editorSectionStart("params", "Параметры")}
          ${numberInput("buildings", "Корпусов", value("buildings", "1"), "1", "шт.", { integer: true, min: "0" })}
          ${numberInput("units", "Квартир доступно", value("units", "0"), "1", "шт.", { integer: true, min: "0" })}
          ${numberInput("min_price", "Цена от", value("min_price", "0"), "1000", "₽", { integer: true, min: "0" })}
          ${numberInput("area_min", "Площадь от", value("area_min", "0"), "0.01", "м²", { min: "0" })}
          ${numberInput("area_max", "Площадь до", value("area_max", "0"), "0.01", "м²", { min: "0" })}
          ${roomsSelect(value("rooms", "1,2,3"))}
          ${editorSectionEnd()}
        </fieldset>
        <fieldset class="field-group" data-editor-section="location">
          ${editorSectionStart("location", "Локация и визуал")}
          ${numberInput("lat", "Широта", value("lat", "47.105"), "0.000001", "°", { min: "-90", max: "90" })}
          ${numberInput("lng", "Долгота", value("lng", "37.555"), "0.000001", "°", { min: "-180", max: "180" })}
          <input type="hidden" name="image" value="${value("image", "linear-gradient(135deg,#D6EEF8,#FFFFFF)")}" />
          ${dateInput("source_actuality", "Актуальность источника", value("source_actuality"))}
          ${editorSectionEnd()}
        </fieldset>
        ${objectCoverPicker(item)}
        ${editing && item ? objectDocsUploadForm(item) : ""}
        <fieldset class="field-group object-editor-wide" data-editor-section="description">
          ${editorSectionStart("description", "Описание и заметки")}
          ${textareaInput("description", "Описание", value("description"), "Несколько абзацев разделяйте пустой строкой.")}
          ${textareaInput("internal_notes", "Внутренние заметки", value("internal_notes"))}
          ${editorSectionEnd()}
        </fieldset>
        ${objectDossierEditor(item, draft)}
        <p class="field-error" id="object-editor-error" role="alert"></p>
        <div class="object-editor-submit">
          ${editing ? "" : `<button class="button secondary" type="button" data-save-object-draft>Сохранить черновик</button>`}
          <button class="primary" type="submit"><i data-lucide="check"></i> ${editing ? "Сохранить изменения" : "Создать объект"}</button>
        </div>
      </form>
    </section>
  `;
}

function objectImageStyle(image: unknown) {
  const value = safeObjectImageStyle(image);
  return `background:${h(value)}`;
}

function editorSectionStart(key: string, title: string) {
  const contentId = `object-editor-section-${key.replace(/[^a-z0-9_-]/gi, "-")}`;
  return `
    <legend class="editor-section-legend">
      <button class="editor-section-toggle" type="button" data-editor-section-toggle="${h(key)}" aria-controls="${contentId}" aria-expanded="false" aria-label="Открыть раздел «${h(title)}»">
        <span>${h(title)}</span>
        <i data-lucide="chevron-right" aria-hidden="true"></i>
      </button>
    </legend>
    <div class="editor-section-content" id="${contentId}" data-editor-section-content hidden>
  `;
}

function editorSectionEnd() {
  return "</div>";
}

function objectCoverUrl(image: unknown) {
  const value = String(image || "");
  const match = value.match(/url\((['"]?)(.*?)\1\)/);
  return match?.[2] || "";
}

function safeObjectImageStyle(image: unknown) {
  const fallback = "linear-gradient(135deg,#D6EEF8,#FFFFFF)";
  const value = String(image || fallback).trim();
  if (/^linear-gradient\([#,\s\w().%-]+\)$/i.test(value)) return value;
  const match = value.match(/^url\((['"]?)(\/api\/files\/\d+\/preview)\1\)(;\s*--object-cover-position:\s*[\d.]+%\s+[\d.]+%)?$/);
  return match ? value : fallback;
}

function objectCoverPosition(image: unknown) {
  const value = String(image || "");
  const match = value.match(/--object-cover-position:\s*([\d.]+)%\s+([\d.]+)%/);
  return {
    x: match ? clampNumber(Number(match[1]), 0, 100) : 50,
    y: match ? clampNumber(Number(match[2]), 0, 100) : 50
  };
}

function parseExistingCoverImages(value: string): ObjectCoverImage[] {
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        id: Number(item?.id || 0),
        url: String(item?.url || ""),
        name: String(item?.name || "Фото"),
        cropX: clampNumber(Number(item?.cropX || 50), 0, 100),
        cropY: clampNumber(Number(item?.cropY || 50), 0, 100),
        isPrimary: Boolean(item?.isPrimary)
      }))
      .filter((item) => item.url);
  } catch {
    return [];
  }
}

function objectCoverPicker(item: ApiObject | null) {
  const image = String(item?.image || "linear-gradient(135deg,#D6EEF8,#FFFFFF)");
  const coverUrl = objectCoverUrl(image);
  const coverPosition = objectCoverPosition(image);
  const existingCovers = item?.cover_images?.length
    ? item.cover_images
    : coverUrl
      ? [{ id: 0, url: coverUrl, name: "Текущая обложка", cropX: coverPosition.x, cropY: coverPosition.y, isPrimary: true }]
      : [];
  return `
    <fieldset class="field-group object-editor-wide object-cover-picker" data-cover-picker data-existing-covers="${h(JSON.stringify(existingCovers))}" data-editor-section="cover">
      ${editorSectionStart("cover", "Фотографии обложки")}
      <div class="cover-picker-layout">
        <div class="cover-crop-stage" data-cover-crop-stage>
          <div class="cover-crop-preview" data-cover-preview style="${objectImageStyle(image)}">
            <img data-cover-crop-image alt="" draggable="false" hidden />
            <span data-cover-placeholder>${coverUrl ? "Перетащите фото внутри рамки, чтобы кадрировать обложку" : "Нажмите, чтобы выбрать фото для обложки"}</span>
            <input name="cover" type="file" accept="image/*" multiple data-cover-input aria-label="Выбрать фотографии обложки" />
          </div>
          <p class="cover-crop-hint"><i data-lucide="crop"></i> Кадрирование: тяните изображение внутри рамки. Видимая область станет финальной обложкой.</p>
        </div>
        <div class="cover-picker-panel">
          <div class="cover-panel-head">
            <strong>Фотографии</strong>
          </div>
          <div class="cover-thumbnails" data-cover-thumbnails aria-label="Выбранные фотографии обложки"></div>
          <p data-cover-panel-note>Выберите миниатюру, чтобы кадрировать её в большой рамке.</p>
        </div>
      </div>
      <input type="hidden" name="coverCropX" value="50" data-cover-crop-x />
      <input type="hidden" name="coverCropY" value="50" data-cover-crop-y />
      <input type="hidden" name="coverActiveIndex" value="0" data-cover-active-index />
      <input type="hidden" name="coverExistingActive" value="${existingCovers.length ? "1" : "0"}" data-cover-existing-active />
      <input type="hidden" name="coverExistingFileId" value="${existingCovers.find((cover) => cover.isPrimary)?.id || existingCovers[0]?.id || 0}" data-cover-existing-file-id />
      <input type="hidden" name="coverRemoveExisting" value="0" data-cover-remove-existing />
      <input type="hidden" name="coverRemoveIds" value="" data-cover-remove-ids />
      ${editorSectionEnd()}
    </fieldset>
  `;
}

function objectDossierEditor(item: ApiObject | null, draft: Record<string, any>) {
  return `
    <section class="object-editor-wide object-dossier-editor" data-dossier-editor>
      <div class="section-header section-header-compact">
        <div>
          <p class="eyebrow">Досье объекта</p>
          <h3>Корпуса, остатки, условия и контакты</h3>
        </div>
      </div>
      ${dossierRepeater("object_buildings", "Корпуса", "Добавить корпус", [
        ["name", "Литер", "text"],
        ["handover_date", "Срок сдачи", "date"],
        ["status", "Статус", "text"],
        ["construction_type", "Конструктив", "text"],
        ["source_actuality", "Актуальность", "date"],
        ["notes", "Комментарий", "textarea"]
      ], dossierValues(item, draft, "object_buildings"))}
      ${dossierRepeater("available_units", "Квартиры в наличии", "Добавить квартиру", [
        ["building_name", "Литер", "text"],
        ["unit_number", "№ квартиры", "text"],
        ["rooms", "Комн.", "number", "комн."],
        ["area", "Площадь", "number", "м²"],
        ["floor", "Этаж", "number", "эт."],
        ["status", "Статус", "text"],
        ["price", "Цена", "number", "₽"],
        ["source_actuality", "Актуальность", "date"],
        ["notes", "Комментарий", "textarea"]
      ], dossierValues(item, draft, "available_units"))}
      ${dossierRepeater("booking_terms", "Бронь", "Добавить условие", [
        ["title", "Название", "text"],
        ["amount", "Сумма", "number", "₽"],
        ["duration", "Срок", "text"],
        ["included", "Что входит", "textarea"],
        ["excluded", "Что не входит", "textarea"],
        ["source_actuality", "Актуальность", "date"],
        ["notes", "Комментарий", "textarea"]
      ], dossierValues(item, draft, "booking_terms"))}
      ${dossierRepeater("purchase_programs", "Программы и ипотека", "Добавить программу", [
        ["name", "Название", "text"],
        ["type", "Тип", "text"],
        ["description", "Описание", "textarea"],
        ["eligibility", "Кому подходит", "textarea"],
        ["exclusions", "Исключения", "textarea"],
        ["limits", "Лимиты", "textarea"],
        ["source_actuality", "Актуальность", "date"],
        ["notes", "Внутренний комментарий", "textarea"]
      ], dossierValues(item, draft, "purchase_programs"))}
      ${dossierRepeater("document_requirements", "Документы", "Добавить документ", [
        ["title", "Документ", "text"],
        ["category", "Категория", "text"],
        ["condition_text", "Когда требуется", "textarea"],
        ["recipient", "Куда отправить", "text"],
        ["required", "Обязательный", "checkbox"]
      ], dossierValues(item, draft, "document_requirements"))}
      ${dossierRepeater("object_contacts", "Контакты", "Добавить контакт", [
        ["name", "Имя / канал", "text"],
        ["role", "Роль", "text"],
        ["phone", "Телефон", "tel"],
        ["email", "Email", "email"],
        ["schedule", "График", "textarea"],
        ["notes", "Комментарий", "textarea"]
      ], dossierValues(item, draft, "object_contacts"))}
      ${dossierRepeater("object_sources", "Источники", "Добавить источник", [
        ["title", "Источник", "text"],
        ["source_type", "Тип", "text"],
        ["url", "Ссылка", "url"],
        ["source_date", "Дата", "date"],
        ["notes", "Комментарий", "textarea"]
      ], dossierValues(item, draft, "object_sources"))}
    </section>
  `;
}

function dossierValues(item: ApiObject | null, draft: Record<string, any>, key: ObjectDossierKey) {
  if (Array.isArray(item?.[key])) return item?.[key] || [];
  if (Array.isArray(draft[key])) return draft[key];
  if (typeof draft[key] === "string") {
    try {
      const parsed = JSON.parse(draft[key]);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function dossierRepeater(key: ObjectDossierKey, title: string, addLabel: string, fields: DossierFieldConfig[], values: any[]) {
  const rows = values.length ? values : [];
  return `
    <fieldset class="field-group object-editor-wide dossier-repeater" data-dossier-key="${key}" data-dossier-fields="${h(JSON.stringify(fields))}" data-editor-section="${key}">
      ${editorSectionStart(key, title)}
      <input type="hidden" name="${key}" value="${h(JSON.stringify(rows))}" data-dossier-json="${key}" />
      <div class="dossier-repeater-rows" data-dossier-rows>
        ${rows.map((row) => dossierRepeaterRow(fields, row)).join("")}
      </div>
      <button class="button secondary" type="button" data-dossier-add><i data-lucide="plus"></i> ${addLabel}</button>
      ${editorSectionEnd()}
    </fieldset>
  `;
}

function dossierRepeaterRow(fields: DossierFieldConfig[], row: Record<string, any> = {}) {
  return `
    <div class="dossier-row" data-dossier-row>
      <div class="dossier-row-grid">
        ${fields.map(([name, label, type, unit]) => dossierField(name, label, type, row[name], unit)).join("")}
      </div>
      <button class="icon-button dossier-row-remove" type="button" data-dossier-remove aria-label="Удалить строку"${tooltipAttr("Удалить строку досье")}><i data-lucide="trash-2"></i></button>
    </div>
  `;
}

function dossierField(name: string, label: string, type: string, value: unknown, unit = "") {
  if (type === "textarea") {
    return `<label class="dossier-field dossier-field-wide"><span>${label}</span><textarea data-dossier-field="${name}" rows="2">${h(value ?? "")}</textarea></label>`;
  }
  if (type === "checkbox") {
    const checked = value === undefined || value === true || value === 1 || value === "1";
    return `<label class="dossier-field dossier-checkbox"><input data-dossier-field="${name}" type="checkbox" ${checked ? "checked" : ""} /><span>${label}</span></label>`;
  }
  if (type === "date") {
    return `<label class="dossier-field"><span>${label}</span><input data-dossier-field="${name}" type="date" value="${h(String(value ?? "").slice(0, 10))}" /></label>`;
  }
  if (type === "number") {
    const integer = integerDossierFields.has(name);
    return `
      <label class="dossier-field">
        <span>${label}</span>
        <span class="object-field-control">
          <input data-dossier-field="${name}" type="number" inputmode="${integer ? "numeric" : "decimal"}" min="0" step="${numberStepForField(name)}" value="${h(value ?? "")}" data-object-mask="${integer ? "integer" : "decimal"}" />
          ${unit ? `<small aria-hidden="true">${unit}</small>` : ""}
        </span>
      </label>
    `;
  }
  const mask = type === "tel" ? ` data-object-mask="phone" inputmode="tel" autocomplete="tel" placeholder="+7 ___ ___-__-__"` : "";
  return `<label class="dossier-field"><span>${label}</span><input data-dossier-field="${name}" type="${type}" value="${h(value ?? "")}"${mask} /></label>`;
}

function objectDetailSections(item: ApiObject) {
  const rooms = String(item.rooms || "").split(",").map((room) => room.trim()).filter(Boolean);
  return `
    <section class="object-detail-stack">
      <article class="object-detail-section">
        <h3>Описание</h3>
        <div class="object-description">${paragraphs(item.description)}</div>
      </article>
      <article class="object-detail-section">
        <h3>Основные параметры</h3>
        <div class="metrics">
          <div><span>Минимальная цена</span><strong>${h(formatObjectPrice(item.min_price))}</strong></div>
          <div><span>Статус</span><strong>${h(item.status)}</strong></div>
          <div><span>Срок сдачи</span><strong>${item.handover_date ? safeDate(item.handover_date) : "Уточняется по корпусу"}</strong></div>
          <div><span>Корпусов</span><strong>${item.buildings}</strong></div>
          <div><span>Квартир доступно</span><strong>${item.units > 0 ? item.units : "Уточняется"}</strong></div>
          <div><span>Обновлено</span><strong>${safeDate(item.updated_at)}</strong></div>
        </div>
      </article>
      <article class="object-detail-section">
        <h3>Планировки и площади</h3>
        <div class="metrics">
          ${rooms.length ? rooms.map((room) => `<div><span>Комнатность</span><strong>${h(formatRoomLabel(room))}</strong></div>`).join("") : `<div><span>Комнатность</span><strong>Не указана</strong></div>`}
          <div><span>Диапазон площади</span><strong>${item.area_min > 0 && item.area_max > 0 ? `${number.format(item.area_min)}–${number.format(item.area_max)} м²` : "Уточняется"}</strong></div>
        </div>
      </article>
      <article class="object-detail-section">
        <h3>Локация</h3>
        <div class="metrics">
          <div><span>Район</span><strong>${h(item.district)}</strong></div>
          <div><span>Адрес</span><strong>${h(item.address)}</strong></div>
          <div><span>Широта</span><strong>${number.format(Number(item.lat))}</strong></div>
          <div><span>Долгота</span><strong>${number.format(Number(item.lng))}</strong></div>
        </div>
      </article>
      <article class="object-detail-section">
        <h3>Файлы и медиа</h3>
        <div class="row-card">
          <i data-lucide="folder"></i>
          <div><strong>Документы объекта</strong><span>Презентации, договоры, фото и рабочие материалы открываются в разделе файлов.</span></div>
          <a class="button secondary" href="#/files">Открыть файлы</a>
        </div>
      </article>
      ${!isClientWorkMode() && (item.internal_notes || item.source_actuality) ? `<article class="object-detail-section">
        <h3>Внутренние заметки</h3>
        ${item.internal_notes ? `<aside class="internal-block"><strong>Для сотрудников</strong><p>${h(item.internal_notes)}</p></aside>` : ""}
        <div class="metrics"><div><span>Актуальность источника</span><strong>${item.source_actuality ? safeDate(item.source_actuality) : "Не указана"}</strong></div></div>
      </article>` : ""}
    </section>
  `;
}

function paragraphs(value: unknown) {
  const items = String(value || "").split(/\n{2,}/).map((item) => item.trim()).filter(Boolean);
  return items.length ? items.map((item) => `<p>${h(item)}</p>`).join("") : `<p>Описание пока не добавлено.</p>`;
}

function objectDocsUploadForm(item: ApiObject) {
  return `
    <fieldset class="field-group object-editor-wide object-docs-upload" data-editor-section="object_files">
      ${editorSectionStart("object_files", "Файлы объекта")}
      <div class="object-docs-form" id="object-docs-form" data-object-id="${item.id}">
        <label>
          <span>Документы и фото</span>
          <input name="objectFiles" type="file" multiple hidden />
          <button class="secondary" type="button" data-upload-trigger><i data-lucide="upload"></i> Выбрать файлы</button>
        </label>
        <button class="primary" type="button" data-object-docs-submit><i data-lucide="upload"></i> Загрузить в папку объекта</button>
        <div class="files-upload-list object-upload-list" data-upload-list>Файлы не выбраны</div>
        <div class="upload-progress" data-upload-progress hidden><span></span><strong>0%</strong></div>
        <p class="field-error" id="object-docs-error" role="alert"></p>
      </div>
      ${editorSectionEnd()}
    </fieldset>
  `;
}

function objectStepActions(previousObject: ApiObject | null, nextObject: ApiObject | null) {
  const previous = previousObject
    ? `<a class="button secondary" href="${objectHref(previousObject.id)}" aria-label="Предыдущий объект: ${h(previousObject.name)}">← Назад</a>`
    : `<span class="button secondary is-disabled" aria-disabled="true">← Назад</span>`;
  const next = nextObject
    ? `<a class="button secondary" href="${objectHref(nextObject.id)}" aria-label="Следующий объект: ${h(nextObject.name)}">Далее →</a>`
    : `<span class="button secondary is-disabled" aria-disabled="true">Далее →</span>`;
  return `<div class="object-step-actions" aria-label="Навигация между объектами">${previous}${next}</div>`;
}

function mapPage(): string {
  const filters = objectFiltersFromUrl();
  const items = filteredObjects(filters);
  const districts = unique(objects.map((item) => item.district));
  const statuses = unique(objects.map((item) => item.status));
  return `
    <section class="workspace-intro">
      <div>
        <p class="eyebrow">Карта консультаций</p>
        <h2>Объекты в рабочей зоне менеджера</h2>
      </div>
      <span>${items.length} ${pluralizeObjects(items.length)}</span>
    </section>
    ${objectFilterToolbar(districts, statuses, filters)}
    <section class="map-layout">
      <div class="map-canvas" data-provider-map aria-label="Карта объектов">
        <div class="provider-map" id="provider-map"></div>
        <div class="map-fallback" data-map-fallback>
          <div class="map-grid"></div>
          ${items.filter(hasObjectCoordinates).map(marker).join("")}
          <span class="map-status" data-map-status>Загружаем карту…</span>
        </div>
      </div>
      <aside class="map-list">
        <h2>Объекты на карте</h2>
        ${items.length ? items.map((item) => `<button class="map-list-item" data-open-object="${item.id}"><strong>${h(item.name)}</strong><span>${h(item.district)} · ${h(formatObjectPrice(item.min_price))}</span></button>`).join("") : emptyState("Объекты не найдены", "Измените фильтры или сбросьте поиск.")}
      </aside>
    </section>
  `;
}

function marker(item: ApiObject): string {
  const x = ((item.lng - 37.52) / 0.06) * 100;
  const y = 100 - ((item.lat - 47.08) / 0.05) * 100;
  return `<button class="map-marker ${statusBadgeClass(item.status)}" style="left:${Math.max(5, Math.min(92, x))}%;top:${Math.max(8, Math.min(88, y))}%" data-open-object="${item.id}">${h(formatObjectPrice(item.min_price))}</button>`;
}

function calculatorPage(): string {
  const selected = calculatorObjectFromUrl();
  const price = Number(selected?.min_price || 0);
  const initialPrice = price > 0 ? price : 0;
  const tab = calculatorTabFromUrl();
  return `
    <section class="calculator-screen">
      <section class="calculator-header-panel">
        <div>
          <p class="eyebrow">Рабочие сценарии</p>
          <h2>Калькулятор менеджера</h2>
        </div>
        ${calculatorTabs(tab)}
      </section>
      ${calculatorObjectSelector(selected)}
      ${tab === "mortgage-compare" ? mortgageComparePage(initialPrice) : profitabilityCalculatorPage(selected, initialPrice)}
    </section>
  `;
}

function calculatorObjectSelector(selected: ApiObject | null) {
  const selectedLabel = selected ? `${selected.name} — ${formatObjectPrice(selected.min_price)}` : "";
  return `
    <section class="calculator-object-picker" aria-label="Объект для расчета">
      <label for="calculator-object-select">
        <span>Объект расчета</span>
        <select id="calculator-object-select">
          <option value="">Выберите объект</option>
          ${objects.map((item) => `<option value="${item.id}" ${selected?.id === item.id ? "selected" : ""}>${h(item.name)} · ${h(item.district)}</option>`).join("")}
        </select>
      </label>
      ${selected ? `<div class="selected-object-summary" title="${h(selectedLabel)}"><strong>${h(selectedLabel)}</strong></div>` : `<div class="selected-object-summary is-empty"><strong>Ручной расчёт — объект не выбран</strong></div>`}
    </section>
  `;
}

function profitabilityCalculatorPage(selected: ApiObject | null, price: number): string {
  const effectivePrice = price || 7_200_000;
  const purchaseDate = isoDateValue(new Date());
  const projectDate = /^\d{4}-\d{2}-\d{2}$/.test(String(selected?.handover_date || ""))
    ? String(selected?.handover_date)
    : "";
  return `
    <section class="calculator-layout profitability-layout">
      <form class="calc-form investment-calculator-form" id="calc-form">
        <input type="hidden" name="objectId" value="${selected?.id || ""}" />
        <input type="hidden" name="methodVersion" value="2" />
        <div class="calc-state-bar" id="calc-state-bar" data-state="incomplete"><span id="calc-validation-message" role="status"></span><button type="button" class="secondary" data-calc-fix hidden>Исправить поле</button><button type="button" class="secondary" data-calc-go-result>Результат</button></div>
        <div class="form-section calculator-form-intro"><h2>${selected ? h(selected.name) : "Ручной расчёт"}</h2></div>
        <section class="field-group calc-input-section" data-warning-group="purchase" aria-labelledby="calc-section-basics"><h3 id="calc-section-basics" class="calc-section-title">Основные параметры</h3><div class="calc-section-fields">
          ${calcNumberField("price", "Стоимость квартиры", effectivePrice, "₽")}
          <p class="calc-field-help">Цена после скидки, с бронью и отделкой.</p>
          ${calcNumberField("area", "Метраж", selected?.area_min || 34, "м²")}
          ${calcDateField("purchaseDate", "Дата покупки", purchaseDate, true)}
          ${calcDateField("projectDate", "Сдача дома", projectDate)}
          ${calcDateField("keysDate", "Получение ключей", "", true)}
          ${calcNumberField("repairMonths", "Срок ремонта", 3, "мес.", { integer: true })}
          ${calcNumberField("investmentYears", "Горизонт", 5, "лет")}
          ${calcNumberField("ownershipYears", "До продажи", 5, "лет")}
          ${calcNumberField("valueIndex", "Рост стоимости", 5, "%/год", { min: -99 })}
          <p class="field-error" data-calc-warning="purchase" role="alert"></p>
        </div></section>
        <section class="field-group calc-input-section" data-warning-group="mortgage" aria-labelledby="calc-section-mortgage"><h3 id="calc-section-mortgage" class="calc-section-title">Ипотека</h3><div class="calc-section-fields">
          <p class="calc-field-help">ПВ по ДДУ, без брони.</p>
          ${calcNumberField("downPayment", "Первоначальный взнос", Math.round(effectivePrice / 6), "₽")}
          ${calcNumberField("rate", "Ставка", 2, "% годовых")}
          ${calcNumberField("loanYears", "Срок кредита", 20, "лет")}
          ${calcNumberField("manualPayment", "Платёж вручную", "", "₽/мес.", { placeholder: "авто" })}
          <p class="field-error" data-calc-warning="mortgage" role="alert"></p>
        </div></section>
        <section class="field-group calc-input-section" data-warning-group="rent" aria-labelledby="calc-section-rent"><h3 id="calc-section-rent" class="calc-section-title">Аренда</h3><div class="calc-section-fields">
          ${calcNumberField("rentRate", "Ставка аренды", 40_000, "₽/мес.")}
          ${calcNumberField("rentIndex", "Индексация", 5, "%/год", { min: -99 })}
          ${calcNumberField("plannedRentYears", "Срок аренды", 5, "лет")}
          ${calcNumberField("rentTaxPct", "Налог со сдачи", 0, "%", { max: 100, required: true })}
          <p class="field-error" data-calc-warning="rent" role="alert"></p>
        </div></section>
        <section class="field-group calc-input-section" data-warning-group="fitout" aria-labelledby="calc-section-fitout"><h3 id="calc-section-fitout" class="calc-section-title">Ремонт, мебель и страхование</h3><div class="calc-section-fields">
          ${calcNumberField("repairCost", "Ремонт под ключ", 1_200_000, "₽")}
          ${calcNumberField("furnitureCost", "Мебель", 0, "₽")}
          ${calcNumberField("applianceCost", "Бытовая техника", 0, "₽")}
          ${calcNumberField("insuranceLifeAnnual", "Страхование жизни", 0, "₽/год")}
          ${calcNumberField("insuranceLifeYears", "Срок страхования жизни", 20, "лет")}
          ${calcNumberField("insurancePropertyAnnual", "Страхование имущества", 0, "₽/год")}
          ${calcNumberField("insurancePropertyYears", "Срок страхования имущества", 20, "лет")}
          ${calcNumberField("appraisalCost", "Оценка имущества", 0, "₽")}
          ${calcCheckbox("repairIncludes", "Ремонт уже включает мебель и технику", true)}
          <p class="field-error" data-calc-warning="fitout" role="alert"></p>
        </div></section>
        <section class="field-group calc-input-section" data-warning-group="expenses" aria-labelledby="calc-section-operating"><h3 id="calc-section-operating" class="calc-section-title">Другие расходы и налоги</h3><div class="calc-section-fields">
          ${calcNumberField("utilities", "Коммунальные без счётчиков", 0, "₽/мес.")}
          ${calcNumberField("utilitiesGrowthPct", "Рост коммунальных услуг", 0, "%/год", { min: -99, max: 100 })}
          ${calcNumberField("managementPct", "Управляющая компания", 0, "%", { max: 100 })}
          ${calcNumberField("propertyTaxAnnual", "Налог на имущество", 0, "₽/год")}
          ${calcNumberField("realtorPct", "Услуги риелтора при продаже", 0, "%", { max: 100 })}
          ${calcNumberField("saleTaxPct", "Налог с продажи", 13, "%", { max: 100 })}
          ${calcNumberField("minOwnYears", "Владение без налога", 5, "лет")}
          ${calcCheckbox("ownerPaysUtilities", "После сдачи коммунальные платит собственник", false)}
          <p class="field-error" data-calc-warning="expenses" role="alert"></p>
        </div></section>
        <section class="field-group calc-input-section" aria-labelledby="calc-section-purchase-costs"><h3 id="calc-section-purchase-costs" class="calc-section-title">Дополнительные расходы при покупке</h3><div class="calc-section-fields">
          ${calcNumberField("bookingCost", "Бронь отдельно от ПВ", 0, "₽")}
          ${calcNumberField("registrationCost", "Расходы по оформлению", 0, "₽")}
          ${calcExpenses("Purchase")}
        </div></section>
        <section class="field-group calc-input-section" aria-labelledby="calc-section-before-rent"><h3 id="calc-section-before-rent" class="calc-section-title">Расходы после покупки до запуска аренды</h3><div class="calc-section-fields">
          ${calcNumberField("acceptanceCost", "Приёмка квартиры", 0, "₽")}
          ${calcNumberField("dutyCost", "Госпошлина", 0, "₽")}
          ${calcExpenses("BeforeRent")}
        </div></section>
        <section class="field-group calc-input-section" aria-labelledby="calc-section-after-rent"><h3 id="calc-section-after-rent" class="calc-section-title">Дополнительные расходы после сдачи</h3><div class="calc-section-fields">
          ${calcExpenses("AfterRent")}
        </div></section>
        <section class="field-group calc-input-section" aria-labelledby="calc-section-client-report"><h3 id="calc-section-client-report" class="calc-section-title">Клиентский отчёт</h3><div class="calc-section-fields">
          <label class="calc-comment-field" for="calc-comment"><span>Комментарий менеджера для клиента</span><textarea id="calc-comment" name="comment" maxlength="5000" rows="4" placeholder="На что клиенту обратить внимание"></textarea></label>
          <div class="calc-attachment">
            <label for="calc-floor-plan">Планировка квартиры (PNG или JPEG)</label>
            <input id="calc-floor-plan" type="file" accept="image/png,image/jpeg" aria-describedby="calc-floor-plan-status" />
            <input type="hidden" name="floorPlan" value="" />
            <p id="calc-floor-plan-status" role="status">Не добавлена</p>
            <img id="calc-floor-plan-image" alt="Планировка для отчёта" hidden />
            <button class="ghost" id="calc-remove-floor-plan" type="button" hidden>Удалить планировку</button>
          </div>
        </div></section>
      </form>
      <aside class="calc-result" id="calc-result" aria-live="polite">
        ${skeleton("Расчет обновляется")}
      </aside>
    </section>
  `;
}

function calcNumberField(
  name: string,
  label: string,
  value: number | string,
  unit: string,
  options: { min?: number; max?: number; integer?: boolean; decimalPlaces?: number; placeholder?: string; required?: boolean } = {}
) {
  const rule = numberFieldRule(name);
  options = { ...options, ...rule };
  const id = `calc-${name}`;
  return `<label class="number-field calc-field" for="${id}"><span>${label}</span><span class="compare-control"><input id="${id}" name="${name}" type="number" inputmode="decimal" autocomplete="off" ${options.required ? "required" : ""} min="${options.min ?? 0}" ${options.max == null ? "" : `max="${options.max}"`} step="${options.integer ? 1 : options.decimalPlaces === 2 ? "0.01" : "any"}" value="${value}" ${options.placeholder ? `placeholder="${h(options.placeholder)}"` : ""}/><small aria-hidden="true">${unit}</small></span></label>`;
}

function calcDateField(name: string, label: string, value: string, required = false) {
  const id = `calc-${name}`;
  return `<label class="number-field calc-field" for="${id}"><span>${label}</span><input id="${id}" name="${name}" type="date" min="1900-01-01" max="2200-12-31" ${required ? "required" : ""} value="${h(value)}" /></label>`;
}

let expenseRowSequence = 0;
function calcExpenses(phase: "Purchase" | "BeforeRent" | "AfterRent") {
  return `<div class="calc-expense-list" data-expense-list="${phase}">${calcExpenseRow(phase, true)}</div><button type="button" class="ghost calc-add-expense" data-add-expense="${phase}"><i data-lucide="plus"></i> Добавить расход</button>`;
}
function calcExpenseRow(phase: string, first = false) {
  const suffix = first ? "" : `-${++expenseRowSequence}`;
  const field = (key: string) => `extra${phase}${key}${suffix}`;
  const labels = { "outside-price": "Сверх цены", "price-outside-ddu": "В цене, отдельно от ДДУ", "inside-ddu": "Уже в ДДУ" };
  return `<div class="calc-expense-row" data-expense-row data-expense-phase="${phase}">
    <label class="calc-field" for="calc-${field("Name")}"><span>Название</span><input id="calc-${field("Name")}" name="${field("Name")}" data-expense-name maxlength="120" placeholder="Например, отделка" /></label>
    ${calcNumberField(field("Amount"), "Сумма", 0, "₽")}
    <button type="button" class="calc-expense-remove" data-remove-expense aria-label="Удалить расход"><i data-lucide="x"></i></button>
    ${phase !== "AfterRent" ? `<label class="calc-field calc-expense-accounting"><span>Учёт этого расхода</span><select id="calc-${field("Treatment")}" name="${field("Treatment")}" data-ui="segments">${Object.entries(labels).map(([value,label])=>`<option value="${value}">${label}</option>`).join("")}</select></label>` : ""}
    ${phase !== "Purchase" ? calcCheckbox(field("Monthly"), "Каждый месяц", false) : ""}
  </div>`;
}
function readExpenses(form: HTMLFormElement) {
  return Array.from(form.querySelectorAll<HTMLElement>("[data-expense-row]")).flatMap(row => {
    const amount = Number(row.querySelector<HTMLInputElement>('input[type="number"]')?.value || 0);
    const name = row.querySelector<HTMLInputElement>("[data-expense-name]")!;name.required = amount > 0;
    name.setCustomValidity(amount > 0 && !name.value.trim() ? "Введите название расхода" : "");
    if (!name.value.trim() && amount === 0) return [];
    return [{ name: name.value.trim(), amount, phase: ({Purchase:"purchase", BeforeRent:"before-rent",AfterRent:"after-rent"} as Record<string,string>)[row.dataset.expensePhase!], treatment: row.querySelector<HTMLSelectElement>("select")?.value || "outside-price", monthly: row.querySelector<HTMLInputElement>('input[type="checkbox"]')?.checked || false }];
  });
}

function calcCheckbox(name: string, label: string, checked: boolean) {
  const id = `calc-${name}`;
  return `<label class="calc-checkbox" for="${id}"><input type="hidden" name="${name}" value="false"/><input id="${id}" name="${name}" type="checkbox" value="true" ${checked ? "checked" : ""}/><span>${label}</span></label>`;
}

function mortgageComparePage(price: number): string {
  const firstRate = 6.8;
  const secondRate = 0;
  const firstYears = 20;
  const secondYears = 30;
  return `
    <section class="mortgage-compare-screen">
      <div class="compare-intro">
        <p class="eyebrow">Сравнение условий</p>
        <h2>Ипотечные сценарии</h2>
      </div>
      <form id="mortgage-compare-form" class="mortgage-compare-grid">
        ${mortgageCompareCard(1, "Базовый сценарий", price, firstYears, firstRate)}
        ${mortgageCompareCard(2, "Альтернативный сценарий", price, secondYears, secondRate)}
      </form>
      <section class="compare-summary-card" aria-label="Разница между сценариями">
        <div class="result-header">
          <p class="eyebrow">Итог сравнения</p>
          <h2>Альтернативный сценарий относительно базового</h2>
        </div>
        <div class="compare-summary-layout">
          ${compareSummaryMetric("Платеж в месяц", "paymentDiff", true)}
          ${compareSummaryMetric("Разница в переплате за весь срок", "overpayDiff")}
        </div>
      </section>
    </section>
  `;
}

function mortgageCompareCard(index: 1 | 2, title: string, amount: number, years: number, rate: number): string {
  return `
    <article class="compare-card">
      <div class="compare-card-header">
        <span class="scenario-index">${index}</span>
        <div>
          <h3>${title}</h3>
        </div>
      </div>
      <div class="compare-inputs">
        ${compareInput(index, "amount", "Сумма кредита", amount, "₽", "10000")}
        ${compareInput(index, "years", "Срок кредита", years, "лет", "1")}
        ${compareInput(index, "rate", "Процентная ставка", rate, "%", "0.01")}
      </div>
      <dl class="compare-metrics">
        ${compareMetric("Сумма кредита", `amountResult${index}`)}
        ${compareMetric("Срок кредита", `yearsResult${index}`)}
        ${compareMetric("Процентная ставка", `rateResult${index}`)}
        ${compareMetric("Ежемесячный платеж", `payment${index}`, true)}
        ${compareMetric("Переплата за весь срок", `overpay${index}`)}
        ${compareMetric("Всего выплат", `total${index}`)}
      </dl>
    </article>
  `;
}

function compareInput(index: 1 | 2, name: string, label: string, value: number, unit: string, step: string): string {
  const id = `compare-${name}-${index}`;
  return `
    <label class="compare-input" for="${id}">
      <span>${label}</span>
      <span class="compare-control">
        <input id="${id}" name="${name}${index}" type="number" inputmode="decimal" autocomplete="off" min="0" step="${step}" value="${value}" />
        <small aria-hidden="true">${unit}</small>
      </span>
    </label>
  `;
}

function compareMetric(label: string, id: string, emphasize = false): string {
  return `<div class="${emphasize ? "is-emphasis" : ""}"><dt>${label}</dt><dd id="${id}">-</dd></div>`;
}

function compareSummaryMetric(label: string, id: string, primary = false): string {
  return `<div class="compare-summary-metric ${primary ? "is-primary" : ""}"><span>${label}</span><strong id="${id}">-</strong></div>`;
}

function input(name: string, label: string, value: number, unit: string, min = "0", tooltip = ""): string {
  const id = `calc-${name}`;
  return `
    <label class="number-field" for="${id}"${tooltip ? tooltipAttr(tooltip) : ""}>
      <span>${label}</span>
      <span class="compare-control">
        <input id="${id}" name="${name}" type="number" inputmode="decimal" autocomplete="off" min="${min}" step="0.01" value="${value}" />
        <small aria-hidden="true">${unit}</small>
      </span>
    </label>
  `;
}

function textInput(name: string, label: string, value: string) {
  return `<label class="number-field"><span>${label}</span><input name="${name}" value="${value}" autocomplete="off" /></label>`;
}

function numberInput(name: string, label: string, value: string, step: string, unit = "", options: { min?: string; max?: string; integer?: boolean } = {}) {
  const id = `object-${name}`;
  const mask = options.integer ? "integer" : "decimal";
  const min = options.min ?? "";
  const max = options.max ?? "";
  const allowNegative = min.startsWith("-");
  return `
    <label class="number-field" for="${id}">
      <span>${label}</span>
      <span class="object-field-control">
        <input id="${id}" name="${name}" type="number" inputmode="decimal" autocomplete="off" step="${step}" value="${value}" data-object-mask="${mask}" ${allowNegative ? `data-object-allow-negative="true"` : ""} ${min ? `min="${min}"` : ""} ${max ? `max="${max}"` : ""} />
        ${unit ? `<small aria-hidden="true">${unit}</small>` : ""}
      </span>
    </label>
  `;
}

function dateInput(name: string, label: string, value: string) {
  return `<label class="number-field"><span>${label}</span><input name="${name}" type="date" value="${h(String(value).slice(0, 10))}" /></label>`;
}

function textareaInput(name: string, label: string, value: string, hint = "") {
  return `<label class="number-field textarea-field"><span>${label}</span><textarea name="${name}" rows="7">${value}</textarea>${hint ? `<small>${h(hint)}</small>` : ""}</label>`;
}

function selectInput(name: string, label: string, value: string, options: string[]) {
  return `<label class="number-field"><span>${label}</span><select name="${name}">${options.map((option) => `<option value="${h(option)}" ${option === value ? "selected" : ""}>${h(option)}</option>`).join("")}</select></label>`;
}

function roomsSelect(value: string) {
  const selected = new Set(String(value || "").split(",").map((item) => item.trim()).filter(Boolean));
  const options = ["Студия", "1", "2", "3", "4", "5+"];
  const label = selected.size ? options.filter((option) => selected.has(option)).map(formatRoomLabel).join(", ") : "Выберите комнатность";
  return `
    <label class="number-field rooms-field">
      <span>Комнатность</span>
      <button class="rooms-selector-button" type="button" data-rooms-toggle aria-expanded="false">
        <span data-rooms-label>${h(label)}</span>
        <i data-lucide="chevron-right" aria-hidden="true"></i>
      </button>
      <input type="hidden" name="rooms" value="${h([...selected].join(","))}" data-rooms-value />
      <div class="rooms-popover" data-rooms-popover hidden>
        ${options.map((option) => `
          <label>
            <input type="checkbox" value="${h(option)}" data-room-option ${selected.has(option) ? "checked" : ""} />
            <span>${h(formatRoomLabel(option))}</span>
          </label>
        `).join("")}
      </div>
    </label>
  `;
}

function formatRoomLabel(room: string) {
  return room === "Студия" ? "Студия" : `${room}-комн.`;
}

function objectStatusOptions() {
  return unique([...objects.map((item) => String(item.status || "")), "В продаже", "Бронь", "Продано", "Сдан", "Проект"].filter(Boolean));
}

function filesPage(): string {
  queueMicrotask(() => void hydrateFiles());
  return `<section class="files-space" id="files-list" aria-busy="true">${skeleton("Загружаем файловое пространство")}</section>`;
}

function newsPage(): string {
  queueMicrotask(() => void hydrateNews());
  return `<section class="panel-list" id="news-list" aria-busy="true">${skeleton("Загружаем новости")}</section>`;
}

function aiRopWorkspacePage(): string {
  if (!appConfig?.aiRopEnabled) return emptyState("ИИ-РОП не подключен", "Обратитесь к администратору.");
  queueMicrotask(() => void hydrateAiRop(currentUser?.role || "manager"));
  return aiRopPage();
}

function usersPage(): string {
  if (!canManage()) return denied();
  queueMicrotask(() => void hydrateUsers());
  return `<section class="panel-list" id="users-list" aria-busy="true">${skeleton("Загружаем пользователей")}</section>`;
}

function auditPage(): string {
  if (!canManage()) return denied();
  queueMicrotask(() => void hydrateAudit());
  return `<section class="panel-list" id="audit-list" aria-busy="true">${skeleton("Загружаем журнал")}</section>`;
}

function bindGlobal() {
  const navToggle = document.querySelector<HTMLButtonElement>("[data-action='toggle-nav']");
  const navBackdrop = document.querySelector<HTMLButtonElement>("[data-action='close-nav']");
  navToggle?.addEventListener("click", () => setMobileNavOpen(!document.body.classList.contains("is-mobile-nav-open")));
  navBackdrop?.addEventListener("click", () => setMobileNavOpen(false));
  document.querySelectorAll<HTMLAnchorElement>(".module-nav a").forEach((link) => {
    link.addEventListener("click", () => setMobileNavOpen(false));
  });
  document.removeEventListener("keydown", closeMobileNavOnEscape);
  document.addEventListener("keydown", closeMobileNavOnEscape);
  document.querySelector("[data-action='logout']")?.addEventListener("click", async () => {
    await api("/api/auth/logout", { method: "POST" });
    currentUser = null;
    setMobileNavOpen(false);
    render();
  });
  document.querySelector("[data-action='toggle-client-work-mode']")?.addEventListener("click", () => {
    setClientWorkMode(!isClientWorkMode());
    setMobileNavOpen(false);
    render();
  });
  bindObjectActions();
  bindObjectEditorSections();
  bindObjectEditor();
  bindObjectDossierEditor();
  bindObjectFieldMasks();
  bindZeroValueReplacement();
  bindRoomSelectors();
  bindObjectCoverPickers();
  bindObjectAssetForms();
  bindObjectFilters();
  bindObjectTabs();
  bindObjectFileRows();
  bindCalculator();
  bindCalculatorTabs();
  bindMortgageCompare();
  bindMapHighlights();
  void bindProviderMap();
}

function setMobileNavOpen(open: boolean) {
  const wasOpen = document.body.classList.contains("is-mobile-nav-open");
  document.body.classList.toggle("is-mobile-nav-open", open);
  const navToggle = document.querySelector<HTMLButtonElement>("[data-action='toggle-nav']");
  navToggle?.setAttribute("aria-expanded", open ? "true" : "false");
  navToggle?.setAttribute("aria-label", open ? "Закрыть меню" : "Открыть меню");
  if (open) (document.querySelector<HTMLElement>(".module-nav-mobile a.active") || document.querySelector<HTMLElement>(".module-nav-mobile a"))?.focus({ preventScroll: true });
  else if (wasOpen) navToggle?.focus({ preventScroll: true });
}

function closeMobileNavOnEscape(event: KeyboardEvent) {
  if (event.key === "Escape" && document.body.classList.contains("is-mobile-nav-open")) setMobileNavOpen(false);
}

function bindObjectActions() {
  document.querySelectorAll<HTMLElement>("[data-open-object]").forEach((node) => node.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("button, a, input, select, textarea, label")) return;
    location.hash = objectRoute(node.dataset.openObject || "");
  }));
  document.querySelectorAll<HTMLElement>(".object-card[data-open-object]").forEach((node) => node.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    location.hash = objectRoute(node.dataset.openObject || "");
  }));
  document.querySelectorAll<HTMLElement>("[data-calc-object]").forEach((node) => node.addEventListener("click", (event) => {
    event.stopPropagation();
    activeObject = objects.find((item) => String(item.id) === node.dataset.calcObject) || null;
    location.hash = calculatorRoute(node.dataset.calcObject || "");
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-delete-object]").forEach((button) => {
    button.addEventListener("click", async (event) => {
      event.stopPropagation();
      const objectId = button.dataset.deleteObject || "";
      const item = objects.find((object) => String(object.id) === objectId) || activeObject;
      if (!objectId || !item || !await confirmDialog({
        title: "Удалить объект?",
        body: `Объект «${item.name}» будет полностью удален из портала вместе с досье, доступами, новостями и папкой файлов. Загруженные файлы объекта будут скрыты и попадут в корзину на 21 день.`,
        confirmText: "Удалить объект"
      })) return;
      try {
        button.disabled = true;
        await api(`/api/objects/${objectId}`, { method: "DELETE" });
        activeObject = null;
        await loadObjects();
        location.hash = objectsListHref();
      } catch (error) {
        button.disabled = false;
        alert(message(error));
      }
    });
  });
  bindCalculatorObjectSelector();
}

function bindObjectEditorSections() {
  document.querySelectorAll<HTMLButtonElement>("[data-editor-section-toggle]").forEach((button) => {
    if (button.dataset.editorSectionBound === "true") return;
    button.dataset.editorSectionBound = "true";
    button.addEventListener("click", () => {
      const fieldset = button.closest<HTMLElement>("[data-editor-section]");
      const content = fieldset?.querySelector<HTMLElement>("[data-editor-section-content]");
      const title = button.querySelector<HTMLElement>("span")?.textContent?.trim() || "раздел";
      if (!content) return;
      const collapsed = !content.hidden;
      content.hidden = collapsed;
      fieldset?.classList.toggle("is-editor-section-collapsed", collapsed);
      button.setAttribute("aria-expanded", collapsed ? "false" : "true");
      button.setAttribute("aria-label", `${collapsed ? "Открыть" : "Скрыть"} раздел «${title}»`);
      button.setAttribute("data-tooltip", `${collapsed ? "Открыть" : "Скрыть"} раздел`);
      if (!collapsed) {
        fieldset?.querySelector<HTMLButtonElement>("[data-cover-thumb][aria-pressed='true']")?.click();
      }
    });
    const fieldset = button.closest<HTMLElement>("[data-editor-section]");
    const content = fieldset?.querySelector<HTMLElement>("[data-editor-section-content]");
    fieldset?.classList.toggle("is-editor-section-collapsed", Boolean(content?.hidden));
    fieldset?.addEventListener("click", (event) => {
      const target = event.target as HTMLElement | null;
      if (!content?.hidden || target?.closest("button, a, input, select, textarea, label")) return;
      button.click();
    });
  });
}

function bindCalculatorObjectSelector() {
  const select = document.querySelector<HTMLSelectElement>("#calculator-object-select");
  if (!select) return;
  select.addEventListener("change", () => {
    const item = objects.find((object) => String(object.id) === select.value) || null;
    activeObject = item;
    location.hash = calculatorRoute(select.value);
  });
}

function bindObjectEditor() {
  const form = document.querySelector<HTMLFormElement>("#object-editor-form");
  if (!form) return;
  const isDraftable = !form.dataset.objectId;
  if (isDraftable) bindObjectDraft(form);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    syncAllDossierInputs();
    const objectId = form.dataset.objectId || "";
    const formData = new FormData(form);
    const body = Object.fromEntries(formData.entries()) as Record<string, FormDataEntryValue | boolean>;
    body.rooms = String(formData.get("rooms") || "").trim();
    ["cover", "objectFiles", "coverCropX", "coverCropY", "coverActiveIndex", "coverExistingActive", "coverExistingFileId", "coverRemoveExisting", "coverRemoveIds"].forEach((key) => delete body[key]);
    const errorNode = document.querySelector<HTMLElement>("#object-editor-error");
    const submit = form.querySelector<HTMLButtonElement>("button[type='submit']");
    if (submit) submit.disabled = true;
    if (errorNode) errorNode.textContent = "";
    try {
      const coverValidation = validateObjectCoverSelection(form);
      if (coverValidation) throw new Error(coverValidation);
      const data = await api<{ item: ApiObject }>(objectId ? `/api/objects/${objectId}` : "/api/objects", {
        method: objectId ? "PATCH" : "POST",
        body: JSON.stringify(body)
      });
      try {
        await uploadObjectCoverPhotos(form, data.item.id);
      } catch (uploadError) {
        if (!objectId) await deleteCreatedObjectAfterAssetFailure(data.item.id);
        throw uploadError;
      }
      if (!objectId) clearObjectDraft();
      await loadObjects();
      activeObject = data.item;
      location.hash = objectRoute(data.item.id);
    } catch (reason) {
      if (errorNode) errorNode.textContent = message(reason);
      if (submit) submit.disabled = false;
    }
  });
}

async function deleteCreatedObjectAfterAssetFailure(objectId: number | string) {
  try {
    await api(`/api/objects/${objectId}`, { method: "DELETE" });
  } catch {
    // Keep the original upload error visible; cleanup failure is secondary.
  }
}

function validateObjectCoverSelection(form: HTMLFormElement) {
  const files = Array.from(form.querySelector<HTMLInputElement>("[data-cover-input]")?.files || []);
  if (files.some((file) => !String(file.type || "").startsWith("image/"))) {
    return "Для обложки можно выбрать только изображения.";
  }
  if (files.some((file) => file.size > 500 * 1024 * 1024)) {
    return "Файл обложки превышает допустимый размер.";
  }
  return "";
}

function bindObjectFieldMasks() {
  document.querySelectorAll<HTMLInputElement>("[data-object-mask]").forEach((input) => {
    if (input.dataset.objectMaskBound === "true") return;
    input.dataset.objectMaskBound = "true";
    const apply = () => applyObjectFieldMask(input);
    input.addEventListener("input", apply);
    input.addEventListener("blur", apply);
    apply();
  });
}

function applyObjectFieldMask(input: HTMLInputElement) {
  if (input.dataset.objectMask === "phone") {
    input.value = formatRussianPhone(input.value);
    return;
  }
  if (input.dataset.objectMask === "integer") {
    const sign = input.dataset.objectAllowNegative === "true" ? "-" : "";
    input.value = input.value.replace(new RegExp(`[^\\d${sign}]`, "g"), "").replace(/(?!^)-/g, "");
    return;
  }
  if (input.dataset.objectMask === "decimal") {
    const sign = input.dataset.objectAllowNegative === "true" ? "-" : "";
    input.value = input.value.replace(",", ".").replace(new RegExp(`[^\\d.${sign}]`, "g"), "").replace(/(?!^)-/g, "").replace(/(\..*)\./g, "$1");
  }
}

function bindZeroValueReplacement() {
  document.querySelectorAll<HTMLInputElement>("#object-editor-form input[type='number'], #calc-form input[type='number'], #mortgage-compare-form input[type='number']").forEach((input) => {
    if (input.dataset.zeroReplaceBound === "true") return;
    input.dataset.zeroReplaceBound = "true";
    input.addEventListener("focus", () => selectZeroValue(input));
    input.addEventListener("mouseup", (event) => {
      if (!isZeroValue(input.value)) return;
      event.preventDefault();
      selectZeroValue(input);
    });
    input.addEventListener("beforeinput", (event) => {
      replaceZeroBeforeInput(input, event as InputEvent);
    });
  });
}

function selectZeroValue(input: HTMLInputElement) {
  if (!isZeroValue(input.value)) return;
  window.setTimeout(() => {
    try {
      input.select();
    } catch {
      // Numeric inputs do not expose text selection APIs in every browser.
    }
  }, 0);
}

function replaceZeroBeforeInput(input: HTMLInputElement, event: InputEvent) {
  if (!isZeroValue(input.value) || !event.data || event.inputType !== "insertText") return;
  const value = replacementInputValue(input, event.data);
  if (!value) return;
  event.preventDefault();
  input.value = value;
  applyObjectFieldMask(input);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

function replacementInputValue(input: HTMLInputElement, data: string) {
  if (data === "," || data === ".") return input.dataset.objectMask === "integer" ? "" : "0.";
  if (data === "-" && input.dataset.objectAllowNegative === "true") return "-";
  if (!/^\d$/.test(data)) return "";
  return data;
}

function isZeroValue(value: string) {
  return /^-?0(?:[.,]0+)?$/.test(value.trim());
}

function formatRussianPhone(value: string) {
  const digits = value.replace(/\D/g, "");
  const national = digits.startsWith("8") ? digits.slice(1) : digits.startsWith("7") ? digits.slice(1) : digits;
  const limited = national.slice(0, 10);
  if (!limited) return "";
  const parts = ["+7"];
  if (limited.length <= 3) return `${parts[0]} ${limited}`;
  parts.push(limited.slice(0, 3));
  if (limited.length <= 6) return `${parts.join(" ")} ${limited.slice(3)}`.trim();
  parts.push(`${limited.slice(3, 6)}-${limited.slice(6, 8)}`);
  if (limited.length > 8) parts[parts.length - 1] += `-${limited.slice(8, 10)}`;
  return parts.join(" ");
}

function bindObjectDossierEditor() {
  document.querySelectorAll<HTMLElement>("[data-dossier-key]").forEach((repeater) => {
    if (repeater.dataset.dossierBound === "true") return;
    repeater.dataset.dossierBound = "true";
    const rows = repeater.querySelector<HTMLElement>("[data-dossier-rows]");
    const fields = parseDossierFields(repeater.dataset.dossierFields || "[]");
    const sync = () => syncDossierInput(repeater);
    repeater.querySelector<HTMLButtonElement>("[data-dossier-add]")?.addEventListener("click", () => {
      rows?.insertAdjacentHTML("beforeend", dossierRepeaterRow(fields));
      bindDossierRowEvents(repeater);
      bindObjectFieldMasks();
      bindZeroValueReplacement();
      sync();
      createIcons({ icons: iconSet });
    });
    bindDossierRowEvents(repeater);
    sync();
  });
}

function bindDossierRowEvents(repeater: HTMLElement) {
  repeater.querySelectorAll<HTMLElement>("[data-dossier-row]").forEach((row) => {
    if (row.dataset.dossierRowBound === "true") return;
    row.dataset.dossierRowBound = "true";
    row.querySelector<HTMLButtonElement>("[data-dossier-remove]")?.addEventListener("click", () => {
      row.remove();
      syncDossierInput(repeater);
    });
    row.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-dossier-field]").forEach((field) => {
      field.addEventListener("input", () => syncDossierInput(repeater));
      field.addEventListener("change", () => syncDossierInput(repeater));
    });
  });
}

function parseDossierFields(value: string): DossierFieldConfig[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function numberStepForField(name: string) {
  if (integerDossierFields.has(name)) return "1";
  if (name === "price" || name === "amount") return "1000";
  return "0.01";
}

function syncAllDossierInputs() {
  document.querySelectorAll<HTMLElement>("[data-dossier-key]").forEach(syncDossierInput);
}

function syncDossierInput(repeater: HTMLElement) {
  const key = repeater.dataset.dossierKey || "";
  const input = repeater.querySelector<HTMLInputElement>(`input[data-dossier-json="${key}"]`);
  const rows = [...repeater.querySelectorAll<HTMLElement>("[data-dossier-row]")].map((row) => {
    const item: Record<string, string | number | boolean> = {};
    row.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-dossier-field]").forEach((field) => {
      const fieldKey = field.dataset.dossierField || "";
      if (!fieldKey) return;
      if (field instanceof HTMLInputElement && field.type === "checkbox") item[fieldKey] = field.checked ? 1 : 0;
      else if (field instanceof HTMLInputElement && field.type === "number") item[fieldKey] = field.value ? Number(field.value) : 0;
      else item[fieldKey] = field.value.trim();
    });
    return item;
  }).filter((item) => Object.values(item).some((value) => value !== "" && value !== 0 && value !== false));
  if (input) {
    input.value = JSON.stringify(rows);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

function bindObjectDraft(form: HTMLFormElement) {
  const status = document.querySelector<HTMLElement>("[data-draft-status]");
  let draftTimer = 0;
  const scheduleSave = () => {
    window.clearTimeout(draftTimer);
    draftTimer = window.setTimeout(() => {
      saveObjectDraft(form);
      if (status) status.textContent = "Черновик сохранён";
    }, 350);
  };
  form.addEventListener("input", scheduleSave);
  form.addEventListener("change", scheduleSave);
  document.querySelectorAll<HTMLButtonElement>("[data-save-object-draft]").forEach((button) => button.addEventListener("click", () => {
    saveObjectDraft(form);
    if (status) status.textContent = "Черновик сохранён";
  }));
  document.querySelector<HTMLButtonElement>("[data-clear-object-draft]")?.addEventListener("click", () => {
    window.clearTimeout(draftTimer);
    clearObjectDraft();
    if (route === "/objects/new") {
      render();
    } else if (status) {
      status.textContent = "Черновик очищен";
    }
  });
}

function readObjectDraft(): Record<string, any> {
  try {
    return JSON.parse(localStorage.getItem(objectDraftKey) || "{}");
  } catch {
    return {};
  }
}

function saveObjectDraft(form: HTMLFormElement) {
  syncAllDossierInputs();
  const formData = new FormData(form);
  const draft: Record<string, any> = Object.fromEntries(formData.entries());
  draft.rooms = String(formData.get("rooms") || "").trim();
  ["cover", "objectFiles", "coverCropX", "coverCropY", "coverActiveIndex", "coverExistingActive", "coverExistingFileId", "coverRemoveExisting", "coverRemoveIds"].forEach((key) => delete draft[key]);
  localStorage.setItem(objectDraftKey, JSON.stringify(draft));
}

function clearObjectDraft() {
  localStorage.removeItem(objectDraftKey);
}

function bindRoomSelectors() {
  document.querySelectorAll<HTMLElement>(".rooms-field").forEach((field) => {
    if (field.dataset.roomsBound === "true") return;
    field.dataset.roomsBound = "true";
    const button = field.querySelector<HTMLButtonElement>("[data-rooms-toggle]");
    const popover = field.querySelector<HTMLElement>("[data-rooms-popover]");
    const valueInput = field.querySelector<HTMLInputElement>("[data-rooms-value]");
    const label = field.querySelector<HTMLElement>("[data-rooms-label]");
    const options = [...field.querySelectorAll<HTMLInputElement>("[data-room-option]")];
    const setOpen = (open: boolean) => {
      if (!popover || !button) return;
      popover.hidden = !open;
      button.setAttribute("aria-expanded", open ? "true" : "false");
      field.classList.toggle("is-open", open);
    };
    const updateValue = () => {
      const selected = options.filter((option) => option.checked).map((option) => option.value);
      if (valueInput) valueInput.value = selected.join(",");
      if (label) label.textContent = selected.length ? selected.map(formatRoomLabel).join(", ") : "Выберите комнатность";
      valueInput?.dispatchEvent(new Event("input", { bubbles: true }));
      valueInput?.dispatchEvent(new Event("change", { bubbles: true }));
    };
    button?.addEventListener("click", (event) => {
      event.preventDefault();
      setOpen(popover?.hidden ?? true);
    });
    options.forEach((option) => option.addEventListener("change", updateValue));
    document.addEventListener("pointerdown", (event) => {
      if (field.contains(event.target as Node | null)) return;
      setOpen(false);
    });
    field.addEventListener("keydown", (event) => {
      if (event.key === "Escape") setOpen(false);
    });
  });
}

async function uploadObjectCoverPhotos(form: HTMLFormElement, objectId: number | string) {
  const input = form.querySelector<HTMLInputElement>("[data-cover-input]");
  const existingActive = form.querySelector<HTMLInputElement>("[data-cover-existing-active]")?.value === "1";
  const existingFileId = form.querySelector<HTMLInputElement>("[data-cover-existing-file-id]")?.value || "0";
  const removeIds = form.querySelector<HTMLInputElement>("[data-cover-remove-ids]")?.value || "";
  const selectedFiles = Array.from(input?.files || []);
  const removeExisting = form.querySelector<HTMLInputElement>("[data-cover-remove-existing]")?.value === "1" && !selectedFiles.length;
  if (!input) return false;
  if (!selectedFiles.length && !existingActive && !removeExisting && !removeIds) return false;
  const files = removeExisting ? [] : selectedFiles;
  const activeIndex = Number(form.querySelector<HTMLInputElement>("[data-cover-active-index]")?.value || 0);
  const ordered = files
    .map((file, index) => ({ file, index }))
    .sort((a, b) => (a.index === activeIndex ? -1 : b.index === activeIndex ? 1 : a.index - b.index))
    .map((item) => item.file);
  const body = new FormData();
  ordered.forEach((file) => body.append("cover", file));
  body.set("cropX", form.querySelector<HTMLInputElement>("[data-cover-crop-x]")?.value || "50");
  body.set("cropY", form.querySelector<HTMLInputElement>("[data-cover-crop-y]")?.value || "50");
  body.set("existingActive", existingActive ? "1" : "0");
  body.set("existingFileId", existingFileId);
  body.set("removeExisting", removeExisting ? "1" : "0");
  body.set("removeIds", removeIds);
  await uploadFormData(`/api/objects/${objectId}/cover`, body, () => {});
  return true;
}

function bindObjectCoverPickers() {
  document.querySelectorAll<HTMLElement>("[data-cover-picker]").forEach((picker) => {
    if (picker.dataset.coverBound === "true") return;
    picker.dataset.coverBound = "true";
    const input = picker.querySelector<HTMLInputElement>("[data-cover-input]");
    const image = picker.querySelector<HTMLImageElement>("[data-cover-crop-image]");
    const preview = picker.querySelector<HTMLElement>("[data-cover-preview]");
    const placeholder = picker.querySelector<HTMLElement>("[data-cover-placeholder]");
    const thumbnails = picker.querySelector<HTMLElement>("[data-cover-thumbnails]");
    const note = picker.querySelector<HTMLElement>("[data-cover-panel-note]");
    const cropXInput = picker.querySelector<HTMLInputElement>("[data-cover-crop-x]");
    const cropYInput = picker.querySelector<HTMLInputElement>("[data-cover-crop-y]");
    const activeIndexInput = picker.querySelector<HTMLInputElement>("[data-cover-active-index]");
    const existingActiveInput = picker.querySelector<HTMLInputElement>("[data-cover-existing-active]");
    const existingFileIdInput = picker.querySelector<HTMLInputElement>("[data-cover-existing-file-id]");
    const removeExistingInput = picker.querySelector<HTMLInputElement>("[data-cover-remove-existing]");
    const removeIdsInput = picker.querySelector<HTMLInputElement>("[data-cover-remove-ids]");
    type CoverPhoto = { id: string; url: string; file?: File; existing?: boolean; existingId?: number; name?: string; cropX: number; cropY: number; isPrimary?: boolean };
    const existingCovers = parseExistingCoverImages(picker.dataset.existingCovers || "[]");
    const photos: CoverPhoto[] = existingCovers.map((cover) => ({
      id: `existing-${cover.id || cover.url}`,
      url: cover.url,
      existing: true,
      existingId: Number(cover.id || 0) || undefined,
      name: cover.name,
      cropX: cover.cropX,
      cropY: cover.cropY,
      isPrimary: cover.isPrimary
    }));
    const removedExistingIds = new Set<number>();
    let crop = { x: 50, y: 50 };
    let activeIndex = Math.max(0, photos.findIndex((photo) => photo.isPrimary));
    let drag: { x: number; y: number; offsetX: number; offsetY: number } | null = null;
    let didDrag = false;

    const syncInputFiles = () => {
      if (!input) return;
      const transfer = new DataTransfer();
      photos.filter((photo) => photo.file).forEach((photo) => transfer.items.add(photo.file!));
      input.files = transfer.files;
    };

    const activePhoto = () => photos[activeIndex] || null;

    const coverFrame = () => {
      if (!preview || !image?.naturalWidth || !image?.naturalHeight) return null;
      const rect = preview.getBoundingClientRect();
      const scale = Math.max(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      return {
        width,
        height,
        maxX: Math.max(0, (width - rect.width) / 2),
        maxY: Math.max(0, (height - rect.height) / 2)
      };
    };

    const offsetFromCrop = (photo: CoverPhoto) => {
      const frame = coverFrame();
      if (!frame) return { x: 0, y: 0 };
      return {
        x: frame.maxX ? ((50 - photo.cropX) / 50) * frame.maxX : 0,
        y: frame.maxY ? ((50 - photo.cropY) / 50) * frame.maxY : 0
      };
    };

    const cropFromOffset = (x: number, y: number) => {
      const frame = coverFrame();
      if (!frame) return { x: 50, y: 50 };
      const offsetX = clampNumber(x, -frame.maxX, frame.maxX);
      const offsetY = clampNumber(y, -frame.maxY, frame.maxY);
      return {
        x: frame.maxX ? 50 - (offsetX / frame.maxX) * 50 : 50,
        y: frame.maxY ? 50 - (offsetY / frame.maxY) * 50 : 50
      };
    };

    const applyImageFrame = () => {
      const photo = activePhoto();
      const frame = coverFrame();
      if (!image || !photo || !frame) return;
      const offset = offsetFromCrop(photo);
      image.style.width = `${frame.width}px`;
      image.style.height = `${frame.height}px`;
      image.style.objectPosition = "50% 50%";
      image.style.transform = `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`;
    };

    const setCrop = (x: number, y: number) => {
      crop = { x: clampNumber(x, 0, 100), y: clampNumber(y, 0, 100) };
      const photo = activePhoto();
      if (photo) {
        photo.cropX = crop.x;
        photo.cropY = crop.y;
      }
      applyImageFrame();
      if (cropXInput) cropXInput.value = String(Math.round(crop.x));
      if (cropYInput) cropYInput.value = String(Math.round(crop.y));
    };

    const selectImage = (index: number) => {
      if (!photos[index]) return;
      activeIndex = index;
      const photo = photos[index];
      const fileIndex = photo.file ? photos.filter((item) => item.file).indexOf(photo) : -1;
      if (activeIndexInput) activeIndexInput.value = String(Math.max(0, fileIndex));
      if (existingActiveInput) existingActiveInput.value = photo.existing ? "1" : "0";
      if (existingFileIdInput) existingFileIdInput.value = photo.existingId ? String(photo.existingId) : "0";
      if (removeExistingInput && photo.existing) removeExistingInput.value = "0";
      if (image) {
        image.onload = () => setCrop(photo.cropX, photo.cropY);
        image.src = photo.url;
        image.hidden = false;
      }
      preview?.classList.add("has-cover-image");
      if (placeholder) placeholder.hidden = true;
      setCrop(photo.cropX, photo.cropY);
      thumbnails?.querySelectorAll<HTMLElement>("[data-cover-thumb]").forEach((button) => {
        const isActive = Number(button.dataset.coverThumb || 0) === index;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    };

    const renderThumbnails = () => {
      if (thumbnails) {
        const addTile = `<button class="cover-thumb-add" type="button" data-cover-empty-add><i data-lucide="plus"></i><span>Добавить фото</span></button>`;
        thumbnails.innerHTML = photos.length
          ? photos.map((photo, index) => `
            <div class="cover-thumb-item ${index === activeIndex ? "is-active" : ""}">
              <button type="button" data-cover-thumb="${index}" aria-label="${photo.existing ? "Кадрировать текущую обложку" : `Кадрировать фото ${index + 1}`}" aria-pressed="${index === activeIndex ? "true" : "false"}">
                <img src="${h(photo.url)}" alt="" />
                <span>${photo.existing ? (photo.isPrimary ? "Обложка" : "Сохранено") : `Новое ${index + 1}`}</span>
              </button>
              <button class="cover-thumb-delete" type="button" data-cover-remove="${index}" aria-label="${photo.existing ? "Удалить текущую обложку" : `Удалить фото ${index + 1}`}"${tooltipAttr(photo.existing ? "Удалить сохраненную обложку" : `Удалить фото ${index + 1}`)}><i data-lucide="trash-2"></i></button>
            </div>
          `).join("") + addTile
          : addTile;
      }
      thumbnails?.querySelectorAll<HTMLButtonElement>("[data-cover-thumb]").forEach((button) => {
        button.addEventListener("click", () => selectImage(Number(button.dataset.coverThumb || 0)));
      });
      thumbnails?.querySelectorAll<HTMLButtonElement>("[data-cover-remove]").forEach((button) => {
        button.addEventListener("click", () => {
          const index = Number(button.dataset.coverRemove || 0);
          const [removed] = photos.splice(index, 1);
          if (removed?.file) URL.revokeObjectURL(removed.url);
          if (removed?.existingId) removedExistingIds.add(removed.existingId);
          if (removeIdsInput) removeIdsInput.value = [...removedExistingIds].join(",");
          if (removeExistingInput) removeExistingInput.value = photos.length ? "0" : "1";
          activeIndex = Math.max(0, Math.min(activeIndex, photos.length - 1));
          syncInputFiles();
          renderThumbnails();
          if (photos.length) {
            selectImage(activeIndex);
          } else {
            if (image) {
              image.removeAttribute("src");
              image.hidden = true;
            }
            preview?.classList.remove("has-cover-image");
            if (placeholder) {
              placeholder.hidden = false;
              placeholder.textContent = "Нажмите, чтобы выбрать фото для обложки";
            }
            if (activeIndexInput) activeIndexInput.value = "0";
            if (existingActiveInput) existingActiveInput.value = "0";
            if (existingFileIdInput) existingFileIdInput.value = "0";
            if (note) note.textContent = "Нажмите на плитку добавления, чтобы загрузить фотографию.";
          }
          createIcons({ icons: iconSet });
        });
      });
      thumbnails?.querySelector<HTMLButtonElement>("[data-cover-empty-add]")?.addEventListener("click", () => input?.click());
      if (note) note.textContent = photos.length
        ? "Выберите миниатюру, удалите лишнее или добавьте ещё фотографии."
        : "Нажмите на плитку добавления, чтобы загрузить фотографию.";
      createIcons({ icons: iconSet });
    };

    const appendFiles = (files: File[]) => {
      files.forEach((file) => photos.push({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
        url: URL.createObjectURL(file),
        file,
        cropX: 50,
        cropY: 50
      }));
      syncInputFiles();
      activeIndex = Math.max(0, photos.length - files.length);
      renderThumbnails();
      if (photos.length) selectImage(activeIndex);
    };

    input?.addEventListener("change", () => {
      const files = Array.from(input.files || []).filter((file) => String(file.type || "").startsWith("image/"));
      if (!files.length) {
        syncInputFiles();
        return;
      }
      appendFiles(files);
    });

    const startDrag = (clientX: number, clientY: number) => {
      const photo = activePhoto();
      if (!photo) return;
      const offset = offsetFromCrop(photo);
      drag = { x: clientX, y: clientY, offsetX: offset.x, offsetY: offset.y };
      didDrag = false;
      preview?.classList.add("is-dragging");
    };
    const moveDrag = (clientX: number, clientY: number) => {
      if (!drag || !preview) return;
      if (Math.abs(clientX - drag.x) > 3 || Math.abs(clientY - drag.y) > 3) didDrag = true;
      const next = cropFromOffset(drag.offsetX + clientX - drag.x, drag.offsetY + clientY - drag.y);
      setCrop(next.x, next.y);
    };
    const endDrag = () => {
      if (!drag) return;
      drag = null;
      preview?.classList.remove("is-dragging");
    };

    preview?.addEventListener("pointerdown", (event) => {
      if (!image || image.hidden) return;
      event.preventDefault();
      preview.setPointerCapture(event.pointerId);
      startDrag(event.clientX, event.clientY);
    });
    preview?.addEventListener("pointermove", (event) => {
      moveDrag(event.clientX, event.clientY);
    });
    const stopDrag = (event: PointerEvent) => {
      endDrag();
      if (preview?.hasPointerCapture(event.pointerId)) preview.releasePointerCapture(event.pointerId);
    };
    preview?.addEventListener("pointerup", stopDrag);
    preview?.addEventListener("pointercancel", stopDrag);
    preview?.addEventListener("mousedown", (event) => {
      if (!image || image.hidden) return;
      event.preventDefault();
      startDrag(event.clientX, event.clientY);
      const onMove = (moveEvent: MouseEvent) => moveDrag(moveEvent.clientX, moveEvent.clientY);
      const onUp = () => {
        endDrag();
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    });
    preview?.addEventListener("click", () => {
      if (didDrag) {
        window.setTimeout(() => {
          didDrag = false;
        }, 0);
        return;
      }
      if (!photos.length) input?.click();
    });
    renderThumbnails();
    if (photos.length) selectImage(activeIndex);
  });
}

function bindObjectAssetForms() {
  const docsForm = document.querySelector<HTMLElement>("#object-docs-form");
  const objectUpload = docsForm ? createSelectedUploadFilesController(docsForm) : null;
  docsForm?.querySelector<HTMLButtonElement>("[data-object-docs-submit]")?.addEventListener("click", async () => {
    const input = docsForm.querySelector<HTMLInputElement>("input[name='objectFiles'], input[name='files']");
    const errorNode = document.querySelector<HTMLElement>("#object-docs-error");
    const submit = docsForm.querySelector<HTMLButtonElement>("[data-object-docs-submit]");
    const progress = docsForm.querySelector<HTMLElement>("[data-upload-progress]");
    const objectId = docsForm.dataset.objectId;
    const files = objectUpload?.files() || [];
    if (!files.length || !objectId) return;
    const body = new FormData();
    body.set("complexId", objectId);
    files.forEach((file) => body.append("files", file));
    try {
      if (errorNode) errorNode.textContent = "";
      setUploadBusy(submit, progress, 0, "Загружаем");
      await uploadFormData("/api/files/upload", body, (percent) => setUploadBusy(submit, progress, percent, "Загружаем"));
      input?.value && (input.value = "");
      objectUpload?.clear();
      await refreshUnreadFilesCount();
    } catch (reason) {
      if (errorNode) errorNode.textContent = reason instanceof Error ? reason.message : message(reason);
    } finally {
      setUploadIdle(submit, progress, "Загрузить в папку объекта");
    }
  });
}

function createSelectedUploadFilesController(root: HTMLElement) {
  const input = root.querySelector<HTMLInputElement>("input[type='file'][multiple]");
  const list = root.querySelector<HTMLElement>("[data-upload-list], #files-upload-list");
  const trigger = root.querySelector<HTMLButtonElement>("[data-upload-trigger], #files-upload-trigger");
  let selected: Array<{ id: string; file: File }> = [];

  const emitChange = () => root.dispatchEvent(new CustomEvent("upload-files-change", { bubbles: true }));
  const render = () => {
    if (!list) return;
    if (!selected.length) {
      list.textContent = "Файлы не выбраны";
      emitChange();
      return;
    }
    list.innerHTML = selected.map(({ id, file }) => `
      <span class="upload-file-chip">
        <span>${h(file.name)} · ${h(formatClientFileSize(file.size))}</span>
        <button type="button" data-remove-upload-file="${h(id)}" aria-label="Убрать файл ${h(file.name)}"><i data-lucide="x"></i></button>
      </span>
    `).join("");
    list.querySelectorAll<HTMLButtonElement>("[data-remove-upload-file]").forEach((button) => {
      button.addEventListener("click", () => {
        selected = selected.filter((item) => item.id !== button.dataset.removeUploadFile);
        syncNativeInput();
        render();
      });
    });
    createIcons({ icons: iconSet });
    emitChange();
  };
  const appendFiles = (files: File[]) => {
    files.forEach((file) => {
      const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(16).slice(2)}`;
      selected.push({ id, file });
    });
    syncNativeInput();
    render();
  };
  const syncNativeInput = () => {
    if (!input) return;
    try {
      const transfer = new DataTransfer();
      selected.forEach(({ file }) => transfer.items.add(file));
      input.files = transfer.files;
    } catch {
      input.value = "";
    }
  };

  trigger?.addEventListener("click", () => input?.click());
  input?.addEventListener("change", () => {
    appendFiles(Array.from(input.files || []));
    if (input) input.value = "";
  });
  render();

  return {
    files: () => selected.map((item) => item.file),
    clear: () => {
      selected = [];
      syncNativeInput();
      render();
    }
  };
}

function uploadFormData<T = unknown>(url: string, body: FormData, onProgress: (percent: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", url);
    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return;
      onProgress(Math.max(1, Math.min(99, Math.round((event.loaded / event.total) * 100))));
    });
    request.addEventListener("load", () => {
      const payload = parseUploadResponse(request.responseText);
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(requestMessage(payload, request.status)));
        return;
      }
      onProgress(100);
      resolve(payload as T);
    });
    request.addEventListener("error", () => reject(new Error("Не удалось загрузить файлы.")));
    request.addEventListener("abort", () => reject(new Error("Загрузка отменена.")));
    request.send(body);
  });
}

function parseUploadResponse(value: string) {
  try {
    return JSON.parse(value || "{}");
  } catch {
    return { message: value };
  }
}

function setUploadBusy(button: HTMLButtonElement | null | undefined, progress: HTMLElement | null | undefined, percent: number, label: string) {
  if (button) {
    button.dataset.uploadIdleHtml ||= button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<i data-lucide="refresh-cw"></i> ${label}`;
  }
  if (progress) {
    progress.hidden = false;
    progress.querySelector<HTMLElement>("span")?.style.setProperty("--upload-progress", `${percent}%`);
    const text = progress.querySelector<HTMLElement>("strong");
    if (text) text.textContent = `${percent}%`;
  }
  createIcons({ icons: iconSet });
}

function setUploadIdle(button: HTMLButtonElement | null | undefined, progress: HTMLElement | null | undefined, fallbackLabel: string) {
  if (button) {
    button.disabled = false;
    button.innerHTML = button.dataset.uploadIdleHtml || `<i data-lucide="upload"></i> ${fallbackLabel}`;
  }
  if (progress) {
    progress.hidden = true;
    progress.querySelector<HTMLElement>("span")?.style.setProperty("--upload-progress", "0%");
    const text = progress.querySelector<HTMLElement>("strong");
    if (text) text.textContent = "0%";
  }
  createIcons({ icons: iconSet });
}

function bindMapHighlights() {
  document.querySelectorAll<HTMLElement>(".map-layout [data-open-object]").forEach((node) => {
    if (node.dataset.mapHighlightBound === "true") return;
    node.dataset.mapHighlightBound = "true";
    const objectId = node.dataset.openObject || "";
    node.addEventListener("pointerenter", () => setMapObjectHighlight(objectId, true));
    node.addEventListener("pointerleave", () => setMapObjectHighlight(objectId, false));
    node.addEventListener("focus", () => setMapObjectHighlight(objectId, true));
    node.addEventListener("blur", () => setMapObjectHighlight(objectId, false));
  });
}

function setMapObjectHighlight(objectId: string, active: boolean) {
  document.querySelectorAll<HTMLElement>(".map-layout [data-open-object]").forEach((node) => {
    if (node.dataset.openObject === objectId) node.classList.toggle("is-highlighted", active);
  });
}

function bindObjectFilters() {
  const query = document.querySelector<HTMLInputElement>("#object-query");
  const district = document.querySelector<HTMLSelectElement>("#district-filter");
  const status = document.querySelector<HTMLSelectElement>("#status-filter");
  const grid = document.querySelector("#object-grid");
  if (!query) return;
  const apply = () => {
    const filters = { q: query.value, district: district?.value || "", status: status?.value || "" };
    setObjectFilterUrl(filters);
    const items = filteredObjects(filters);
    if (!grid) {
      render();
      return;
    }
    grid.innerHTML = objectGrid(items);
    bindObjectActions();
    createIcons({ icons: iconSet });
    document.querySelector("#reset-filters-inline")?.addEventListener("click", () => {
      query.value = "";
      if (district) district.value = "";
      if (status) status.value = "";
      apply();
    });
  };
  [query, district, status].forEach((el) => {
    el?.addEventListener("input", apply);
    el?.addEventListener("change", apply);
  });
  document.querySelector("#reset-filters")?.addEventListener("click", () => {
    query.value = "";
    if (district) district.value = "";
    if (status) status.value = "";
    apply();
  });
}

async function bindProviderMap() {
  const host = document.querySelector<HTMLElement>("[data-provider-map]");
  if (!host) return;
  const fallbackStatus = host.querySelector<HTMLElement>("[data-map-status]");

  try {
    const config = await loadAppConfig();
    if (!host.isConnected) return;

    if (config.mapProvider === "geoapify") {
      await renderGeoapifyMap(host, config, fallbackStatus);
      return;
    }

    if (config.mapProvider === "osm") {
      await renderOsmMap(host, fallbackStatus);
      return;
    }

    if (config.mapProvider === "yandex-tiles") {
      await renderYandexTilesMap(host, config, fallbackStatus);
      return;
    }

    await renderYandexMap(host, config, fallbackStatus);
  } catch {
    try {
      await renderOsmMap(host, fallbackStatus);
    } catch {
      renderMapGrid(host, fallbackStatus, "Не удалось загрузить карту. Попробуйте обновить страницу.");
    }
  }
}

async function renderGeoapifyMap(host: HTMLElement, config: AppConfig, fallbackStatus: HTMLElement | null) {
  if (!config.geoapifyApiKey) {
    await renderOsmMap(host, fallbackStatus);
    return;
  }

  host.classList.add("is-loading-map");
  if (fallbackStatus) fallbackStatus.textContent = "Загружаем карту…";
  const L = await loadLeaflet();
  if (!host.isConnected) return;

  const container = host.querySelector<HTMLElement>("#provider-map");
  if (!container) return;
  container.innerHTML = "";

  const items = filteredObjects(objectFiltersFromUrl());
  const mapLocation = leafletMapLocation(items);
  const map = L.map(container, { zoomControl: true, attributionControl: true }).setView(mapLocation.center, mapLocation.zoom);
  removeLeafletAttributionPrefix(map);
  const tileLayer = L.tileLayer(geoapifyTileUrl(config), {
    maxZoom: 20,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://www.geoapify.com/">Geoapify</a>'
  }).addTo(map);

  addLeafletMarkers(L, map, items);
  setTimeout(() => map.invalidateSize(), 0);
  markLeafletMapReady(host, tileLayer, fallbackStatus);
}

async function renderOsmMap(host: HTMLElement, fallbackStatus: HTMLElement | null) {
  host.classList.add("is-loading-map");
  if (fallbackStatus) fallbackStatus.textContent = "Загружаем карту…";
  const L = await loadLeaflet();
  if (!host.isConnected) return;

  const container = host.querySelector<HTMLElement>("#provider-map");
  if (!container) return;
  container.innerHTML = "";

  const items = filteredObjects(objectFiltersFromUrl());
  const mapLocation = leafletMapLocation(items);
  const map = L.map(container, { zoomControl: true, attributionControl: true }).setView(mapLocation.center, mapLocation.zoom);
  removeLeafletAttributionPrefix(map);
  const tileLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  }).addTo(map);

  addLeafletMarkers(L, map, items);
  setTimeout(() => map.invalidateSize(), 0);
  markLeafletMapReady(host, tileLayer, fallbackStatus);
}

function renderMapGrid(host: HTMLElement, fallbackStatus: HTMLElement | null, statusText: string) {
  host.classList.remove("is-loading-map", "is-provider-ready");
  if (fallbackStatus) fallbackStatus.textContent = statusText;
}

async function renderYandexTilesMap(host: HTMLElement, config: AppConfig, fallbackStatus: HTMLElement | null) {
  if (!config.yandexTilesEnabled) {
    await renderOsmMap(host, fallbackStatus);
    return;
  }

  host.classList.add("is-loading-map");
  if (fallbackStatus) fallbackStatus.textContent = "Загружаем карту…";
  const L = await loadLeaflet();
  if (!host.isConnected) return;

  const container = host.querySelector<HTMLElement>("#provider-map");
  if (!container) return;
  container.innerHTML = "";

  const items = filteredObjects(objectFiltersFromUrl());
  const mapLocation = leafletMapLocation(items);
  const map = L.map(container, { zoomControl: true, attributionControl: true }).setView(mapLocation.center, mapLocation.zoom);
  removeLeafletAttributionPrefix(map);
  const tileLayer = L.tileLayer(yandexTilesUrl(config), {
    maxZoom: 20,
    tileSize: 256,
    attribution: '<a href="https://yandex.ru/maps" target="_blank" rel="noopener noreferrer">Яндекс Карты</a>'
  }).addTo(map);

  addLeafletMarkers(L, map, items);
  setTimeout(() => map.invalidateSize(), 0);
  markLeafletMapReady(host, tileLayer, fallbackStatus, 10000);
}

async function renderYandexMap(host: HTMLElement, config: AppConfig, fallbackStatus: HTMLElement | null) {
    if (!config.yandexMapsApiKey) {
      await renderOsmMap(host, fallbackStatus);
      return;
    }

    host.classList.add("is-loading-map");
    if (fallbackStatus) fallbackStatus.textContent = "Загружаем карту…";
    const ymaps3 = await loadYandexMaps(config);
    if (!host.isConnected) return;

    const container = host.querySelector<HTMLElement>("#provider-map");
    if (!container) return;
    container.innerHTML = "";

    const map = new ymaps3.YMap(container, {
      location: yandexMapLocation(filteredObjects(objectFiltersFromUrl()))
    });
    map.addChild(new ymaps3.YMapDefaultSchemeLayer());
    map.addChild(new ymaps3.YMapDefaultFeaturesLayer());

    filteredObjects(objectFiltersFromUrl()).filter(hasObjectCoordinates).forEach((item) => {
      const element = document.createElement("button");
      element.type = "button";
      element.className = `provider-map-marker ${statusBadgeClass(item.status)}`;
      element.dataset.openObject = String(item.id);
      element.innerHTML = `<strong>${h(formatObjectPrice(item.min_price))}</strong><span>${h(item.name)}</span>`;
      element.addEventListener("click", () => {
        location.hash = objectRoute(item.id);
      });
      map.addChild(new ymaps3.YMapMarker({ coordinates: [Number(item.lng), Number(item.lat)] }, element));
    });

    host.classList.remove("is-loading-map");
    host.classList.add("is-provider-ready");
    bindMapHighlights();
}

async function loadAppConfig() {
  if (!appConfig) appConfig = await api<AppConfig>("/api/config");
  return appConfig;
}

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const existingCss = document.querySelector("link[data-leaflet-css]");
    if (!existingCss) {
      const link = document.createElement("link");
      link.dataset.leafletCss = "true";
      link.rel = "stylesheet";
      link.href = "/vendor/leaflet/leaflet.css";
      document.head.append(link);
    }

    const script = document.createElement("script");
    script.src = "/vendor/leaflet/leaflet.js";
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error("Leaflet script failed to load"));
    document.head.append(script);
  });
  return leafletPromise;
}

function geoapifyTileUrl(config: AppConfig) {
  const style = encodeURIComponent(config.geoapifyMapStyle || "osm-bright");
  const apiKey = encodeURIComponent(config.geoapifyApiKey);
  return `https://maps.geoapify.com/v1/tile/${style}/{z}/{x}/{y}.png?apiKey=${apiKey}&lang=ru`;
}

function yandexTilesUrl(config: AppConfig) {
  const params = new URLSearchParams({
    maptype: config.yandexTilesMapType || "map"
  });
  return `/api/map/yandex-tile?x={x}&y={y}&z={z}&${params.toString()}`;
}

function removeLeafletAttributionPrefix(map: any) {
  map.attributionControl?.setPrefix("");
}

function markLeafletMapReady(host: HTMLElement, tileLayer: any, fallbackStatus: HTMLElement | null, timeoutMs = 5000) {
  let done = false;
  const ready = () => {
    if (done || !host.isConnected) return;
    done = true;
    host.classList.remove("is-loading-map");
    host.classList.add("is-provider-ready");
    if (fallbackStatus) fallbackStatus.textContent = "Карта загружена.";
  };
  tileLayer.once("load", ready);
  window.setTimeout(ready, timeoutMs);
}

function addLeafletMarkers(L: any, map: any, items: ApiObject[]) {
  const markers = items.filter(hasObjectCoordinates).map((item) => {
    const badgeClass = statusBadgeClass(item.status);
    const icon = L.divIcon({
      className: "",
      html: `<button type="button" class="provider-map-marker ${badgeClass}" data-open-object="${item.id}"><span class="provider-map-label"><strong>${h(formatObjectPrice(item.min_price))}</strong><em>${h(item.name)}</em></span><span class="provider-map-pin" aria-hidden="true"></span></button>`,
      iconSize: [170, 82],
      iconAnchor: [85, 82]
    });
    const marker = L.marker([Number(item.lat), Number(item.lng)], { icon }).addTo(map);
    marker.on("click", () => {
      location.hash = objectRoute(item.id);
    });
    return marker;
  });
  bindMapHighlights();

  if (markers.length > 1) {
    const group = L.featureGroup(markers);
    map.fitBounds(group.getBounds().pad(0.22), { maxZoom: 13 });
  }
}

function loadYandexMaps(config: AppConfig) {
  if (window.ymaps3) return window.ymaps3.ready.then(() => window.ymaps3);
  if (yandexMapsPromise) return yandexMapsPromise;
  yandexMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const params = new URLSearchParams({ apikey: config.yandexMapsApiKey, lang: config.yandexMapsLang || "ru_RU" });
    script.src = `https://api-maps.yandex.ru/v3/?${params.toString()}`;
    script.async = true;
    script.onload = async () => {
      try {
        await window.ymaps3?.ready;
        resolve(window.ymaps3);
      } catch (error) {
        reject(error);
      }
    };
    script.onerror = () => reject(new Error("Yandex Maps script failed to load"));
    document.head.append(script);
  });
  return yandexMapsPromise;
}

function leafletMapLocation(items: ApiObject[]) {
  items = items.filter(hasObjectCoordinates);
  if (!items.length) return { center: [47.105, 37.555], zoom: 11 };
  const lngs = items.map((item) => Number(item.lng)).filter(Number.isFinite);
  const lats = items.map((item) => Number(item.lat)).filter(Number.isFinite);
  if (!lngs.length || !lats.length) return { center: [47.105, 37.555], zoom: 11 };
  const center = [
    (Math.min(...lats) + Math.max(...lats)) / 2,
    (Math.min(...lngs) + Math.max(...lngs)) / 2
  ];
  const spread = Math.max(Math.max(...lngs) - Math.min(...lngs), Math.max(...lats) - Math.min(...lats));
  return { center, zoom: spread > 0.045 ? 11 : 12 };
}

function yandexMapLocation(items: ApiObject[]) {
  items = items.filter(hasObjectCoordinates);
  if (!items.length) return { center: [37.555, 47.105], zoom: 11 };
  const lngs = items.map((item) => Number(item.lng)).filter(Number.isFinite);
  const lats = items.map((item) => Number(item.lat)).filter(Number.isFinite);
  if (!lngs.length || !lats.length) return { center: [37.555, 47.105], zoom: 11 };
  const center = [
    (Math.min(...lngs) + Math.max(...lngs)) / 2,
    (Math.min(...lats) + Math.max(...lats)) / 2
  ];
  const spread = Math.max(Math.max(...lngs) - Math.min(...lngs), Math.max(...lats) - Math.min(...lats));
  return { center, zoom: spread > 0.045 ? 11 : 12 };
}

function bindObjectTabs() {
  document.querySelectorAll<HTMLButtonElement>("[data-object-tab]").forEach((button) => {
    button.addEventListener("click", () => {
      setRouteParam("tab", button.dataset.objectTab || "buildings");
    });
  });
}

function bindCalculator() {
  const form = document.querySelector<HTMLFormElement>("#calc-form");
  if (!form) return;
  let debounceTimer = 0;
  let activeController: AbortController | null = null;
  let requestSeq = 0;
  let invalidFields: HTMLInputElement[] = [];
  const host = document.querySelector<HTMLElement>("#calc-result")!;
  const statusBar = form.querySelector<HTMLElement>("#calc-state-bar")!;
  const validationMessage = form.querySelector<HTMLElement>("#calc-validation-message")!;
  const focusInvalid = () => { if (invalidFields[0]) focusControl(invalidFields[0]); };
  const showState = (state: "pending" | "incomplete" | "ready" | "error", text: string) => {
    statusBar.dataset.state = state;
    validationMessage.textContent = text;
    statusBar.querySelector<HTMLButtonElement>("[data-calc-fix]")!.hidden = !invalidFields.length || state === "ready";
    host.dataset.state = state;
    host.querySelectorAll<HTMLButtonElement>("#preview-calc, #download-calc, #share-calc").forEach(button => { button.disabled = state !== "ready"; });
    host.querySelector(".calc-result-status")?.remove();
    const report = host.querySelector(".calculation-report");
    if (report && state !== "ready") {
      host.insertAdjacentHTML("afterbegin", `<div class="calc-result-status" role="status"><strong>Предыдущий расчёт</strong><span>${h(text)}</span>${invalidFields.length ? '<button type="button" class="secondary" data-calc-fix>Исправить поле</button>' : ""}</div>`);
    }
    const exportStatus = host.querySelector<HTMLElement>("[data-export-status]");
    if (exportStatus) { exportStatus.hidden = state === "ready"; exportStatus.innerHTML = `${h(text)}${invalidFields.length ? ' <button type="button" class="secondary" data-calc-fix>Исправить поле</button>' : ""}`; }
  };
  const clearInputErrors = () => {
    form.querySelectorAll<HTMLInputElement>("[data-contract-error]").forEach(input => { input.setCustomValidity(""); delete input.dataset.contractError; });
    form.querySelectorAll(".calc-input-error").forEach(node => node.remove());
    form.querySelectorAll<HTMLInputElement>("[data-calc-invalid]").forEach(input => {
      input.removeAttribute("aria-invalid");
      const description = (input.getAttribute("aria-describedby") || "").split(" ").filter(id => id !== input.id + "-error").join(" ");
      if (description) input.setAttribute("aria-describedby", description); else input.removeAttribute("aria-describedby");
      delete input.dataset.calcInvalid;
    });
  };
  const inputError = (input: HTMLInputElement) => {
    const label = input.closest("label")?.querySelector("span")?.textContent?.trim() || "Планировка квартиры";
    const phase = input.closest<HTMLElement>("[data-expense-row]")?.dataset.expensePhase;
    if (input.matches("[data-expense-name]")) return `Укажите название расхода ${{Purchase:"при покупке", BeforeRent:"до сдачи", AfterRent:"после сдачи"}[phase || "Purchase"] || ""}`;
    if (input.dataset.contractError) return input.validationMessage;
    if (input.type === "date") return `Проверьте дату «${label}»: день, месяц и год`;
    if (input.validity.badInput) return `Введите число в поле «${label}»`;
    if (input.validity.rangeUnderflow) return `«${label}»: значение от ${input.min}`;
    if (input.validity.rangeOverflow) return `«${label}»: значение до ${input.max}`;
    if (input.validity.stepMismatch) return `«${label}»: введите целое число месяцев`;
    return input.validationMessage && input.id === "calc-floor-plan" ? input.validationMessage : `Заполните поле «${label}»`;
  };
  form.querySelector("[data-calc-go-result]")?.addEventListener("click", () => {
    host.scrollIntoView({block:"start",behavior:window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"});
    host.tabIndex = -1; host.focus({preventScroll:true});
  });
  const handleFix = (event: Event) => { if ((event.target as HTMLElement).closest("[data-calc-fix],#calc-fill-missing")) focusInvalid(); };
  form.addEventListener("click", handleFix);
  host.addEventListener("click", handleFix);
  host.addEventListener("click", event => {
    if ((event.target as HTMLElement).closest("[data-calc-edit]")) focusControl(form.querySelector<HTMLInputElement>("#calc-price")!);
  });
  const named = (name: string) => form.querySelector<HTMLInputElement>(`input[name="${name}"]:not([type="hidden"])`)!;
  const rentalTerm = named("plannedRentYears");
  let rentalEdited = false;
  let rentalMaximum: number | null = null;
  const rentalHint = document.createElement("span"); rentalHint.className = "calc-field-hint";
  const rentalLimit = document.createElement("button"); rentalLimit.type = "button"; rentalLimit.className = "calc-term-limit"; rentalLimit.textContent = "До продажи";
  rentalTerm.closest("label")!.append(rentalHint, rentalLimit);
  const updateDependentFields = () => {
    for (const name of ["furnitureCost", "applianceCost"]) {
      const input = named(name); input.disabled = named("repairIncludes").checked;
      let hint = input.closest("label")!.querySelector<HTMLElement>(".calc-field-hint");
      if (!hint) { hint = document.createElement("span"); hint.className = "calc-field-hint"; input.closest("label")!.append(hint); }
      hint.textContent = input.disabled ? "Учтено в ремонте" : "";
    }
    const outsideDdu = Array.from(form.querySelectorAll<HTMLElement>("[data-expense-row]")).reduce((sum,row) => sum + (row.querySelector<HTMLSelectElement>("select")?.value === "price-outside-ddu" ? Number(row.querySelector<HTMLInputElement>('input[type="number"]')?.value || 0) : 0),0);
    const mortgageBase = Math.round((Number(named("price").value) - Number(named("bookingCost").value) - outsideDdu) * 100) / 100;
    const cashPurchase = mortgageBase <= Number(named("downPayment").value);
    for (const name of ["rate", "loanYears", "manualPayment"]) {
      const input = named(name); input.disabled = cashPurchase;
      let hint = input.closest("label")!.querySelector<HTMLElement>(".calc-field-hint");
      if (!hint) { hint = document.createElement("span"); hint.className = "calc-field-hint"; input.closest("label")!.append(hint); }
      hint.textContent = cashPurchase ? "Покупка без кредита" : "";
    }
    for (const kind of ["Life", "Property"]) named(`insurance${kind}Years`).disabled = !Number(named(`insurance${kind}Annual`).value);
    const purchase = named("purchaseDate"); const keys = named("keysDate");
    rentalMaximum = null; rentalTerm.max = "50"; rentalHint.textContent = ""; rentalLimit.hidden = true;
    if (!purchase.validity.valid || !keys.validity.valid || !purchase.value || !keys.value || !named("repairMonths").validity.valid) { rentalTerm.max = "50"; return; }
    const sale = addMonths(new Date(purchase.value + "T12:00:00Z"), (Number(named("ownershipYears").value) > 0 ? Number(named("ownershipYears").value) : Number(named("investmentYears").value)) * 12);
    const start = addMonths(new Date(keys.value + "T12:00:00Z"), Number(named("repairMonths").value));
    if (!Number.isFinite(sale.getTime()) || !Number.isFinite(start.getTime()) || sale < start) return;
    const months = calendarMonthsBetween(start, sale);
    rentalMaximum = Math.min(50, months / 12); rentalTerm.max = String(rentalMaximum);
    rentalHint.textContent = `До продажи: ${months} мес.`; rentalLimit.hidden = false;
    if (!rentalEdited && Number(rentalTerm.value) > rentalMaximum) rentalTerm.value = String(Math.floor(rentalMaximum * 100 + 1e-8) / 100);
  };
  rentalLimit.addEventListener("click", () => { if (rentalMaximum !== null) { rentalTerm.value = String(Math.floor(rentalMaximum * 100 + 1e-8) / 100); rentalEdited = true; rentalTerm.dispatchEvent(new Event("input", { bubbles: true })); } });
  form.addEventListener("input", event => { if (event.target === rentalTerm) rentalEdited = true; updateDependentFields(); });
  const runCalc = async () => {
    window.clearTimeout(debounceTimer);
    activeController?.abort();
    if (!form.isConnected) return;
    const controller = new AbortController();
    activeController = controller;
    const requestId = ++requestSeq;
    clearInputErrors();
    updateDependentFields();
    const expenses = readExpenses(form);
    const body = { ...Object.fromEntries(new FormData(form).entries()), additionalExpenses: JSON.stringify(expenses) };
    clearCalculationWarnings();
    form.querySelectorAll<HTMLInputElement>('input[type="number"]').forEach(input => {
      const rule = numberFieldRule(input.name);
      const message = rule && !input.disabled ? numberFieldError(input.value, rule) : null;
      if (message) { input.setCustomValidity(message); input.dataset.contractError = "true"; }
    });
    if (rentalMaximum !== null && Number(rentalTerm.value) > rentalMaximum + 1e-9 && !rentalTerm.validity.badInput) {
      rentalTerm.setCustomValidity("Срок аренды не может быть больше периода до продажи"); rentalTerm.dataset.contractError = "true";
    }
    invalidFields = Array.from(form.querySelectorAll<HTMLInputElement>('input[name],#calc-floor-plan')).filter(input => input.willValidate && !input.validity.valid);
    if (invalidFields.length) {
      invalidFields.forEach(input => {
        const errorId = input.id + "-error";
        input.dataset.calcInvalid = "";
        input.setAttribute("aria-invalid", "true");
        input.setAttribute("aria-describedby", [input.getAttribute("aria-describedby"), errorId].filter(Boolean).join(" "));
        const error = document.createElement("span"); error.id = errorId; error.className = "calc-input-error"; error.textContent = inputError(input);
        (input.closest("label") || input.parentElement)?.append(error);
      });
      const prompt = inputError(invalidFields[0]);
      if (!host.querySelector(".calculation-report")) host.innerHTML = `<section class="calc-pending"><h2>Результат расчёта</h2><p>${h(prompt)}</p><button type="button" class="secondary" id="calc-fill-missing">Заполнить</button></section>`;
      showState("incomplete", prompt);
      host.removeAttribute("aria-busy");
      activeController = null;
      return;
    }
    showState("pending", "Обновляем расчёт…");
    host.setAttribute("aria-busy", "true");
    if (!host.querySelector(".calculation-report")) host.innerHTML = skeleton("Считаем…");
    try {
      const data = await api<{ input: CalculatorInput; result: ProfitabilityResult }>("/api/calculations/preview", { method: "POST", body: JSON.stringify(body), signal: controller.signal });
      if (requestId !== requestSeq || !form.isConnected) return;
      // Financial warnings remain in the result; the approved methodology needs no UI disclaimer.
      host.innerHTML = profitabilityResult(data.result, data.input);
      createIcons({ icons: iconSet });
      bindCalculationResultActions(body);
      showState("ready", "Расчёт обновлён");
    } catch (error) {
      if (controller.signal.aborted || requestId !== requestSeq) return;
      if (!host.querySelector(".calculation-report")) host.innerHTML = errorState("Расчёт не выполнен", message(error), "Повторить расчёт", "retry-calc");
      showState("error", message(error));
      host.querySelector(".calc-result-status")?.insertAdjacentHTML("beforeend", '<button type="button" class="secondary" id="retry-calc">Повторить расчёт</button>');
      createIcons({ icons: iconSet });
      document.querySelector("#retry-calc")?.addEventListener("click", () => void runCalc());
    } finally {
      if (activeController === controller) {
        activeController = null;
        host.removeAttribute("aria-busy");
      }
    }
  };
  const scheduleCalc = () => {
    if (!form.isConnected) return;
    window.clearTimeout(debounceTimer);
    requestSeq += 1;
    activeController?.abort();
    host.setAttribute("aria-busy", "true");
    showState("pending", "Обновляем расчёт…");
    debounceTimer = window.setTimeout(() => void runCalc(), 450);
  };
  form.addEventListener("click", event => {
    const target = event.target as HTMLElement;
    const add = target.closest<HTMLElement>("[data-add-expense]");
    if (add) {
      if (form.querySelectorAll("[data-expense-row]").length >= 50) return;
      const list=form.querySelector(`[data-expense-list="${add.dataset.addExpense}"]`)!;
      list.insertAdjacentHTML("beforeend",calcExpenseRow(add.dataset.addExpense!));
      list.querySelector<HTMLInputElement>("[data-expense-row]:last-child [data-expense-name]")?.focus();
      createIcons({icons:iconSet});scheduleCalc();
    }
    const remove=target.closest("[data-remove-expense]");
    if (remove) { const row=remove.closest("[data-expense-row]")!;const list=row.parentElement!;row.remove();list.parentElement?.querySelector<HTMLButtonElement>("[data-add-expense]")?.focus();scheduleCalc(); }
  });
  form.addEventListener("change",event=>{
    const select=event.target;
    if (!(select instanceof HTMLSelectElement) || !select.closest("[data-expense-row]")) return;
    const monthly=select.closest("[data-expense-row]")?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    if (!monthly) return;
    monthly.disabled=select.value!=="outside-price";
    if (monthly.disabled && monthly.checked) { monthly.checked=false;monthly.dispatchEvent(new Event("input",{bubbles:true})); }
  });
  bindCalculationFloorPlan(form, scheduleCalc);
  form.addEventListener("input", scheduleCalc);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void runCalc();
  });
  void runCalc();
}

function profitabilityResult(result: ProfitabilityResult, input: CalculatorInput) {
  const report = buildCalculationReport({ input, result, objectName: "", objectAddress: "", preparedBy: "", createdAt: "" });
  return `<div class="calculation-report"><div class="result-header"><p class="eyebrow">Расчёт</p><h2>Результат</h2><button type="button" class="secondary" data-calc-edit>К параметрам</button></div>
    <section class="report-kpis" aria-label="Основные показатели">${report.kpis.map(([label, value]) => `<article><span>${h(label)}</span><strong>${h(value)}</strong></article>`).join("")}</section>
    ${reportSectionsHtml(report.sections)}${rentScheduleTable(result.rentSchedule)}
    ${input.comment ? `<section class="report-section"><h2>Комментарий менеджера</h2><p class="report-comment">${h(input.comment)}</p></section>` : ""}
    ${input.floorPlan ? `<section class="report-section"><h2>Планировка квартиры</h2><img class="report-floor-plan" src="${h(input.floorPlan)}" alt="Планировка выбранной квартиры" /></section>` : ""}
    <p class="calc-export-status" data-export-status hidden></p><div class="calc-client-actions">
      <button class="secondary" id="preview-calc" type="button">Предварительный просмотр</button>
      <button class="primary" id="download-calc" type="button">Скачать PDF</button>
      <button class="secondary" id="share-calc" type="button">Подготовить ссылку</button>
    </div><div id="calc-share-output" class="calc-share-output" hidden></div>
    </div>`;
}

function rentScheduleTable(rows: ProfitabilityResult["rentSchedule"]) {
  if (!rows.length) return "";
  return `<details class="calc-schedule"><summary>График арендного дохода по годам</summary><div class="calc-table-scroll"><table><thead><tr><th>Год</th><th>Мес.</th><th>Ставка/мес.</th><th>Доход</th><th>Налог</th></tr></thead><tbody>${rows.map((row) => `<tr><td>${row.year}</td><td>${row.months}</td><td>${calculationMoney(row.monthlyRent)}</td><td>${calculationMoney(row.gross)}</td><td>${calculationMoney(row.tax)}</td></tr>`).join("")}</tbody></table></div></details>`;
}

function bindCalculationResultActions(body: Record<string, FormDataEntryValue>) {
  document.querySelector<HTMLButtonElement>("#preview-calc")?.addEventListener("click", (event) => void previewCalculation(body, event.currentTarget as HTMLButtonElement));
  document.querySelector<HTMLButtonElement>("#download-calc")?.addEventListener("click", (event) => void downloadCalculation(body, event.currentTarget as HTMLButtonElement));
  document.querySelector<HTMLButtonElement>("#share-calc")?.addEventListener("click", (event) => void shareCalculation(body, event.currentTarget as HTMLButtonElement));
}

function bindCalculatorTabs() {
  document.querySelectorAll<HTMLButtonElement>("[data-calculator-tab]").forEach((button) => {
    button.addEventListener("click", () => {
      setRouteParam("tab", button.dataset.calculatorTab || "profitability");
    });
  });
}

function bindMortgageCompare() {
  const form = document.querySelector<HTMLFormElement>("#mortgage-compare-form");
  if (!form) return;
  const render = () => {
    const first = calculateMortgageVariant(form, 1);
    const second = calculateMortgageVariant(form, 2);
    renderDiff("paymentDiff", second.payment - first.payment);
    renderDiff("overpayDiff", second.overpay - first.overpay);
  };
  form.addEventListener("input", render);
  render();
}

function bindCalculationFloorPlan(form: HTMLFormElement, refresh: () => void) {
  const fileInput = form.querySelector<HTMLInputElement>("#calc-floor-plan")!;
  const dataInput = form.querySelector<HTMLInputElement>('[name="floorPlan"]')!;
  const status = form.querySelector<HTMLElement>("#calc-floor-plan-status")!;
  const image = form.querySelector<HTMLImageElement>("#calc-floor-plan-image")!;
  const remove = form.querySelector<HTMLButtonElement>("#calc-remove-floor-plan")!;
  let selection = 0;
  remove.addEventListener("click", () => {
    selection += 1;
    dataInput.value = ""; fileInput.value = ""; fileInput.setCustomValidity("");
    image.hidden = true; image.removeAttribute("src"); remove.hidden = true;
    status.textContent = "Не добавлена";
    refresh();
  });
  fileInput.addEventListener("change", async () => {
    const id = ++selection;
    const file = fileInput.files?.[0];
    if (!file) return;
    fileInput.setCustomValidity("Дождитесь обработки планировки.");
    status.textContent = "Подготавливаем изображение…";
    refresh();
    try {
      if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 10 * 1024 * 1024) throw new Error("Выберите PNG или JPEG размером до 10 МБ.");
      const bitmap = await createImageBitmap(file);
      try {
        const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        const context = canvas.getContext("2d")!;
        context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const data = canvas.toDataURL("image/jpeg", 0.82);
        if (data.length > 750000) throw new Error("Изображение слишком детальное. Выберите планировку меньшего размера.");
        if (id !== selection || !fileInput.isConnected) return;
        dataInput.value = data; image.src = data; image.hidden = false; remove.hidden = false;
        fileInput.setCustomValidity("");
        status.textContent = "Планировка добавлена в отчёт";
      } finally { bitmap.close(); }
    } catch (error) {
      if (id !== selection || !fileInput.isConnected) return;
      fileInput.setCustomValidity(message(error)); status.textContent = message(error);
      remove.hidden = false;
    }
    if (id === selection && fileInput.isConnected) refresh();
  });
}

async function previewCalculation(body: Record<string, FormDataEntryValue>, button: HTMLButtonElement) {
  try {
    button.disabled = true;
    const { html } = await api<{ html: string }>("/api/calculations/client-preview", { method: "POST", body: JSON.stringify(body) });
    if (!button.isConnected) return;
    const dialog = document.createElement("dialog");
    dialog.className = "calc-report-dialog";
    dialog.setAttribute("aria-labelledby", "calc-preview-title");
    dialog.innerHTML = `<div class="calc-report-toolbar"><h2 id="calc-preview-title">Отчёт для клиента</h2><button class="secondary" type="button" autofocus>Закрыть просмотр</button></div><iframe title="Отчёт для клиента" sandbox="allow-same-origin"></iframe>`;
    const frame = dialog.querySelector("iframe")!;
    frame.addEventListener("load", () => {
      frame.contentDocument?.addEventListener("keydown", (event) => {
        if (event.key === "Escape") { event.preventDefault(); dialog.close(); }
      });
    });
    frame.srcdoc = html;
    dialog.querySelector("button")!.addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => {
      dialog.remove();
      document.querySelector<HTMLButtonElement>("#preview-calc")?.focus({ preventScroll: true });
    }, { once: true });
    document.body.append(dialog);
    dialog.showModal();
  } catch (error) {
    alert(message(error));
  } finally { button.disabled = false; }
}

async function downloadCalculation(body: Record<string, FormDataEntryValue>, button?: HTMLButtonElement) {
  try {
    if (button) button.disabled = true;
    const response = await fetch("/api/calculations/download", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!response.ok) throw new Error(requestMessage(await readResponsePayload(response), response.status));
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "novy-bereg-profitability.pdf";
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  } catch (error) {
    alert(message(error));
  } finally {
    if (button) button.disabled = false;
  }
}

async function shareCalculation(body: Record<string, FormDataEntryValue>, button: HTMLButtonElement) {
  const host = document.querySelector<HTMLElement>("#calc-share-output");
  if (!host) return;
  try {
    button.disabled = true;
    button.innerHTML = `<i data-lucide="refresh-cw"></i> Готовим ссылку`;
    createIcons({ icons: iconSet });
    const share = await api<{ token: string; url: string }>("/api/calculations/share", { method: "POST", body: JSON.stringify(body) });
    host.hidden = false;
    host.innerHTML = `
      <div><span>Ссылка для клиента</span><strong>Открывается без входа и не ведёт во внутренний портал</strong></div>
      <input id="client-calculation-url" value="${h(share.url)}" readonly aria-label="Ссылка для клиента" />
      <div class="calc-share-actions">
        <button class="secondary" id="copy-calc-link" type="button"><i data-lucide="copy"></i> Копировать</button>
        <a class="button secondary" href="${h(share.url)}" target="_blank" rel="noopener noreferrer"><i data-lucide="external-link"></i> Открыть</a>
        <button class="ghost danger-link" id="revoke-calc-link" type="button">Отозвать</button>
      </div>`;
    createIcons({ icons: iconSet });
    document.querySelector("#copy-calc-link")?.addEventListener("click", () => void copyCalculationUrl(share.url));
    document.querySelector<HTMLButtonElement>("#revoke-calc-link")?.addEventListener("click", (event) => void revokeCalculationUrl(share.token, host, event.currentTarget as HTMLButtonElement));
  } catch (error) {
    host.hidden = false;
    host.innerHTML = `<div class="warning"><i data-lucide="alert-triangle"></i><span>${h(message(error))}</span></div>`;
    createIcons({ icons: iconSet });
  } finally {
    button.disabled = false;
    button.innerHTML = `<i data-lucide="link"></i> Подготовить новую ссылку`;
    createIcons({ icons: iconSet });
  }
}

async function copyCalculationUrl(url: string) {
  const input = document.querySelector<HTMLInputElement>("#client-calculation-url");
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    input?.select();
    document.execCommand("copy");
  }
  const button = document.querySelector<HTMLButtonElement>("#copy-calc-link");
  if (button) button.textContent = "Скопировано";
}

async function revokeCalculationUrl(token: string, host: HTMLElement, button: HTMLButtonElement) {
  try {
    button.disabled = true;
    await api(`/api/calculations/share/${encodeURIComponent(token)}`, { method: "DELETE" });
    host.innerHTML = `<div class="calc-share-revoked"><i data-lucide="check"></i><span>Ссылка отключена.</span></div>`;
    createIcons({ icons: iconSet });
  } catch (error) {
    button.disabled = false;
    alert(message(error));
  }
}

async function hydrateFiles() {
  const list = document.querySelector("#files-list");
  if (!list) return;
  try {
    const data = await api<{ items: FileItem[]; tree: FolderNode[] }>("/api/files");
    const activeFolderId = activeFileFolderId(data.tree);
    const folders = flattenFolders(data.tree);
    currentFiles = data.items;
    currentFolders = folders;
    setUnreadFilesCountFromItems(data.items);
    const childFolders = folders.filter((folder) => folder.parentId === activeFolderId);
    const items = activeFolderId == null
      ? data.items
      : data.items.filter((item) => Number(item.folder_id) === activeFolderId);
    const readCount = data.items.filter((item) => item.read_status?.status === "read").length;
    const unreadCount = Math.max(0, data.items.length - readCount);
    list.innerHTML = `
      <section class="files-space-header">
        <div>
          <p class="eyebrow">Файлы и база знаний</p>
          <h2>${h(activeFolderLabel(data.tree, activeFolderId))}</h2>
        </div>
        ${isClientWorkMode() ? "" : `<div class="file-read-summary" aria-label="Статусы прочтения">
          <span><strong>${readCount}</strong> прочитано</span>
          <span><strong>${unreadCount}</strong> не прочитано</span>
        </div>`}
        ${filesBreadcrumb(data.tree, activeFolderId)}
      </section>
      ${isClientWorkMode() ? "" : folderCreatePanel(activeFolderId)}
      ${isClientWorkMode() ? "" : filesUploadPanel(data.tree, activeFolderId)}
      ${childFolders.length ? `<section class="folder-grid" aria-label="Вложенные папки">${childFolders.map(folderCard).join("")}</section>` : ""}
      ${items.length
        ? `<section class="files-panel"><div class="files-toolbar"><strong>${items.length} ${pluralizeFiles(items.length)}</strong><span>${h(activeFolderPathLabel(data.tree, activeFolderId))}</span></div>${items.map(fileRow).join("")}</section>`
        : emptyState("В папке нет файлов", childFolders.length ? "Выберите папку." : "Загрузите файл или создайте папку.")}
    `;
    list.removeAttribute("aria-busy");
    document.querySelectorAll<HTMLElement>("[data-open-folder]").forEach((node) => node.addEventListener("click", () => {
      setRouteParam("folder", node.dataset.openFolder || "");
    }));
    if (!isClientWorkMode()) {
      bindFolderCreate(activeFolderId);
      bindFileUpload(data.tree, activeFolderId);
    }
    bindFileActions();
  } catch (error) {
    list.innerHTML = errorState("Не удалось загрузить файлы", message(error), "Повторить", "retry-files");
    document.querySelector("#retry-files")?.addEventListener("click", () => void hydrateFiles());
  }
  createIcons({ icons: iconSet });
}

async function refreshUnreadFilesCount() {
  try {
    const data = await api<{ items: FileItem[]; tree: FolderNode[] }>("/api/files");
    setUnreadFilesCountFromItems(data.items);
  } catch {
    unreadFilesCount = 0;
  }
}

function setUnreadFilesCountFromItems(items: FileItem[]) {
  unreadFilesCount = items.filter((item) => item.read_status?.status !== "read").length;
  updateUnreadFilesBadges();
}

function updateUnreadFilesBadges() {
  document.querySelectorAll<HTMLElement>("[data-files-unread-badge]").forEach((node) => {
    node.textContent = String(unreadFilesCount);
    node.toggleAttribute("hidden", unreadFilesCount <= 0);
    node.setAttribute("aria-label", `${unreadFilesCount} непрочитанных файлов`);
  });
}

async function hydrateNews() {
  const host = document.querySelector("#news-list")!;
  try {
    const data = await api<{ items: any[] }>("/api/news");
    host.innerHTML = data.items.length ? data.items.map((item) => `<article class="row-card"><i data-lucide="bell"></i><div><strong>${h(item.title)}</strong><span>${safeDate(item.created_at)} · ${h(item.body)}</span></div></article>`).join("") : emptyState("Новостей пока нет", "Обновления появятся здесь.");
    host.removeAttribute("aria-busy");
  } catch (error) {
    host.innerHTML = errorState("Не удалось загрузить новости", message(error), "Повторить", "retry-news");
    document.querySelector("#retry-news")?.addEventListener("click", () => void hydrateNews());
  }
  createIcons({ icons: iconSet });
}

async function hydrateUsers() {
  const host = document.querySelector("#users-list")!;
  try {
    const data = await api<{ items: any[] }>("/api/users");
    host.innerHTML = usersDashboard(data.items);
    host.removeAttribute("aria-busy");
    bindUserCreateForm();
  } catch (error) {
    host.innerHTML = errorState("Не удалось загрузить пользователей", message(error), "Повторить", "retry-users");
    document.querySelector("#retry-users")?.addEventListener("click", () => void hydrateUsers());
  }
  createIcons({ icons: iconSet });
}

function usersDashboard(items: any[]) {
  return `
    <section class="users-admin-panel">
      <div class="section-header">
        <div>
          <h2>Новый пользователь</h2>
        </div>
      </div>
      <form class="user-create-form" id="user-create-form">
        <label>Имя сотрудника<input name="name" autocomplete="name" placeholder="Например, Иван Петров" required /></label>
        <label>Логин<input name="login" autocomplete="username" placeholder="ivan.petrov" required /></label>
        <label>Роль
          <select name="role">
            <option value="manager">Менеджер</option>
            <option value="director">Директор</option>
            <option value="admin">Технический администратор</option>
          </select>
        </label>
        <label>Статус
          <select name="state">
            <option value="active">Активен</option>
            <option value="blocked">Заблокирован</option>
            <option value="archived">Архивирован</option>
          </select>
        </label>
        <label>Пользователь amoCRM<input name="amoUserId" type="number" inputmode="numeric" min="1" placeholder="Например, 12070314" /></label>
        <label>Временный пароль<input name="password" type="password" autocomplete="new-password" minlength="8" placeholder="Не короче 8 символов" required /></label>
        ${managerObjectAccessField([], "create")}
        <button class="primary" type="submit"><i data-lucide="users"></i> Добавить пользователя</button>
        <p class="field-error" id="user-create-error" role="alert"></p>
      </form>
    </section>
    <section class="users-table-panel">
      <table>
        <thead><tr><th>Сотрудник</th><th>Логин</th><th>Роль</th><th>Статус</th><th>amoCRM ID</th><th>Объекты менеджера</th><th>Новый пароль</th><th>Действия</th></tr></thead>
        <tbody>${items.map(userTableRow).join("")}</tbody>
      </table>
    </section>
  `;
}

function userTableRow(user: any) {
  const isSelf = currentUser?.id === Number(user.id);
  return `
    <tr class="user-row" data-user-id="${user.id}">
      <td><input name="name" value="${h(user.name)}" autocomplete="name" /></td>
      <td><input name="login" value="${h(user.login)}" autocomplete="username" /></td>
      <td>
        <select name="role" ${isSelf ? "disabled" : ""}>
          ${userRoleOptions(user.role)}
        </select>
      </td>
      <td>
        <select name="state" ${isSelf ? "disabled" : ""}>
          ${userStateOptions(user.state)}
        </select>
      </td>
      <td><input name="amoUserId" type="number" inputmode="numeric" min="1" value="${user.amo_user_id || ""}" placeholder="Не связан" /></td>
      <td>${managerObjectAccessField(user.complex_ids || [], `user-${user.id}`)}</td>
      <td><input name="password" type="password" autocomplete="new-password" minlength="8" placeholder="Не менять" /></td>
      <td>
        <div class="user-row-actions">
          <button class="user-action-button" type="button" data-user-save="${user.id}" aria-label="Сохранить изменения"${tooltipAttr("Сохранить изменения")}><i data-lucide="save" aria-hidden="true"></i></button>
          ${isSelf ? "" : `<button class="user-action-button" type="button" data-user-toggle="${user.id}" aria-label="${user.state === "active" ? "Заблокировать пользователя" : "Активировать пользователя"}"${tooltipAttr(user.state === "active" ? "Заблокировать пользователя" : "Активировать пользователя")}><i data-lucide="${user.state === "active" ? "lock" : "lock-open"}" aria-hidden="true"></i></button>`}
          ${isSelf ? "" : `<button class="user-action-button is-danger" type="button" data-user-delete="${user.id}" aria-label="Удалить пользователя"${tooltipAttr("Удалить пользователя")}><i data-lucide="trash-2" aria-hidden="true"></i></button>`}
        </div>
        <p class="field-error" data-user-error="${user.id}" role="alert"></p>
      </td>
    </tr>
  `;
}

function bindUserCreateForm() {
  const form = document.querySelector<HTMLFormElement>("#user-create-form");
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const errorNode = document.querySelector<HTMLElement>("#user-create-error");
    const submit = form.querySelector<HTMLButtonElement>("button[type='submit']");
    const body = formPayloadWithComplexIds(form);
    try {
      if (errorNode) errorNode.textContent = "";
      if (submit) submit.disabled = true;
      await api<{ item: any }>("/api/users", {
        method: "POST",
        body: JSON.stringify(body)
      });
      form.reset();
      await hydrateUsers();
    } catch (error) {
      if (errorNode) errorNode.textContent = message(error);
    } finally {
      if (submit) submit.disabled = false;
    }
  });
  bindUserManagementActions();
}

function bindUserManagementActions() {
  bindUserRoleAccessVisibility();
  document.querySelectorAll<HTMLButtonElement>("[data-user-save]").forEach((button) => {
    button.addEventListener("click", async () => {
      const userId = button.dataset.userSave || "";
      const row = button.closest<HTMLTableRowElement>("[data-user-id]");
      if (!row) return;
      await saveUserRow(row, userId, button);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-user-toggle]").forEach((button) => {
    button.addEventListener("click", async () => {
      const userId = button.dataset.userToggle || "";
      const row = button.closest<HTMLTableRowElement>("[data-user-id]");
      if (!row) return;
      const state = row.querySelector<HTMLSelectElement>("select[name='state']");
      if (!state) return;
      state.value = state.value === "active" ? "blocked" : "active";
      await saveUserRow(row, userId, button);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-user-delete]").forEach((button) => {
    button.addEventListener("click", async () => {
      const userId = button.dataset.userDelete || "";
      const row = button.closest<HTMLTableRowElement>("[data-user-id]");
      const name = row?.querySelector<HTMLInputElement>("input[name='name']")?.value || "пользователя";
      if (!userId || !await confirmDialog({
        title: "Удалить пользователя?",
        body: `Аккаунт «${name}» будет удален из портала. Это действие нельзя выполнить для собственного аккаунта.`,
        confirmText: "Удалить"
      })) return;
      const errorNode = document.querySelector<HTMLElement>(`[data-user-error="${userId}"]`);
      try {
        if (errorNode) errorNode.textContent = "";
        button.disabled = true;
        await api(`/api/users/${userId}`, { method: "DELETE" });
        await hydrateUsers();
      } catch (error) {
        if (errorNode) errorNode.textContent = message(error);
        button.disabled = false;
      }
    });
  });
}

async function saveUserRow(row: HTMLTableRowElement, userId: string, trigger: HTMLButtonElement) {
  const errorNode = document.querySelector<HTMLElement>(`[data-user-error="${userId}"]`);
  const body = userRowPayload(row);
  try {
    if (errorNode) errorNode.textContent = "";
    trigger.disabled = true;
    await api(`/api/users/${userId}`, { method: "PATCH", body: JSON.stringify(body) });
    await hydrateUsers();
  } catch (error) {
    if (errorNode) errorNode.textContent = message(error);
  } finally {
    trigger.disabled = false;
  }
}

function userRowPayload(row: HTMLTableRowElement) {
  const value = (selector: string) => row.querySelector<HTMLInputElement | HTMLSelectElement>(selector)?.value.trim() || "";
  const body: Record<string, string | number[]> = {
    name: value("input[name='name']"),
    login: value("input[name='login']"),
    role: value("select[name='role']"),
    state: value("select[name='state']"),
    amoUserId: value("input[name='amoUserId']")
  };
  const password = value("input[name='password']");
  if (password) body.password = password;
  body.complexIds = selectedUserComplexIds(row);
  return body;
}

function formPayloadWithComplexIds(form: HTMLFormElement) {
  const body = Object.fromEntries(new FormData(form).entries()) as Record<string, FormDataEntryValue | number[]>;
  body.complexIds = selectedUserComplexIds(form);
  return body;
}

function selectedUserComplexIds(root: ParentNode) {
  return Array.from(root.querySelectorAll<HTMLInputElement>("input[name='complexIds']:checked"))
    .map((input) => Number(input.value))
    .filter((id) => Number.isInteger(id) && id > 0);
}

function bindUserRoleAccessVisibility() {
  document.querySelectorAll<HTMLSelectElement>("select[name='role']").forEach((select) => {
    const container = select.closest("form, tr");
    const field = container?.querySelector<HTMLElement>("[data-manager-object-access]");
    const apply = () => {
      const isManager = select.value === "manager";
      if (field) field.hidden = !isManager;
    };
    select.addEventListener("change", apply);
    apply();
  });
  bindManagerObjectMultiselects();
}

function bindManagerObjectMultiselects() {
  if (managerObjectOutsideHandler) document.removeEventListener("pointerdown", managerObjectOutsideHandler, true);
  managerObjectOutsideHandler = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    document.querySelectorAll<HTMLDetailsElement>("[data-manager-object-select][open]").forEach((details) => {
      if (!details.contains(target)) details.open = false;
    });
  };
  document.addEventListener("pointerdown", managerObjectOutsideHandler, true);
  document.querySelectorAll<HTMLDetailsElement>("[data-manager-object-select]").forEach((details) => {
    const update = () => updateManagerObjectSummary(details);
    details.querySelectorAll<HTMLInputElement>("input[name='complexIds']").forEach((input) => input.addEventListener("change", update));
    details.addEventListener("toggle", () => {
      if (!details.open) return;
      document.querySelectorAll<HTMLDetailsElement>("[data-manager-object-select][open]").forEach((other) => {
        if (other !== details) other.open = false;
      });
    });
    details.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      details.open = false;
      details.querySelector<HTMLElement>("summary")?.focus();
    });
    details.addEventListener("focusout", () => window.setTimeout(() => {
      if (!details.contains(document.activeElement)) details.open = false;
    }, 0));
    update();
  });
}

function updateManagerObjectSummary(details: HTMLDetailsElement) {
  const selected = Array.from(details.querySelectorAll<HTMLInputElement>("input[name='complexIds']:checked"));
  const label = details.querySelector<HTMLElement>("[data-manager-object-summary]");
  if (!label) return;
  const names = selected.map((input) => input.dataset.objectName || "").filter(Boolean);
  label.textContent = selected.length === 0 ? "Выберите объекты" : selected.length === 1 ? names[0] : `${selected.length} ${objectsCountWord(selected.length)} выбрано`;
  const summary = details.querySelector<HTMLElement>("summary");
  if (summary) summary.title = names.length ? names.join(", ") : "Выберите объекты менеджера";
}

function objectsCountWord(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "объект";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "объекта";
  return "объектов";
}

function managerObjectAccessField(selectedIds: number[], scope: string) {
  if (!objects.length) {
    return `<fieldset class="manager-object-access" data-manager-object-access><legend>Объекты менеджера</legend><p class="muted">Объекты еще не добавлены. Менеджер не увидит ЖК до назначения.</p></fieldset>`;
  }
  const selected = new Set(selectedIds.map(Number));
  return `
    <fieldset class="manager-object-access" data-manager-object-access>
      <legend>Объекты менеджера</legend>
      <details class="manager-object-select" data-manager-object-select>
        <summary><span data-manager-object-summary>Выберите объекты</span><i data-lucide="chevron-down" aria-hidden="true"></i></summary>
        <div class="manager-object-access-list">
          ${objects.map((object) => {
            const id = Number(object.id);
            const inputId = `complex-access-${scope}-${id}`;
            return `
              <label for="${inputId}">
                <input id="${inputId}" name="complexIds" type="checkbox" value="${id}" data-object-name="${h(object.name)}" ${selected.has(id) ? "checked" : ""} />
                <span>${h(object.name)}</span>
              </label>
            `;
          }).join("")}
        </div>
      </details>
    </fieldset>
  `;
}

function userRoleOptions(active: string) {
  return [
    ["manager", "Менеджер"],
    ["director", "Директор"],
    ["admin", "Технический администратор"]
  ].map(([value, label]) => `<option value="${value}" ${active === value ? "selected" : ""}>${label}</option>`).join("");
}

function userStateOptions(active: string) {
  return [
    ["active", "Активен"],
    ["blocked", "Заблокирован"],
    ["archived", "Архивирован"]
  ].map(([value, label]) => `<option value="${value}" ${active === value ? "selected" : ""}>${label}</option>`).join("");
}

async function hydrateAudit() {
  const host = document.querySelector("#audit-list")!;
  try {
    const [audit, users] = await Promise.all([
      api<{ items: any[] }>("/api/audit-log"),
      api<{ items: any[] }>("/api/users")
    ]);
    host.innerHTML = auditDashboard(audit.items, users.items);
    host.removeAttribute("aria-busy");
  } catch (error) {
    host.innerHTML = errorState("Не удалось загрузить журнал", message(error), "Повторить", "retry-audit");
    document.querySelector("#retry-audit")?.addEventListener("click", () => void hydrateAudit());
  }
}

async function api<T>(url: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, { ...init, headers: { "Content-Type": "application/json", ...(init.headers || {}) } });
  } catch {
    throw new Error("Не удалось подключиться к серверу. Проверьте, что портал запущен, и повторите действие.");
  }
  const data = await readResponsePayload(response);
  if (!response.ok) {
    if (response.status === 401 && url !== "/api/auth/login") expirePortalSession();
    throw new Error(requestMessage(data, response.status));
  }
  return data as T;
}

function expirePortalSession() {
  if (!currentUser) return;
  currentUser = null;
  appConfig = null;
  objects = [];
  activeObject = null;
  currentFiles = [];
  currentFolders = [];
  unreadFilesCount = 0;
  render();
}

async function readResponsePayload(response: Response) {
  const text = await response.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function currentHash() {
  return location.hash.replace(/^#/, "") || "/objects";
}

function currentRoutePath() {
  return currentHash().split("?")[0] || "/objects";
}

function routeParams() {
  return new URLSearchParams(currentHash().split("?")[1] || "");
}

function objectFiltersFromUrl(): ObjectFilters {
  const params = routeParams();
  return {
    q: params.get("q") || "",
    district: params.get("district") || "",
    status: params.get("status") || ""
  };
}

function filteredObjects(filters: ObjectFilters) {
  const q = filters.q.trim().toLowerCase();
  return objects.filter((item) =>
    (!q || [item.name, item.address, item.developer, item.district].join(" ").toLowerCase().includes(q))
    && (!filters.district || item.district === filters.district)
    && (!filters.status || item.status === filters.status)
  );
}

function auditDashboard(items: any[], users: any[]) {
  const userById = new Map(users.map((user) => [Number(user.id), user]));
  const today = new Date().toDateString();
  const todayCount = items.filter((item) => new Date(item.created_at).toDateString() === today).length;
  const fileCount = items.filter((item) => item.entity === "file" || item.entity === "folder").length;
  const loginCount = items.filter((item) => String(item.action).startsWith("login")).length;
  const actorCount = new Set(items.map((item) => item.user_id).filter(Boolean)).size;
  return `
    <section class="audit-dashboard" aria-label="Журнал действий пользователей">
      <div class="audit-summary">
        ${auditSummaryCard("Сегодня", todayCount, "событий")}
        ${auditSummaryCard("Сотрудники", actorCount, "активных")}
        ${auditSummaryCard("Входы", loginCount, "за период")}
        ${auditSummaryCard("Файлы", fileCount, "операций")}
      </div>
      ${auditTable(items, userById)}
    </section>
  `;
}

function auditSummaryCard(label: string, value: number, caption: string) {
  return `<div class="audit-summary-card"><span>${label}</span><strong>${number.format(value)}</strong><small>${caption}</small></div>`;
}

function auditTable(items: any[], userById: Map<number, any>) {
  if (!items.length) return emptyState("Записей пока нет", "Действия сотрудников появятся здесь.");
  return `
    <section class="audit-table-panel" aria-label="Таблица действий пользователей">
      <div class="audit-table-scroll">
        <table class="audit-table">
          <colgroup><col class="audit-col-time" /><col class="audit-col-action" /><col class="audit-col-actor" /><col class="audit-col-entity" /><col class="audit-col-details" /></colgroup>
          <thead>
            <tr>
              <th scope="col">Время</th>
              <th scope="col">Действие</th>
              <th scope="col">Сотрудник</th>
              <th scope="col">Сущность</th>
              <th scope="col">Детали</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((item) => auditTableRow(item, userById)).join("")}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function auditTableRow(item: any, userById: Map<number, any>) {
  const user = item.user_id ? userById.get(Number(item.user_id)) : null;
  const actor = user?.name || auditActorFallback(item);
  const meta = [user?.role ? roleName(user.role) : "", user?.login ? `логин: ${user.login}` : ""].filter(Boolean).join(" · ");
  const parsedDetails = auditDetails(item.details);
  const chips = auditDetailChips(parsedDetails);
  const description = auditEventDescription(item, actor, parsedDetails);
  return `
    <tr class="${auditToneClass(item.action)}" title="${h(description)}">
      <td class="audit-time-cell"><strong>${h(safeTime(item.created_at))}</strong><span>${h(safeDate(item.created_at))}</span></td>
      <td class="audit-action-cell"><div class="audit-action-content"><span class="audit-action-pill">${h(auditActionLabel(item.action))}</span><strong>${h(auditEventTitle(item, actor, parsedDetails))}</strong></div></td>
      <td class="audit-actor-cell"><strong>${h(actor)}</strong><span>${h(meta || "Система")}</span></td>
      <td><span class="audit-entity-chip">${h(auditEntityLabel(item.entity))}</span></td>
      <td class="audit-details-cell">${chips ? `<div class="audit-detail-chips">${chips}</div>` : `<span>${h(description)}</span>`}</td>
    </tr>
  `;
}

function auditActorFallback(item: any) {
  const details = String(item.details || "");
  if (item.action === "login_failed") return details.replace(/^login=/, "") || "Неизвестный пользователь";
  return item.user_id ? `Пользователь #${item.user_id}` : "Система";
}

function auditEventTitle(item: any, actor: string, details: Record<string, string>) {
  switch (item.action) {
    case "login_success": return `${actor} вошел в портал`;
    case "login_failed": return `Неудачная попытка входа`;
    case "folder_create": return `${actor} создал папку`;
    case "folder_delete": return `${actor} удалил папку`;
    case "file_upload": return `${actor} загрузил файлы`;
    case "file_update": return `${actor} переместил или обновил файл`;
    case "file_delete": return `${actor} удалил файл`;
    case "file_read_status_update": return `${actor} изменил отметку прочтения`;
    case "object_delete": return `${actor} удалил объект`;
    case "user_create": return `${actor} создал пользователя`;
    case "user_update": return `${actor} изменил пользователя`;
    case "user_delete": return `${actor} удалил пользователя`;
    case "calculation_share_create": return `${actor} подготовил ссылку на расчёт`;
    case "calculation_share_revoke": return `${actor} отозвал ссылку на расчёт`;
    default: return `${actor} выполнил действие`;
  }
}

function auditEventDescription(item: any, actor: string, details: Record<string, string>) {
  switch (item.action) {
    case "login_success": return `Успешная авторизация под учетной записью ${details.login || item.details || actor}.`;
    case "login_failed": return `Портал отклонил вход для логина ${details.login || String(item.details || "").replace(/^login=/, "") || "без имени"}.`;
    case "file_upload": return `Добавлено файлов: ${details.count || "1"}. Папка назначения: ${details.folder_id ? `#${details.folder_id}` : "не указана"}.`;
    case "file_update": return `Файл ${details.file_id ? `#${details.file_id}` : ""} перемещен или переименован. Новая папка: ${details.folder_id ? `#${details.folder_id}` : "не указана"}.`;
    case "file_delete": return `Файл ${details.file_id ? `#${details.file_id}` : ""} удален из файлового пространства.`;
    case "folder_create": return `Создана новая папка ${details.folder_id ? `#${details.folder_id}` : ""} для материалов.`;
    case "folder_delete": return `Папка ${details.folder_id ? `#${details.folder_id}` : ""} удалена.`;
    case "file_read_status_update": return `Изменена отметка файла ${details.file_id ? `#${details.file_id}` : ""}: ${details.status === "read" ? "прочитано" : details.status === "unread" ? "не прочитано" : details.status || "статус не указан"}.`;
    case "object_delete": return `Объект ${details.complex_id ? `#${details.complex_id}` : ""} удален вместе с досье и доступами.`;
    case "user_create": return `Создан пользователь ${details.login || details.user_id || ""}. Роль: ${details.role ? roleName(details.role) : "не указана"}.`;
    case "user_update": return `Изменен пользователь ${details.login || details.user_id || ""}. Статус: ${details.state ? userStateLabel(details.state) : "не указан"}.`;
    case "user_delete": return `Удален пользователь ${details.login || details.user_id || ""}.`;
    case "calculation_share_create": return `Подготовлена клиентская ссылка для ${details.object_id === "manual" ? "ручного расчёта" : `объекта #${details.object_id || "без номера"}`}.`;
    case "calculation_share_revoke": return `Клиентская ссылка на расчёт отозвана.`;
    default: return `${auditEntityLabel(item.entity)} · ${item.details || "детали не указаны"}.`;
  }
}

function auditActionLabel(action: string) {
  return ({
    login_success: "Вход",
    login_failed: "Ошибка входа",
    folder_create: "Папка",
    folder_delete: "Удаление",
    file_upload: "Загрузка",
    file_update: "Изменение",
    file_delete: "Удаление",
    file_read_status_update: "Прочтение",
    object_delete: "Удаление",
    user_create: "Доступ",
    user_update: "Доступ",
    user_delete: "Удаление",
    calculation_share_create: "Расчёт",
    calculation_share_revoke: "Отзыв ссылки"
  } as Record<string, string>)[action] || "Действие";
}

function auditEntityLabel(entity: string) {
  return ({
    user: "Пользователь",
    file: "Файл",
    folder: "Папка",
    complex: "Объект",
    calculation_share: "Расчёт"
  } as Record<string, string>)[entity] || entity || "Сущность";
}

function auditToneClass(action: string) {
  if (action === "login_failed" || action === "file_delete" || action === "folder_delete" || action === "object_delete" || action === "user_delete") return "is-attention";
  if (action === "file_upload" || action === "folder_create" || action === "user_create") return "is-positive";
  if (action === "file_read_status_update") return "is-read";
  return "is-neutral";
}

function auditDetails(value: unknown) {
  const details = String(value || "");
  if (!details.includes("=")) return details ? { login: details } : {};
  return Object.fromEntries(details.split(";").map((part) => {
    const [key, ...rest] = part.split("=");
    return [key.trim(), rest.join("=").trim()];
  }).filter(([key]) => key));
}

function auditDetailChips(details: Record<string, string>) {
  const labels: Record<string, string> = {
    login: "Логин",
    folder_id: "Папка",
    file_id: "Файл",
    complex_id: "Объект",
    object_id: "Объект",
    count: "Количество",
    status: "Статус"
  };
  return Object.entries(details)
    .filter(([, value]) => value)
    .map(([key, value]) => `<span>${h(labels[key] || key)}: ${h(auditDetailValue(key, value))}</span>`)
    .join("");
}

function auditDetailValue(key: string, value: string) {
  if (key === "folder_id") return `#${value}`;
  if (key === "file_id") return `#${value}`;
  if (key === "complex_id") return `#${value}`;
  if (key === "object_id") return value === "manual" ? "ручной расчёт" : `#${value}`;
  if (key === "status") return value === "read" ? "прочитано" : value === "unread" ? "не прочитано" : value;
  return value;
}

function safeTime(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "--:--" : new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(parsed);
}

function statusBadgeClass(status: string) {
  const normalized = status.trim().toLowerCase();
  if (["в продаже", "свободно", "доступно", "free", "available"].includes(normalized)) return "status-badge--free";
  if (["бронь", "забронировано", "резерв", "reserved", "booked"].includes(normalized)) return "status-badge--reserved";
  if (["продано", "сдан", "сдано", "sold", "delivered"].includes(normalized)) return "status-badge--sold";
  if (["строится", "строительство", "construction"].includes(normalized)) return "status-badge--construction";
  return "status-badge--neutral";
}

function objectFilterToolbar(districts: string[], statuses: string[], filters: ObjectFilters) {
  return `
    <section class="toolbar">
      <label class="search-field"><span class="sr-only">Поиск объекта</span><i data-lucide="search"></i><input id="object-query" value="${h(filters.q)}" placeholder="Поиск по названию, району, адресу" autocomplete="off" /></label>
      ${selectField("district-filter", "Район", "Все районы", districts, filters.district)}
      ${selectField("status-filter", "Статус", "Все статусы", statuses, filters.status)}
      <button class="secondary reset-filters" id="reset-filters" type="button"><i data-lucide="sliders-horizontal"></i> Сбросить</button>
    </section>
  `;
}

function objectsOverview(items: ApiObject[]) {
  const prices = items.map((item) => Number(item.min_price)).filter(Number.isFinite);
  const minPrice = prices.length ? Math.min(...prices) : NaN;
  const units = items.reduce((sum, item) => sum + Number(item.units || 0), 0);
  const districts = unique(items.map((item) => item.district)).length;
  return `
    <section class="workspace-intro">
      <div>
        <p class="eyebrow">Рабочая база</p>
        <h2>Жилая база для консультаций</h2>
      </div>
      <div class="quick-metrics" aria-label="Сводка по объектам">
        <span><strong>${items.length}</strong> ${pluralizeObjects(items.length)}</span>
        <span><strong>${units}</strong> ${pluralizeApartments(units)}</span>
        <span><strong>${districts}</strong> ${pluralizeDistricts(districts)}</span>
        <span><strong>${Number.isFinite(minPrice) ? formatMoney(minPrice) : "-"}</strong> от</span>
      </div>
    </section>
  `;
}

function heroStatusPanel(path: string) {
  const items = filteredObjects(objectFiltersFromUrl());
  const units = items.reduce((sum, item) => sum + Number(item.units || 0), 0);
  const primary = path.startsWith("/objects/") && activeObject ? activeObject.name : `${items.length} ${pluralizeObjects(items.length)}`;
  return `
    <aside class="hero-status-card" aria-label="Рабочая сводка">
      <strong>${h(primary)}</strong>
      <dl>
        <div><dt>Квартир доступно</dt><dd>${units}</dd></div>
        <div><dt>Роль</dt><dd>${currentUser ? roleName(currentUser.role) : "-"}</dd></div>
      </dl>
    </aside>
  `;
}

function objectGrid(items: ApiObject[]) {
  return items.length ? items.map(objectCard).join("") : emptyState("Объекты не найдены", "Измените фильтры или сбросьте поиск.", "Сбросить фильтры", "reset-filters-inline");
}

function setObjectFilterUrl(filters: ObjectFilters) {
  const params = routeParams();
  params.delete("q");
  params.delete("district");
  params.delete("status");
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.district) params.set("district", filters.district);
  if (filters.status) params.set("status", filters.status);
  const suffix = params.toString();
  history.replaceState(null, "", `${location.pathname}${location.search}#${route}${suffix ? `?${suffix}` : ""}`);
}

function objectHref(id: number | string) {
  return `#${objectRoute(id)}`;
}

function objectRoute(id: number | string) {
  return `/objects/${id}${objectQuerySuffix()}`;
}

function objectsListHref() {
  return `#/objects${objectQuerySuffix()}`;
}

function calculatorRoute(objectId: number | string) {
  const params = new URLSearchParams();
  if (objectId) params.set("objectId", String(objectId));
  const tab = routeParams().get("tab");
  if (tab) params.set("tab", tab);
  const suffix = params.toString();
  return `/calculator${suffix ? `?${suffix}` : ""}`;
}

function calculatorObjectFromUrl() {
  const objectId = routeParams().get("objectId");
  const selected = objects.find((item) => String(item.id) === objectId) || null;
  activeObject = selected;
  return selected;
}

function objectQuerySuffix() {
  const current = routeParams();
  const params = new URLSearchParams();
  ["q", "district", "status"].forEach((key) => {
    const value = current.get(key);
    if (value) params.set(key, value);
  });
  const suffix = params.toString();
  return suffix ? `?${suffix}` : "";
}

function setRouteParam(key: string, value: string) {
  const params = routeParams();
  if (value) params.set(key, value);
  else params.delete(key);
  const suffix = params.toString();
  location.hash = `${route}${suffix ? `?${suffix}` : ""}`;
}

function activeFileFolderId(tree: FolderNode[]) {
  const value = Number(routeParams().get("folder") || 0);
  if (!value) return null;
  return folderExists(tree, value) ? value : null;
}

function folderExists(nodes: FolderNode[], id: number): boolean {
  return nodes.some((node) => node.id === id || folderExists(node.nodes || [], id));
}

function flattenFolders(nodes: FolderNode[], parentId: number | null = null, trail: Array<{ id: number | null; name: string }> = []): Array<{ id: number; name: string; parentId: number | null; trail: Array<{ id: number | null; name: string }> }> {
  return nodes.flatMap((node) => {
    const currentTrail = [...trail, { id: node.id, name: node.name }];
    return [
      { id: node.id, name: node.name, parentId, trail: currentTrail },
      ...flattenFolders(node.nodes || [], node.id, currentTrail)
    ];
  });
}

function calculatorTabFromUrl(): CalculatorTab {
  const tab = routeParams().get("tab");
  return tab === "mortgage-compare" ? "mortgage-compare" : "profitability";
}

function objectTabFromUrl(): ObjectTab {
  const tab = routeParams().get("tab");
  return clientSafeObjectTabs().some(([id]) => id === tab) ? tab as ObjectTab : "buildings";
}

function objectTabs(active: ObjectTab) {
  const tabs = clientSafeObjectTabs();
  return `<div class="tabs" role="tablist">${tabs.map(([id, label]) => `<button type="button" role="tab" data-object-tab="${id}" aria-selected="${id === active}" class="${id === active ? "active" : ""}">${label}</button>`).join("")}</div>`;
}

function clientSafeObjectTabs(): Array<[ObjectTab, string]> {
  const tabs: Array<[ObjectTab, string]> = [
    ["buildings", "Корпуса"],
    ["units", "Квартиры"],
    ["purchase", "Условия"],
    ["documents", "Документы"],
    ["contacts", "Контакты"],
    ["files", "Файлы"],
    ["history", "История"]
  ];
  return isClientWorkMode() ? tabs.filter(([id]) => id !== "contacts" && id !== "history") : tabs;
}

function calculatorTabs(active: CalculatorTab) {
  const tabs: Array<[CalculatorTab, string]> = [
    ["profitability", "Доходность"],
    ["mortgage-compare", "Сравнение ипотеки"]
  ];
  return `<div class="tabs calculator-tabs" role="tablist" aria-label="Сценарии калькулятора">${tabs.map(([id, label]) => `<button type="button" role="tab" data-calculator-tab="${id}" aria-selected="${id === active}" class="${id === active ? "active" : ""}">${label}</button>`).join("")}</div>`;
}

function objectTabPanel(item: ApiObject, tab: ObjectTab) {
  if (tab === "buildings") {
    const rows = Array.isArray(item.object_buildings) ? item.object_buildings : [];
    return `
      <section class="tab-panel" role="tabpanel">
        ${rows.length ? dossierTable(
          isClientWorkMode() ? ["Литер", "Срок", "Статус", "Конструктив"] : ["Литер", "Срок", "Статус", "Конструктив", "Актуальность", "Комментарий"],
          rows.map((row) => isClientWorkMode()
            ? [row.name, row.handover_date ? safeDate(row.handover_date) : "Не указано", row.status, row.construction_type]
            : [row.name, row.handover_date ? safeDate(row.handover_date) : "Не указано", row.status, row.construction_type, row.source_actuality ? safeDate(row.source_actuality) : "Не указано", row.notes])
        ) : emptyState("Корпуса не заполнены", "Добавьте литеры, сроки сдачи и статусы в редакторе объекта.")}
      </section>
    `;
  }
  if (tab === "units") {
    const rows = Array.isArray(item.available_units) ? item.available_units : [];
    return `
      <section class="tab-panel" role="tabpanel">
        ${rows.length ? dossierTable(
          isClientWorkMode() ? ["Литер", "№", "Комн.", "Площадь", "Этаж", "Статус", "Цена"] : ["Литер", "№", "Комн.", "Площадь", "Этаж", "Статус", "Цена", "Актуальность", "Комментарий"],
          rows.map((row) => isClientWorkMode()
            ? [row.building_name, row.unit_number, row.rooms ? `${row.rooms}-комн.` : "", row.area ? `${number.format(Number(row.area))} м²` : "", row.floor || "", row.status, row.price ? formatMoney(row.price) : ""]
            : [row.building_name, row.unit_number, row.rooms ? `${row.rooms}-комн.` : "", row.area ? `${number.format(Number(row.area))} м²` : "", row.floor || "", row.status, row.price ? formatMoney(row.price) : "", row.source_actuality ? safeDate(row.source_actuality) : "", row.notes])
        ) : emptyState("Остатки не заполнены", "Добавьте конкретные квартиры или сводные строки по литерам.")}
      </section>
    `;
  }
  if (tab === "purchase") {
    const booking = Array.isArray(item.booking_terms) ? item.booking_terms : [];
    const programs = Array.isArray(item.purchase_programs) ? item.purchase_programs : [];
    return `
      <section class="tab-panel object-dossier-stack" role="tabpanel">
        <div>
          <h3>Бронь</h3>
          ${booking.length ? dossierTable(
            isClientWorkMode() ? ["Условие", "Сумма", "Срок", "Входит"] : ["Условие", "Сумма", "Срок", "Входит", "Не входит", "Актуальность"],
            booking.map((row) => isClientWorkMode()
              ? [row.title, row.amount ? formatMoney(row.amount) : "", row.duration, row.included]
              : [row.title, row.amount ? formatMoney(row.amount) : "", row.duration, row.included, row.excluded, row.source_actuality ? safeDate(row.source_actuality) : ""])
          ) : emptyState("Условия брони не заполнены", "Добавьте сумму, срок и состав услуги.")}
        </div>
        <div>
          <h3>Программы и ипотека</h3>
          ${programs.length ? programs.map((program) => `
            <article class="dossier-info-block">
              <div><strong>${h(program.name || "Программа")}</strong><span>${h(program.type || "Условия покупки")}${program.active ? "" : " · неактивно"}</span></div>
              ${program.description ? `<p>${h(program.description)}</p>` : ""}
              ${program.eligibility ? `<dl><div><dt>Кому подходит</dt><dd>${h(program.eligibility)}</dd></div></dl>` : ""}
              ${program.exclusions ? `<dl><div><dt>Исключения</dt><dd>${h(program.exclusions)}</dd></div></dl>` : ""}
              ${program.limits ? `<dl><div><dt>Лимиты</dt><dd>${h(program.limits)}</dd></div></dl>` : ""}
              ${!isClientWorkMode() && program.notes ? `<aside class="internal-block"><strong>Внутренний комментарий</strong><p>${h(program.notes)}</p></aside>` : ""}
              ${!isClientWorkMode() && program.source_actuality ? `<small>Актуальность: ${safeDate(program.source_actuality)}</small>` : ""}
            </article>
          `).join("") : emptyState("Программы не заполнены", "Добавьте условия покупки.")}
        </div>
      </section>
    `;
  }
  if (tab === "documents") {
    const docs = Array.isArray(item.document_requirements) ? item.document_requirements : [];
    const sources = Array.isArray(item.object_sources) ? item.object_sources : [];
    return `
      <section class="tab-panel object-dossier-stack" role="tabpanel">
        <div>
          <h3>Пакет документов</h3>
          ${docs.length ? dossierTable(["Документ", "Категория", "Когда требуется", "Куда отправить", "Статус"], docs.map((row) => [
            row.title,
            row.category,
            row.condition_text,
            row.recipient,
            row.required ? "Обязательный" : "По ситуации"
          ])) : emptyState("Документы не заполнены", "Добавьте чеклист для платного бронирования.")}
        </div>
        ${isClientWorkMode() ? "" : `<div>
          <h3>Источники</h3>
          ${sources.length ? dossierTable(["Источник", "Тип", "Дата", "Ссылка", "Комментарий"], sources.map((row) => [
            row.title,
            row.source_type,
            row.source_date ? safeDate(row.source_date) : "",
            sourceLink(row.url),
            row.notes
          ]), true) : emptyState("Источники не заполнены", "Добавьте сайт, файл или сообщение, откуда взяты данные.")}
        </div>`}
      </section>
    `;
  }
  if (isClientWorkMode() && (tab === "contacts" || tab === "history")) return objectTabPanel(item, "buildings");
  if (tab === "contacts") {
    const contacts = Array.isArray(item.object_contacts) ? item.object_contacts : [];
    return `
      <section class="tab-panel" role="tabpanel">
        ${contacts.length ? dossierTable(["Имя / канал", "Роль", "Телефон", "Email", "График", "Комментарий"], contacts.map((row) => [
          row.name,
          row.role,
          row.phone,
          row.email,
          row.schedule,
          row.notes
        ])) : emptyState("Контакты не заполнены", "Добавьте колл-центр, менеджеров и отдел сопровождения.")}
      </section>
    `;
  }
  if (tab === "files") {
    const files = Array.isArray(item.object_files) ? item.object_files : [];
    return `
      <section class="tab-panel" role="tabpanel">
        ${files.length ? objectFilesTable(files) : emptyState("Файлы объекта не загружены", "Добавьте документы, презентации или фотографии в редакторе объекта.")}
        <a class="button secondary detail-map-link" href="#/files">Открыть файловое пространство</a>
      </section>
    `;
  }
  if (tab === "history") {
    const history = Array.isArray(item.object_change_history) ? item.object_change_history : [];
    return `
      <section class="tab-panel" role="tabpanel">
        <div class="metrics">
          <div><span>Последнее обновление</span><strong>${safeDate(item.updated_at)}</strong></div>
          <div><span>Актуальность источника</span><strong>${item.source_actuality ? safeDate(item.source_actuality) : "скрыто"}</strong></div>
        </div>
        ${history.length ? dossierTable(["Дата", "Раздел", "Поле", "Было", "Стало"], history.map((row) => [
          safeDate(row.created_at),
          row.entity,
          row.field,
          compactHistoryValue(row.old_value),
          compactHistoryValue(row.new_value)
        ])) : emptyState("История пока пустая", "Изменения цен, статусов и блоков досье появятся после обновлений.")}
      </section>
    `;
  }
  return objectTabPanel(item, "buildings");
}

function dossierTable(headers: string[], rows: unknown[][], trustedHtml = false) {
  return `
    <div class="dossier-table-wrap">
      <table class="dossier-table">
        <thead><tr>${headers.map((header) => `<th>${h(header)}</th>`).join("")}</tr></thead>
        <tbody>
          ${rows.map((row) => `<tr>${row.map((cell) => `<td>${trustedHtml ? String(cell || "") : h(cell ?? "")}</td>`).join("")}</tr>`).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function objectFilesTable(files: Array<Record<string, unknown>>) {
  return `
    <div class="dossier-table-wrap">
      <table class="dossier-table object-files-table">
        <thead><tr>${["Файл", "Тип", "Размер", "Папка", "Добавлен", ""].map((header) => `<th>${h(header)}</th>`).join("")}</tr></thead>
        <tbody>
          ${files.map((file) => {
            const id = Number(file.id || 0);
            const name = String(file.name || "Файл");
            const openAttrs = id ? ` role="button" tabindex="0" data-open-object-file="${id}" aria-label="Открыть файл ${h(name)}"` : "";
            return `
              <tr${openAttrs}>
                <td>${h(name)}</td>
                <td>${h(fileTypeLabel(String(file.type || "file")))}</td>
                <td>${h(String(file.size_label || ""))}</td>
                <td>${h(String(file.folder || ""))}</td>
                <td>${file.created_at ? safeDate(String(file.created_at)) : ""}</td>
                <td>${id ? `<a class="file-action-button dossier-download-button" href="/api/files/${id}/download" aria-label="Скачать файл ${h(name)}"${tooltipAttr("Скачать файл")}><i data-lucide="arrow-down-to-line"></i></a>` : ""}</td>
              </tr>
            `;
          }).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function bindObjectFileRows() {
  document.querySelectorAll<HTMLTableRowElement>("[data-open-object-file]").forEach((row) => {
    const open = () => {
      const id = row.dataset.openObjectFile;
      if (id) window.open(`/api/files/${id}/preview`, "_blank", "noopener,noreferrer");
    };
    row.addEventListener("click", (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("a, button")) return;
      open();
    });
    row.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      open();
    });
  });
}

function sourceLink(value: unknown) {
  const url = safeExternalUrl(value);
  return url ? `<a href="${h(url)}" target="_blank" rel="noopener noreferrer">Открыть</a>` : "";
}

function compactHistoryValue(value: unknown) {
  const text = String(value || "");
  if (!text) return "";
  if (text.length <= 120) return text;
  return `${text.slice(0, 117)}...`;
}

function clearCalculationWarnings() {
  document.querySelectorAll<HTMLElement>("[data-calc-warning]").forEach((node) => {
    node.textContent = "";
  });
  document.querySelectorAll<HTMLElement>("[data-warning-group]").forEach((node) => node.classList.remove("has-warning"));
}

function calculateMortgageVariant(form: HTMLFormElement, index: 1 | 2) {
  const amount = readNumberField(form, `amount${index}`);
  const years = readNumberField(form, `years${index}`);
  const rate = readNumberField(form, `rate${index}`);
  const payment = mortgagePayment(amount, rate, years);
  const total = payment * years * 12;
  const overpay = total - amount;

  setText(`amountResult${index}`, currency.format(amount));
  setText(`yearsResult${index}`, `${number.format(years)} лет`);
  setText(`rateResult${index}`, `${number.format(rate)} %`);
  setText(`payment${index}`, currency.format(payment));
  setText(`overpay${index}`, currency.format(overpay));
  setText(`total${index}`, currency.format(total));

  return { payment, overpay, total };
}

function readNumberField(form: HTMLFormElement, name: string) {
  const control = form.elements.namedItem(name);
  return control instanceof HTMLInputElement ? Number(control.value) || 0 : 0;
}

function mortgagePayment(amount: number, rate: number, years: number) {
  const months = years * 12;
  if (amount <= 0 || months <= 0) return 0;
  const monthlyRate = rate / 1200;
  if (monthlyRate === 0) return amount / months;
  return amount * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));
}

function renderDiff(id: string, value: number) {
  const node = document.querySelector<HTMLElement>(`#${id}`);
  if (!node) return;
  node.textContent = `${value > 0 ? "+" : ""}${currency.format(value)}`;
  node.classList.toggle("positive", value < 0);
  node.classList.toggle("negative", value > 0);
}

function setText(id: string, value: string) {
  const node = document.querySelector<HTMLElement>(`#${id}`);
  if (node) node.textContent = value;
}

function renderCalculationWarnings(warnings: string[]) {
  const groups = new Map<string, string[]>();
  warnings.forEach((warning) => {
    const normalized = warning.toLowerCase();
    const target = normalized.includes("аренд") || normalized.includes("окуп") || normalized.includes("налог со сдачи")
      ? "rent"
      : normalized.includes("ремонт") || normalized.includes("мебел") || normalized.includes("техник") || normalized.includes("страхован")
        ? "fitout"
        : normalized.includes("коммун") || normalized.includes("имущество") || normalized.includes("риелтор")
          ? "expenses"
          : normalized.includes("взнос") || normalized.includes("стоимость") || normalized.includes("площадь") || normalized.includes("дат") || normalized.includes("срок сдачи")
            ? "purchase"
            : "mortgage";
    groups.set(target, [...(groups.get(target) || []), warning]);
  });
  groups.forEach((items, group) => {
    const node = document.querySelector<HTMLElement>(`[data-calc-warning="${group}"]`);
    if (node) node.textContent = items.join(" ");
    const groupNode = document.querySelector<HTMLElement>(`[data-warning-group="${group}"]`);
    groupNode?.classList.add("has-warning");
    if (groupNode instanceof HTMLDetailsElement) groupNode.open = true;
  });
}

function navItems(options: { includeNews?: boolean } = {}) {
  const includeNews = options.includeNews ?? true;
  const base = [
    ["/objects", "Объекты", "building-2"],
    ["/map", "Карта", "map"],
    ["/calculator", "Калькулятор", "calculator"],
    ["/files", "Файлы", "folder"]
  ];
  if (appConfig?.aiRopEnabled && !isClientWorkMode()) base.push(["/ai-rop", "ИИ-РОП", "shield"]);
  if (includeNews) base.push(["/news", "Новости", "bell"]);
  if (canManage() && !isClientWorkMode()) base.push(["/manage/users", "Пользователи", "users"], ["/manage/audit", "Журнал", "history"]);
  return base;
}

function navItem(item: string[]) {
  const [href, label, icon] = item;
  const badge = href === "/files"
    ? `<span class="nav-badge" data-files-unread-badge ${unreadFilesCount > 0 ? "" : "hidden"} aria-label="${unreadFilesCount} непрочитанных файлов"${tooltipAttr(`Непрочитанные файлы: ${unreadFilesCount}`)}>${unreadFilesCount}</span>`
    : href === "/ai-rop" ? `<span class="nav-badge nav-badge-critical" data-ai-rop-badge hidden>0</span>` : "";
  const tooltip = href === "/manage/audit" ? tooltipAttr("Журнал действий пользователей") : "";
  return `<a href="#${href}" class="${route === href ? "active" : ""}" aria-current="${route === href ? "page" : "false"}"${tooltip}><i data-lucide="${icon}"></i><span>${label}</span>${badge}</a>`;
}

function routeDescription(_path: string) { return ""; }

function canManage() {
  return currentUser?.role === "director" || currentUser?.role === "admin";
}

function isClientWorkMode() {
  return localStorage.getItem(clientWorkModeKey) === "1";
}

function setClientWorkMode(enabled: boolean) {
  if (enabled) localStorage.setItem(clientWorkModeKey, "1");
  else localStorage.removeItem(clientWorkModeKey);
}

function isClientHiddenRoute(path: string) {
  return path === "/manage/users" || path === "/manage/audit" || path === "/ai-rop";
}

function titleForRoute(path: string) {
  if (path === "/objects/new") return "Новый объект";
  if (path.startsWith("/objects/") && path.endsWith("/edit")) return "Редактирование объекта";
  if (path.startsWith("/objects/")) return "Карточка объекта";
  return new Map([
    ["/objects", "Объекты"],
    ["/map", "Карта объектов"],
    ["/calculator", "Калькулятор"],
    ["/files", "Файлы"],
    ["/news", "Новости"],
    ["/ai-rop", "ИИ-РОП"],
    ["/manage/users", "Пользователи"],
    ["/manage/audit", "Журнал действий"]
  ]).get(path) || "Портал";
}

function roleName(role: string) {
  return role === "manager" ? "Менеджер" : role === "director" ? "Директор" : "Технический администратор";
}

function userStateLabel(state: string) {
  return new Map([
    ["active", "Активен"],
    ["blocked", "Заблокирован"],
    ["archived", "Архивирован"],
    ["password_change_required", "Требуется смена пароля"]
  ]).get(state) || state;
}

function formatObjectPrice(value: number | string) {
  return Number(value) > 0 ? formatMoney(value) : "Цена уточняется";
}

function hasObjectCoordinates(item: ApiObject) {
  const lat = Number(item.lat);
  const lng = Number(item.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0);
}

function formatMoney(value: number | string) {
  return typeof value === "number" ? currency.format(value) : value;
}

function safeDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : date.format(parsed);
}

function isoDateValue(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addBrowserMonths(value: Date, months: number) {
  const next = new Date(value);
  next.setMonth(next.getMonth() + months);
  return next;
}

function unique(values: string[]) {
  return [...new Set(values)].filter(Boolean);
}

function denied() {
  return emptyState("Недостаточно прав", "Этот раздел доступен директору или техническому администратору.");
}

function message(error: any) {
  if (error instanceof Error) return error.message;
  return requestMessage(error);
}

function requestMessage(payload: any, status = 0) {
  const raw = payload?.error?.message || payload?.message || payload?.error || "";
  const text = typeof raw === "string" ? raw.trim() : "";
  if (text) return text.length > 500 ? `${text.slice(0, 500)}...` : text;
  if (status === 401) return "Требуется вход в портал.";
  if (status === 403) return "Недостаточно прав для этого действия.";
  if (status === 404) return "Запрошенные данные не найдены.";
  if (status === 413) return "Файл слишком большой для загрузки.";
  if (status >= 500) return "Сервер не смог обработать запрос. Попробуйте ещё раз или проверьте журнал сервера.";
  return "Не удалось выполнить запрос. Повторите действие.";
}

function selectField(id: string, label: string, placeholder: string, options: string[], selected = "", name?: string) {
  const optionMarkup = options.map((item) => {
    const value = item;
    return `<option value="${h(value)}" ${value === selected ? "selected" : ""}>${h(item)}</option>`;
  }).join("");
  return `
    <label class="select-field" for="${id}">
      <span class="sr-only">${label}</span>
      <select id="${id}" ${name ? `name="${name}"` : ""}>
        ${placeholder ? `<option value="">${placeholder}</option>` : ""}
        ${optionMarkup}
      </select>
      <i data-lucide="chevron-right" aria-hidden="true"></i>
    </label>
  `;
}

function skeleton(label: string) {
  return `
    <div class="skeleton-state" role="status" aria-live="polite">
      <span class="sr-only">${label}</span>
      <div class="skeleton-line wide"></div>
      <div class="skeleton-line"></div>
      <div class="skeleton-grid">
        <div></div><div></div><div></div><div></div>
      </div>
    </div>
  `;
}

function emptyState(title: string, body: string, actionLabel?: string, actionId?: string) {
  return `
    <section class="empty-state">
      <h2>${title}</h2>
      <p>${body}</p>
      ${actionLabel && actionId ? `<button class="secondary" id="${actionId}" type="button">${actionLabel}</button>` : ""}
    </section>
  `;
}

function activeFolderLabel(nodes: FolderNode[], activeFolderId: number | null): string {
  if (activeFolderId == null) return "Все файлы";
  return findFolderLabel(nodes, activeFolderId) || "Файлы";
}

function activeFolderPathLabel(nodes: FolderNode[], activeFolderId: number | null): string {
  if (activeFolderId == null) return "Корневой раздел";
  const folder = flattenFolders(nodes).find((item) => item.id === activeFolderId);
  return folder ? folder.trail.map((item) => item.name).join(" / ") : "Файлы";
}

function findFolderLabel(nodes: FolderNode[], id: number): string | null {
  for (const node of nodes) {
    if (node.id === id) return node.name;
    const nested = findFolderLabel(node.nodes || [], id);
    if (nested) return nested;
  }
  return null;
}

function folderCreatePanel(activeFolderId: number | null) {
  return `
    <section class="folder-create-panel">
      <form id="folder-create-form" class="folder-create-form">
        <input type="hidden" name="parentId" value="${activeFolderId ?? ""}" />
        <label>
          <span class="sr-only">Название новой папки</span>
          <input name="name" autocomplete="off" maxlength="80" placeholder="Название новой папки" />
        </label>
        <button class="secondary" type="submit"><i data-lucide="folder-plus"></i> Создать папку</button>
        <p class="field-error" id="folder-create-error" role="alert"></p>
      </form>
    </section>
  `;
}

function bindFolderCreate(activeFolderId: number | null) {
  const form = document.querySelector<HTMLFormElement>("#folder-create-form");
  if (!form) return;
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(form).entries());
    const error = document.querySelector<HTMLElement>("#folder-create-error");
    try {
      if (error) error.textContent = "";
      await api("/api/folders", { method: "POST", body: JSON.stringify({ name: data.name, parentId: activeFolderId }) });
      await hydrateFiles();
    } catch (reason) {
      if (error) error.textContent = message(reason);
    }
  });
}

function fileRow(item: FileItem): string {
  const read = item.read_status?.status === "read";
  const managementActions = isClientWorkMode() ? "" : `
        <button class="file-action-button" type="button" data-move-file="${item.id}" aria-label="Переместить файл ${h(item.name)}"${tooltipAttr("Переместить файл в другую папку")}><i data-lucide="folder"></i></button>
        <button class="file-action-button danger-button" type="button" data-delete-file="${item.id}" aria-label="Удалить файл ${h(item.name)}"${tooltipAttr("Удалить файл после подтверждения")}><i data-lucide="trash-2"></i></button>`;
  return `
    <article class="row-card file-row ${read ? "is-read" : "is-unread"}" role="button" tabindex="0" data-open-file="${item.id}" aria-label="Открыть предпросмотр файла ${h(item.name)}">
      <i data-lucide="${fileIcon(item)}"></i>
      <div class="file-row-main">
        <div class="file-row-title">
          <strong>${h(item.name)}</strong>
          ${isClientWorkMode() ? "" : `<span class="read-badge ${read ? "read" : "unread"}">${read ? "Прочитано" : "Не прочитано"}</span>`}
        </div>
        <span>${h(item.folder_path)} · ${h(fileTypeLabel(item.type))} · ${h(item.size_label)}${item.required ? " · обязательный" : ""}</span>
      </div>
      <div class="file-row-actions">
        <button class="file-action-button" type="button" data-download-file="${item.id}" aria-label="Скачать файл ${h(item.name)}"${tooltipAttr("Скачать файл")}><i data-lucide="arrow-down-to-line"></i></button>
        ${managementActions}
      </div>
    </article>
  `;
}

function filesBreadcrumb(nodes: FolderNode[], activeFolderId: number | null) {
  const folder = activeFolderId == null ? null : flattenFolders(nodes).find((item) => item.id === activeFolderId) || null;
  const items = [{ id: null, name: "Все файлы" }, ...(folder?.trail || [])];
  return `
    <nav class="files-breadcrumb" aria-label="Путь по файловому пространству">
      ${items.map((item, index) => item.id === activeFolderId
        ? `<span class="current">${h(item.name)}</span>`
        : `<span class="crumb-segment"><button class="crumb-link" type="button" data-open-folder="${item.id ?? ""}">${h(item.name)}</button>${index < items.length - 1 ? `<i data-lucide="chevron-right" aria-hidden="true"></i>` : ""}</span>`
      ).join("")}
    </nav>
  `;
}

function folderCard(folder: { id: number; name: string; trail: Array<{ id: number | null; name: string }> }) {
  const caption = folder.trail.length > 1 ? folder.trail.slice(0, -1).map((item) => item.name).join(" / ") : "Корневой раздел";
  return `
    <article class="folder-card">
      <button class="folder-open-button" type="button" data-open-folder="${folder.id}" aria-label="Открыть папку ${h(folder.name)}">
        <span class="folder-visual" aria-hidden="true">
          <span class="folder-shell">
            <span class="folder-paper paper-1"></span>
            <span class="folder-paper paper-2"></span>
            <span class="folder-paper paper-3"></span>
            <span class="folder-front"></span>
            <span class="folder-front right"></span>
          </span>
        </span>
        <span class="folder-card-body">
          <strong>${h(folder.name)}</strong>
          <span>${h(caption)}</span>
        </span>
      </button>
      <button class="danger-button folder-delete-button" type="button" data-delete-folder="${folder.id}" aria-label="Удалить папку"${tooltipAttr(`Удалить папку ${folder.name}`)}><i data-lucide="trash-2"></i></button>
    </article>
  `;
}

function filesUploadPanel(nodes: FolderNode[], activeFolderId: number | null) {
  const folders = flattenFolders(nodes);
  const selectedFolderId = activeFolderId ?? folders[0]?.id ?? null;
  return `
    <section class="files-upload-panel">
      <div class="files-upload-copy">
        <p class="eyebrow">Загрузка документов</p>
        <h3>Добавить файлы в пространство</h3>
        <p>Загрузите документы, изображения, видео или PDF в выбранную папку.</p>
      </div>
      <form id="files-upload-form" class="files-upload-form">
        <input id="files-upload-input" name="files" type="file" multiple hidden />
        <div class="files-upload-controls">
          <label class="select-field" for="upload-folder-id">
            <span class="sr-only">Папка назначения</span>
            <select id="upload-folder-id" name="folderId">
              ${folders.map((folder) => `<option value="${folder.id}" ${folder.id === selectedFolderId ? "selected" : ""}>${h(folder.trail.map((item) => item.name).join(" / "))}</option>`).join("")}
            </select>
            <i data-lucide="chevron-right" aria-hidden="true"></i>
          </label>
          <button class="secondary" id="files-upload-trigger" type="button"><i data-lucide="upload"></i> Выбрать файлы</button>
          <button class="primary" id="files-upload-submit" type="submit" disabled><i data-lucide="arrow-down-to-line"></i> Загрузить</button>
        </div>
        <div class="files-upload-list" id="files-upload-list">Файлы не выбраны</div>
        <div class="upload-progress" data-upload-progress hidden><span></span><strong>0%</strong></div>
        <p class="field-error" id="files-upload-error" role="alert"></p>
      </form>
    </section>
  `;
}

function bindFileUpload(_nodes: FolderNode[], activeFolderId: number | null) {
  const form = document.querySelector<HTMLFormElement>("#files-upload-form");
  if (!form) return;
  const input = document.querySelector<HTMLInputElement>("#files-upload-input");
  const trigger = document.querySelector<HTMLButtonElement>("#files-upload-trigger");
  const submit = document.querySelector<HTMLButtonElement>("#files-upload-submit");
  const list = document.querySelector<HTMLElement>("#files-upload-list");
  const errorNode = document.querySelector<HTMLElement>("#files-upload-error");
  const folderSelect = document.querySelector<HTMLSelectElement>("#upload-folder-id");
  const progress = document.querySelector<HTMLElement>("[data-upload-progress]");
  if (!input || !trigger || !submit || !list || !errorNode || !folderSelect) return;
  const uploadFiles = createSelectedUploadFilesController(form);

  const refreshSelectedFiles = () => {
    const files = uploadFiles.files();
    submit.disabled = files.length === 0 || !folderSelect.value || form.dataset.uploading === "true";
    errorNode.textContent = "";
  };

  input.addEventListener("change", refreshSelectedFiles);
  folderSelect.addEventListener("change", refreshSelectedFiles);
  form.addEventListener("upload-files-change", refreshSelectedFiles);

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const selectedFiles = uploadFiles.files();
    if (!selectedFiles.length || !folderSelect.value) return;
    const body = new FormData();
    body.set("folderId", folderSelect.value);
    selectedFiles.forEach((file) => body.append("files", file));
    form.dataset.uploading = "true";
    setUploadBusy(submit, progress, 0, "Загружаем");
    try {
      await uploadFormData("/api/files/upload", body, (percent) => setUploadBusy(submit, progress, percent, "Загружаем"));
      uploadFiles.clear();
      if (String(activeFolderId || "") !== folderSelect.value) {
        setRouteParam("folder", folderSelect.value);
      } else {
        await hydrateFiles();
      }
    } catch (error) {
      errorNode.textContent = error instanceof Error ? error.message : "Не удалось загрузить файлы.";
    } finally {
      delete form.dataset.uploading;
      setUploadIdle(submit, progress, "Загрузить");
      refreshSelectedFiles();
    }
  });

  refreshSelectedFiles();
}

function bindFileActions() {
  document.querySelectorAll<HTMLButtonElement>("[data-download-file]").forEach((button) => button.addEventListener("click", (event) => {
    event.stopPropagation();
    window.location.href = `/api/files/${button.dataset.downloadFile}/download`;
  }));
  document.querySelectorAll<HTMLElement>("[data-open-file]").forEach((node) => {
    const open = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("button, a, input, select, label")) return;
      const file = currentFiles.find((item) => String(item.id) === node.dataset.openFile);
      if (file) openFilePreview(file, node);
    };
    node.addEventListener("click", open);
    node.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      const file = currentFiles.find((item) => String(item.id) === node.dataset.openFile);
      if (file) openFilePreview(file, node);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-move-file]").forEach((button) => button.addEventListener("click", (event) => {
    event.stopPropagation();
    const id = button.dataset.moveFile || "";
    const file = currentFiles.find((item) => String(item.id) === id);
    if (file) openFileMoveDialog(file, button);
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-delete-file]").forEach((button) => button.addEventListener("click", async (event) => {
    event.stopPropagation();
    const file = currentFiles.find((item) => String(item.id) === button.dataset.deleteFile);
    if (!file || !await confirmDialog({
      title: "Удалить файл?",
      body: `Файл «${file.name}» будет удален из файлового пространства.`,
      confirmText: "Удалить"
    })) return;
    await api(`/api/files/${file.id}`, { method: "DELETE" });
    await hydrateFiles();
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-delete-folder]").forEach((button) => button.addEventListener("click", async () => {
    const folder = currentFolders.find((item) => String(item.id) === button.dataset.deleteFolder);
    if (!folder || !await confirmDialog({
      title: "Удалить папку?",
      body: `Папка «${folder.name}» будет удалена только если внутри нет файлов и вложенных папок.`,
      confirmText: "Удалить"
    })) return;
    await api(`/api/folders/${folder.id}`, { method: "DELETE" });
    if (String(routeParams().get("folder") || "") === String(folder.id)) {
      setRouteParam("folder", "");
    } else {
      await hydrateFiles();
    }
  }));
}

function confirmDialog(options: { title: string; body: string; confirmText?: string; cancelText?: string }) {
  const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  return new Promise<boolean>((resolve) => {
    const backdrop = document.createElement("section");
    backdrop.className = "confirm-backdrop";
    backdrop.setAttribute("role", "presentation");
    backdrop.innerHTML = `
      <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-body">
        <h2 id="confirm-dialog-title">${h(options.title)}</h2>
        <p id="confirm-dialog-body">${h(options.body)}</p>
        <div class="confirm-actions">
          <button class="button secondary" type="button" data-confirm-cancel>${h(options.cancelText || "Отмена")}</button>
          <button class="primary danger-action" type="button" data-confirm-accept>${h(options.confirmText || "Подтвердить")}</button>
        </div>
      </div>
    `;
    const close = (value: boolean) => {
      document.removeEventListener("keydown", onKeydown);
      backdrop.remove();
      returnFocus?.focus({ preventScroll: true });
      resolve(value);
    };
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(false);
    };
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) close(false);
    });
    backdrop.querySelector("[data-confirm-cancel]")?.addEventListener("click", () => close(false));
    backdrop.querySelector("[data-confirm-accept]")?.addEventListener("click", () => close(true));
    document.addEventListener("keydown", onKeydown);
    document.body.append(backdrop);
    requestAnimationFrame(() => backdrop.querySelector<HTMLButtonElement>("[data-confirm-cancel]")?.focus({ preventScroll: true }));
  });
}

function openFileMoveDialog(file: FileItem, anchor: HTMLElement) {
  closeFileMoveDialog();
  closeFilePreview();
  fileMoveReturnFocus = anchor;
  const folders = currentFolders;
  const dialog = document.createElement("section");
  dialog.className = "file-move-popover";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "false");
  dialog.setAttribute("aria-labelledby", "file-move-title");
  dialog.innerHTML = `
    <header>
      <div>
        <p class="eyebrow">Перенос файла</p>
        <h2 id="file-move-title">${h(file.name)}</h2>
        <span>${h(file.folder_path || "Без папки")}</span>
      </div>
      <button class="icon-button" type="button" data-close-file-move aria-label="Закрыть перенос">×</button>
    </header>
    <form id="file-move-form" class="file-move-form">
      <div class="file-move-tree" role="radiogroup" aria-label="Папка назначения">
        ${folders.map((folder) => fileMoveFolderOption(folder, file.folder_id)).join("")}
      </div>
      <p class="field-error" id="file-move-error" role="alert"></p>
      <footer>
        <button class="ghost" type="button" data-close-file-move>Отмена</button>
        <button class="primary" type="submit">Переместить</button>
      </footer>
    </form>
  `;
  document.body.append(dialog);
  positionFileMovePopover(dialog, anchor);
  const closeButtons = dialog.querySelectorAll("[data-close-file-move]");
  closeButtons.forEach((button) => button.addEventListener("click", closeFileMoveDialog));
  fileMoveOutsideHandler = (event) => {
    const target = event.target as Node | null;
    if (!target || dialog.contains(target) || anchor.contains(target)) return;
    closeFileMoveDialog();
  };
  fileMoveKeyHandler = (event) => {
    if (event.key === "Escape") closeFileMoveDialog();
  };
  document.addEventListener("pointerdown", fileMoveOutsideHandler, true);
  document.addEventListener("keydown", fileMoveKeyHandler, true);
  dialog.querySelector<HTMLFormElement>("#file-move-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const folderId = new FormData(form).get("folderId");
    const errorNode = dialog.querySelector<HTMLElement>("#file-move-error");
    const submit = form.querySelector<HTMLButtonElement>("button[type='submit']");
    if (!folderId) {
      if (errorNode) errorNode.textContent = "Выберите папку.";
      return;
    }
    if (submit) submit.disabled = true;
    try {
      await api(`/api/files/${file.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: file.name, folderId })
      });
      closeFileMoveDialog();
      await hydrateFiles();
    } catch (reason) {
      if (errorNode) errorNode.textContent = message(reason);
      if (submit) submit.disabled = false;
    }
  });
  createIcons({ icons: iconSet });
  dialog.querySelector<HTMLElement>("[data-close-file-move]")?.focus({ preventScroll: true });
}

function positionFileMovePopover(popover: HTMLElement, anchor: HTMLElement) {
  const margin = 10;
  const gap = 8;
  const anchorRect = anchor.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  let left = anchorRect.right + gap;
  if (left + popoverRect.width > window.innerWidth - margin) {
    left = anchorRect.left - popoverRect.width - gap;
  }
  if (left < margin) {
    left = Math.min(Math.max(anchorRect.left, margin), Math.max(margin, window.innerWidth - popoverRect.width - margin));
  }
  const centeredTop = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2;
  const top = Math.min(Math.max(centeredTop, margin), Math.max(margin, window.innerHeight - popoverRect.height - margin));
  popover.style.left = `${Math.round(left)}px`;
  popover.style.top = `${Math.round(top)}px`;
}

function fileMoveFolderOption(folder: { id: number; name: string; trail: Array<{ id: number | null; name: string }> }, currentFolderId: number | null) {
  const depth = Math.max(0, folder.trail.length - 1);
  const parentTrail = folder.trail.slice(0, -1).map((item) => item.name).join(" / ");
  return `
    <label class="file-move-option ${folder.id === currentFolderId ? "current" : ""}" style="--folder-depth:${depth}">
      <input type="radio" name="folderId" value="${folder.id}" ${folder.id === currentFolderId ? "checked" : ""} />
      <span class="file-move-option-mark" aria-hidden="true"><i data-lucide="folder"></i></span>
      <span>
        <strong>${h(folder.name)}</strong>
        <small>${h(parentTrail || "Корень")}</small>
      </span>
    </label>
  `;
}

function closeFileMoveDialog() {
  if (fileMoveOutsideHandler) {
    document.removeEventListener("pointerdown", fileMoveOutsideHandler, true);
    fileMoveOutsideHandler = null;
  }
  if (fileMoveKeyHandler) {
    document.removeEventListener("keydown", fileMoveKeyHandler, true);
    fileMoveKeyHandler = null;
  }
  document.querySelector(".file-move-popover")?.remove();
  restoreFocus(fileMoveReturnFocus);
  fileMoveReturnFocus = null;
}

function openFilePreview(file: FileItem, trigger?: HTMLElement) {
  closeFilePreview();
  filePreviewReturnFocus = trigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const previewUrl = `/api/files/${file.id}/preview`;
  const body = previewBody(file, previewUrl);
  const read = file.read_status?.status === "read";
  const dialog = document.createElement("div");
  dialog.className = "file-preview-backdrop";
  dialog.innerHTML = `
    <section class="file-preview-modal" role="dialog" aria-modal="true" aria-labelledby="file-preview-title">
      <header>
        <div>
          <p class="eyebrow">${h(file.folder_path)}</p>
          <h2 id="file-preview-title">${h(file.name)}</h2>
          <span>${h(fileTypeLabel(file.type))} · ${h(file.size_label)}</span>
        </div>
        <button class="icon-button" type="button" data-close-preview aria-label="Закрыть предпросмотр">×</button>
      </header>
      <div class="file-preview-body">${body}</div>
      ${isClientWorkMode() ? "" : fileReadSummaryPanel(file)}
      <footer>
        ${isClientWorkMode() ? "" : `<button class="${read ? "secondary" : "primary"}" type="button" data-preview-read="${file.id}" ${read ? "disabled" : ""}><i data-lucide="check"></i> ${read ? "Прочитано" : "Отметить прочитанным"}</button>`}
        <button class="secondary" type="button" data-download-file="${file.id}">Скачать</button>
      </footer>
    </section>
  `;
  document.body.append(dialog);
  dialog.querySelector("[data-close-preview]")?.addEventListener("click", closeFilePreview);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeFilePreview();
  });
  dialog.querySelector<HTMLButtonElement>("[data-preview-read]")?.addEventListener("click", async () => {
    await api(`/api/files/${file.id}/read-status`, { method: "POST", body: JSON.stringify({ status: "read" }) });
    closeFilePreview();
    await hydrateFiles();
  });
  dialog.querySelector<HTMLButtonElement>("[data-download-file]")?.addEventListener("click", () => {
    window.location.href = `/api/files/${file.id}/download`;
  });
  createIcons({ icons: iconSet });
  dialog.querySelector<HTMLElement>("[data-close-preview]")?.focus({ preventScroll: true });
}

function fileReadSummaryPanel(file: FileItem) {
  if (!file.read_summary?.length) return "";
  const readCount = file.read_summary.filter((item) => item.status === "read").length;
  return `
    <section class="file-preview-readers" aria-label="Статусы прочтения сотрудниками">
      <div class="file-preview-readers-head">
        <div>
          <p class="eyebrow">Контроль прочтения</p>
          <h3>${readCount} из ${file.read_summary.length} прочитали</h3>
        </div>
      </div>
      <div class="file-reader-list">
        ${file.read_summary.map((item) => `
          <span class="file-reader-status ${item.status === "read" ? "is-read" : "is-unread"}">
            <strong>${h(item.name)}</strong>
            <small>${h(roleName(item.role))}${item.status === "read" && item.updated_at ? ` · ${safeDate(item.updated_at)}` : ""}</small>
          </span>
        `).join("")}
      </div>
    </section>
  `;
}

function closeFilePreview() {
  document.querySelector(".file-preview-backdrop")?.remove();
  restoreFocus(filePreviewReturnFocus);
  filePreviewReturnFocus = null;
}

function previewBody(file: FileItem, url: string) {
  const mime = String(file.mime_type || "");
  if (isInlinePreviewMime(mime) && (mime.startsWith("image/") || file.type === "image")) {
    return `<img src="${url}" alt="${h(file.name)}" />`;
  }
  if (isInlinePreviewMime(mime) && (mime.startsWith("video/") || file.type === "video")) {
    return `<video controls src="${url}"></video>`;
  }
  if (isInlinePreviewMime(mime) && (mime === "application/pdf" || file.type === "pdf")) {
    return `<iframe src="${url}" title="Предпросмотр ${h(file.name)}"></iframe>`;
  }
  return `<div class="preview-unavailable"><h3>Предпросмотр недоступен</h3><p>Этот тип файла можно скачать и открыть на устройстве.</p></div>`;
}

function isInlinePreviewMime(value: unknown) {
  return [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "video/mp4",
    "video/webm",
    "video/quicktime"
  ].includes(String(value || "").toLowerCase());
}

function safeExternalUrl(value: unknown) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : "";
  } catch {
    return "";
  }
}

function fileIcon(file: FileItem) {
  const type = String(file.type || "").toLowerCase();
  const mime = String(file.mime_type || "").toLowerCase();
  if (type === "image" || mime.startsWith("image/")) return "file-image";
  if (type === "video" || mime.startsWith("video/")) return "file-video";
  if (type === "xlsx" || type === "xls" || mime.includes("spreadsheet") || mime.includes("excel")) return "file-spreadsheet";
  return "file-text";
}

function formatClientFileSize(size: number) {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1).replace(".0", "")} МБ`;
  if (size >= 1024) return `${Math.max(1, Math.round(size / 1024))} КБ`;
  return `${size} Б`;
}

function fileTypeLabel(type: string) {
  return ({
    image: "изображение",
    video: "видео",
    pdf: "PDF",
    doc: "DOC",
    docx: "DOCX",
    xlsx: "Excel"
  } as Record<string, string>)[type] || type.toUpperCase();
}

function pluralizeFiles(count: number) {
  return pluralizeRu(count, ["файл", "файла", "файлов"]);
}

function pluralizeObjects(count: number) {
  return pluralizeRu(count, ["объект", "объекта", "объектов"]);
}

function pluralizeApartments(count: number) {
  return pluralizeRu(count, ["квартира", "квартиры", "квартир"]);
}

function pluralizeDistricts(count: number) {
  return pluralizeRu(count, ["район", "района", "районов"]);
}

function pluralizeRu(count: number, forms: [string, string, string]) {
  const value = Math.abs(count);
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

function errorState(title: string, body: string, actionLabel: string, actionId: string) {
  return `
    <section class="error-state" role="alert">
      <i data-lucide="alert-triangle"></i>
      <div>
        <h2>${title}</h2>
        <p>${body}</p>
      </div>
      <button class="secondary" id="${actionId}" type="button"><i data-lucide="refresh-cw"></i>${actionLabel}</button>
    </section>
  `;
}

function h(value: unknown) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;"
  })[char] || char);
}

function tooltipAttr(value: unknown) {
  const text = String(value || "").trim();
  return text ? ` data-tooltip="${h(text)}"` : "";
}

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function restoreFocus(target: HTMLElement | null) {
  if (!target?.isConnected) return;
  target.focus({ preventScroll: true });
}

const iconSet = { AlertTriangle, ArrowDownToLine, Bell, Building2, Calculator, Check, ChevronDown, ChevronRight, Copy, Crop, ExternalLink, FileImage, FileSpreadsheet, FileText, FileVideo, Folder, FolderPlus, History, Home, KeyRound, Link: LinkIcon, Lock, LockOpen, LogOut, Map: MapIcon, Menu, Plus, RefreshCw, Save, Search, Shield, SlidersHorizontal, Trash2, Upload, Users, X };
